import { nameToEmoji } from "gemoji";
import { z } from "zod";
import { ASK_USER_FREE_TEXT_PLACEHOLDER, HttpError, NO_BACKOFF, extractBackendErrorCode, getApiClient, getSDK, useStores, withAskUserFreeText, withAskUserPick } from "@littlebird/sdk";
import { findLinkedActionCardIds, isValidRecipient, normalizeRecipient, opensTurn, parseRecipients } from "@littlebird/ui-utils";
import { getLogger } from "@littlebird/logger";
import { computed, makeAutoObservable } from "mobx";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
//#region src/emoji-shortcodes.ts
const SHORTCODE = /:([a-z0-9_+-]+):/g;
const isWordCharacter = (character) => character !== void 0 && /[a-z0-9]/i.test(character);
const emojiFromShortcodes = (text) => text.replace(SHORTCODE, (shortcode, name, offset) => {
	if (isWordCharacter(text[offset - 1]) || isWordCharacter(text[offset + shortcode.length])) return shortcode;
	return Object.hasOwn(nameToEmoji, name) ? nameToEmoji[name] : shortcode;
});
//#endregion
//#region src/person.ts
const ActionCardPersonSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	email: z.string().trim().email().nullish().catch(void 0),
	avatar_url: z.string().url().refine((url) => url.startsWith("https://")).nullish().catch(null)
});
const SlackParentMessageSchema = z.object({
	person: ActionCardPersonSchema,
	channel_name: z.string().nullable(),
	timestamp: z.string().min(1),
	text: z.string().min(1),
	message_link: z.string().nullable().optional().catch(void 0)
});
const optionalActionCardPerson = ActionCardPersonSchema.nullish().catch(void 0);
//#endregion
//#region src/content.ts
const AskUserContentSchema = z.object({
	request_id: z.string().min(1),
	questions: z.array(z.object({
		id: z.string().min(1),
		prompt: z.string().min(1),
		options: z.array(z.string()),
		allow_multiple: z.boolean(),
		allow_free_text: z.boolean()
	})).min(1)
}).transform((fields) => ({
	type: "ask_user",
	...fields
}));
const emailDraftFields = {
	to: z.array(z.string()),
	cc: z.array(z.string()),
	bcc: z.array(z.string()),
	subject: z.string(),
	body: z.string(),
	is_html: z.boolean(),
	account_label: z.string().nullable()
};
const GmailEmailDraftContentSchema = z.object(emailDraftFields).transform((fields) => ({
	type: "gmail_email_draft",
	...fields
}));
const GmailEmailDraftWithAttachmentsContentSchema = z.object({
	...emailDraftFields,
	attachment: z.object({
		file_id: z.string(),
		filename: z.string(),
		mimetype: z.string(),
		size: z.number()
	})
}).transform((fields) => ({
	type: "gmail_email_draft_with_attachments",
	...fields
}));
const gmailReviewItem = z.object({
	source_id: z.string().min(1),
	sender: z.string(),
	subject: z.string(),
	received_at: z.string(),
	group: z.string().nullish(),
	category: z.string().nullish()
});
const gmailReviewFields = {
	account_id: z.string().min(1),
	account_label: z.string(),
	items: z.array(gmailReviewItem).max(10),
	selected_source_ids: z.array(z.string()).max(10),
	choice: z.literal("once").default("once"),
	result_summary: z.string().default("")
};
const GmailNoiseArchiveContentSchema = z.object(gmailReviewFields).transform((fields) => ({
	type: "gmail_noise_archive",
	...fields
}));
const GmailLabelReviewContentSchema = z.object(gmailReviewFields).transform((fields) => ({
	type: "gmail_label_review",
	...fields
}));
const OutlookEmailDraftPreviewContentSchema = z.object(emailDraftFields).transform((fields) => ({
	type: "outlook_email_draft",
	...fields
}));
const nullableText = () => z.string().nullable().default(null);
const GoogleCalendarEventContentSchema = z.object({
	title: z.string(),
	start: z.string(),
	end: z.string(),
	timezone: z.string(),
	attendees: z.array(z.string()),
	location: z.string(),
	description: z.string(),
	account_label: z.string().nullable(),
	create_meeting_room: z.boolean().nullable(),
	send_updates: z.boolean().nullable()
}).transform((fields) => ({
	type: "google_calendar_event",
	...fields
}));
const GoogleCalendarRsvpResponseSchema = z.enum([
	"accepted",
	"tentative",
	"declined"
]);
const GoogleCalendarRsvpContentSchema = z.object({
	event_id: z.string().min(1),
	response: GoogleCalendarRsvpResponseSchema,
	title: z.string(),
	start: z.string().nullable(),
	end: z.string().nullable(),
	timezone: z.string().nullable(),
	organizer: z.string().nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "google_calendar_rsvp",
	...fields
}));
const GoogleCalendarEventUpdateContentSchema = z.object({
	event_id: z.string().min(1),
	title: z.string(),
	previous_title: z.string(),
	start: nullableText(),
	end: nullableText(),
	previous_start: nullableText(),
	previous_end: nullableText(),
	timezone: nullableText(),
	location: z.string(),
	previous_location: z.string(),
	description: z.string(),
	previous_description: z.string(),
	attendees: z.array(z.string()),
	previous_attendees: z.array(z.string()),
	user_email: nullableText(),
	organizer: nullableText(),
	notify_guests: z.boolean().default(true),
	account_label: nullableText(),
	meet_url: nullableText(),
	html_link: nullableText()
}).transform((fields) => ({
	type: "google_calendar_event_update",
	...fields
}));
const TodoistTaskContentSchema = z.object({
	content: z.string(),
	due: z.string().nullable(),
	project: z.string().nullable(),
	project_name: z.string().nullable(),
	priority: z.number().nullable(),
	account_label: z.string().nullable(),
	description: z.string().nullable(),
	deadline: z.string().nullable(),
	duration: z.string().nullable(),
	labels: z.array(z.string()),
	section: z.string().nullable(),
	parent: z.string().nullable(),
	order: z.number().nullable(),
	assignee: z.string().nullable(),
	uncompletable: z.boolean().nullable()
}).transform((fields) => ({
	type: "todoist_task",
	...fields
}));
const todoistTaskTargetShape = {
	task_id: z.string(),
	task_content: z.string(),
	project_name: z.string().nullable(),
	due: z.string().nullable(),
	priority: z.number().nullable(),
	account_label: z.string().nullable()
};
const TodoistTaskCompleteContentSchema = z.object(todoistTaskTargetShape).transform((fields) => ({
	type: "todoist_task_complete",
	...fields
}));
const TodoistTaskUncompleteContentSchema = z.object(todoistTaskTargetShape).transform((fields) => ({
	type: "todoist_task_uncomplete",
	...fields
}));
const TodoistObjectDeleteContentSchema = z.object({
	object_type: z.enum(["task", "project"]),
	object_id: z.string(),
	object_name: z.string(),
	project_name: z.string().nullable(),
	due: z.string().nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "todoist_object_delete",
	...fields
}));
const TodoistTaskUpdateContentSchema = z.object({
	task_id: z.string(),
	task_content: z.string(),
	project_name: z.string().nullable(),
	content: z.string().nullable(),
	description: z.string().nullable(),
	due: z.string().nullable(),
	deadline: z.string().nullable(),
	duration: z.string().nullable(),
	labels: z.array(z.string()).nullable(),
	project: z.string().nullable(),
	assignee: z.string().nullable(),
	uncompletable: z.boolean().nullable(),
	priority: z.number().nullable(),
	previous_due: z.string().nullable(),
	previous_priority: z.number().nullable(),
	previous_labels: z.array(z.string()).nullable(),
	previous_project_name: z.string().nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "todoist_task_update",
	...fields
}));
const GoogleTaskContentSchema = z.object({
	title: z.string(),
	notes: z.string().nullable(),
	due: z.string().nullable(),
	tasklist_id: z.string(),
	tasklist_title: z.string().nullable(),
	task_parent: z.string().nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "google_task",
	...fields
}));
const GoogleTaskUpdateContentSchema = z.object({
	task_id: z.string(),
	tasklist_id: z.string(),
	task_title: z.string(),
	tasklist_title: z.string().nullable(),
	title: z.string().nullable(),
	notes: z.string().nullable(),
	due: z.string().nullable(),
	status: z.enum(["needsAction", "completed"]).nullable(),
	previous_title: z.string().nullable(),
	previous_notes: z.string().nullable(),
	previous_due: z.string().nullable(),
	previous_status: z.enum(["needsAction", "completed"]).nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "google_task_update",
	...fields
}));
const GoogleTaskDeleteContentSchema = z.object({
	task_id: z.string(),
	tasklist_id: z.string(),
	task_title: z.string(),
	tasklist_title: z.string().nullable(),
	due: z.string().nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "google_task_delete",
	...fields
}));
const GoogleTaskMoveContentSchema = z.object({
	task_id: z.string(),
	tasklist_id: z.string(),
	task_title: z.string(),
	tasklist_title: z.string().nullable(),
	parent: z.string().nullable(),
	previous: z.string().nullable(),
	destination_tasklist: z.string().nullable(),
	destination_tasklist_title: z.string().nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "google_task_move",
	...fields
}));
const slackMessageFields = {
	person: optionalActionCardPerson,
	channel_id: z.string(),
	channel_name: z.string().nullable(),
	message: z.string(),
	account_label: z.string().nullable(),
	thread_ts: z.string().nullable(),
	parent_message: SlackParentMessageSchema.nullish().catch(void 0),
	message_link: z.string().nullable().optional().catch(void 0)
};
const SlackMessageContentSchema = z.object(slackMessageFields).transform((fields) => ({
	type: "slack_message",
	...fields
}));
const SlackMessageWithAttachmentsContentSchema = z.object({
	...slackMessageFields,
	attachment: z.object({
		file_id: z.string(),
		filename: z.string(),
		mimetype: z.string(),
		size: z.number()
	})
}).transform((fields) => ({
	type: "slack_message_with_attachments",
	...fields
}));
const SlackReactionContentSchema = z.object({
	person: optionalActionCardPerson,
	channel_id: z.string(),
	channel_name: z.string().nullable(),
	message_ts: z.string(),
	emoji: z.string(),
	account_label: z.string().nullable(),
	message: z.string().nullable(),
	message_link: z.string().nullable().catch(null)
}).transform((fields) => ({
	type: "slack_reaction",
	...fields
}));
const AgentTeamContentSchema = z.object({ agents: z.array(z.object({
	id: z.string().uuid(),
	name: z.string().min(1),
	description: z.string(),
	why: z.string(),
	first_task: z.string(),
	initial_instructions: z.string(),
	selected: z.boolean(),
	routines: z.array(z.object({
		title: z.string(),
		schedule: z.string()
	})).default([])
})).min(1).max(4) }).transform((fields) => ({
	type: "agent_team",
	...fields
}));
const TeammateMessageContentSchema = z.object({
	thread_id: z.string().uuid(),
	chat_id: z.string().uuid(),
	teammate: z.object({
		id: z.number().int(),
		name: z.string()
	}),
	incoming_text: z.string().nullable(),
	draft: z.string(),
	sent_text: z.string().nullable(),
	awaiting_reply: z.boolean(),
	answered: z.boolean().optional(),
	dismissed: z.boolean().optional(),
	nudge_on_slack: z.boolean().optional(),
	sent_automatically: z.boolean().optional()
}).transform((fields) => ({
	type: "teammate_message",
	...fields
}));
const askUserQuestionFields = {
	prompt: z.string(),
	options: z.array(z.string()),
	allow_multiple: z.boolean().default(false),
	allow_free_text: z.boolean().default(true)
};
const QuestionContentSchema = z.object({
	...askUserQuestionFields,
	selected: z.array(z.string()).default([]),
	free_text: z.string().default("")
}).transform((fields) => ({
	type: "question",
	...fields
}));
const RoutineOfferContentSchema = z.object({
	title: z.string(),
	description: z.string(),
	prompt: z.string(),
	kind: z.enum(["digest", "watcher"]),
	schedule: z.record(z.string(), z.unknown()).nullable(),
	triggers: z.array(z.record(z.string(), z.unknown())).nullable(),
	runs_label: z.string()
}).transform((fields) => ({
	type: "routine_offer",
	...fields
}));
const ToolApprovalContentSchema = z.object({
	integration_name: z.string(),
	tool_slug: z.string(),
	arguments: z.record(z.string(), z.json()),
	account_label: z.string().nullish(),
	app_name: z.string().nullish(),
	tool_name: z.string().nullish(),
	icon: z.string().nullish(),
	argument_defaults: z.record(z.string(), z.json()).default({})
}).transform((fields) => ({
	type: "tool_approval",
	...fields
}));
const InformationalContentSchema = z.object({
	title: z.string(),
	body: z.string(),
	url: z.string().nullish(),
	source: z.object({
		app: z.enum([
			"gmail",
			"outlook",
			"google-calendar",
			"todoist",
			"slack",
			"github",
			"linear"
		]),
		account_label: z.string().nullish(),
		people: z.array(z.unknown()).transform((people) => people.flatMap((person) => {
			const parsed = ActionCardPersonSchema.safeParse(person);
			return parsed.success ? [parsed.data] : [];
		})).default([]).catch([]),
		url: z.string().nullish()
	}).nullish().catch(void 0),
	kind: z.enum(["info", "todo"]).default("info"),
	cta_label: z.string().nullish(),
	cta_prompt: z.string().nullish()
}).transform((fields) => ({
	type: "informational",
	...fields
}));
const CodingAgentRunSchema = z.object({
	state: z.enum([
		"starting",
		"working",
		"finished",
		"failed",
		"stopped"
	]),
	device_id: z.string().nullish(),
	error: z.string().nullish(),
	summary: z.string().nullish()
});
const CodingAgentContentSchema = z.object({
	title: z.string().nullish(),
	agent: z.enum([
		"claude-code",
		"codex",
		"cursor",
		"devin"
	]),
	session_id: z.string().nullish(),
	cwd: z.string(),
	prompt: z.string(),
	access: z.enum(["full", "edit"]).default("edit"),
	run: CodingAgentRunSchema.nullish()
}).transform((fields) => ({
	type: "coding_agent",
	...fields,
	access: fields.agent === "cursor" ? "full" : fields.access
}));
const ROUTINE_REACH_OUT_ACTION_TYPE = "routine_reach_out";
const RoutineReachOutContentSchema = z.object({
	title: z.string(),
	body: z.string().default(""),
	chat_id: z.string().uuid()
}).transform((fields) => ({
	type: ROUTINE_REACH_OUT_ACTION_TYPE,
	...fields
}));
const AgentTeamResultSchema = z.object({ agents: z.array(z.object({
	option_id: z.string().uuid(),
	agent_id: z.string().uuid(),
	chat_id: z.string().uuid(),
	name: z.string()
})) });
const CHAT_PROMPT_ACTION_TYPE = "chat_prompt";
const ChatPromptModeExecutionSchema = z.object({
	kind: z.literal("mode"),
	value: z.string().trim().min(1).max(128)
});
const ChatPromptAgentExecutionSchema = z.object({
	kind: z.literal("agent"),
	value: z.string().trim().min(1).max(255)
});
const ChatPromptExecutionSchema = z.discriminatedUnion("kind", [ChatPromptModeExecutionSchema, ChatPromptAgentExecutionSchema]);
const ChatPromptContentSchema = z.object({
	title: z.string().trim().min(1).max(120),
	body: z.string().max(500),
	prompt: z.string().trim().min(1).max(8e4),
	experiment_id: z.string().uuid(),
	execution: ChatPromptExecutionSchema
}).transform((fields) => ({
	type: CHAT_PROMPT_ACTION_TYPE,
	...fields
}));
const hubspotContactTargetFields = {
	contact_id: z.number().int().positive(),
	contact_name: z.string().nullable(),
	contact_email: z.string().nullable(),
	contact_url: z.string().nullable(),
	account_label: z.string().nullable()
};
const valueLabels = () => z.record(z.string(), z.record(z.string(), z.string())).default({});
const HubSpotActivityTypeSchema = z.enum([
	"note",
	"call",
	"meeting",
	"email"
]);
const HubSpotContactNoteContentSchema = z.object({
	...hubspotContactTargetFields,
	body: z.string(),
	activity_type: HubSpotActivityTypeSchema.default("note"),
	title: nullableText(),
	timestamp: nullableText(),
	start_time: nullableText(),
	end_time: nullableText(),
	call_outcome: nullableText(),
	email_direction: nullableText(),
	value_labels: valueLabels()
}).transform((fields) => ({
	type: "hubspot_contact_note",
	...fields
}));
const HubSpotContactUpdateContentSchema = z.object({
	...hubspotContactTargetFields,
	lifecyclestage: z.string().nullable(),
	hs_lead_status: z.string().nullable(),
	hubspot_owner_id: nullableText(),
	firstname: nullableText(),
	lastname: nullableText(),
	phone: nullableText(),
	jobtitle: nullableText(),
	previous_lifecyclestage: z.string().nullable(),
	previous_hs_lead_status: z.string().nullable(),
	previous_hubspot_owner_id: nullableText(),
	previous_firstname: nullableText(),
	previous_lastname: nullableText(),
	previous_phone: nullableText(),
	previous_jobtitle: nullableText(),
	value_labels: valueLabels()
}).transform((fields) => ({
	type: "hubspot_contact_update",
	...fields
}));
const HubSpotDealContentSchema = z.object({
	...hubspotContactTargetFields,
	dealname: z.string(),
	dealstage: z.string().nullable(),
	amount: z.string().nullable(),
	closedate: z.string().nullable(),
	pipeline: nullableText(),
	hubspot_owner_id: nullableText(),
	value_labels: valueLabels()
}).transform((fields) => ({
	type: "hubspot_deal",
	...fields
}));
const HubSpotCompanyContentSchema = z.object({
	name: z.string(),
	domain: z.string().nullable(),
	contact_ids: z.array(z.number().int().positive()),
	contact_labels: z.array(z.string()),
	possible_duplicate: z.string().nullable(),
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "hubspot_company",
	...fields
}));
const HubSpotContactCreateContentSchema = z.object({
	email: nullableText(),
	firstname: nullableText(),
	lastname: nullableText(),
	company: nullableText(),
	company_id: z.number().int().positive().nullable().default(null),
	company_name: nullableText(),
	possible_duplicate: nullableText(),
	account_label: nullableText()
}).transform((fields) => ({
	type: "hubspot_contact_create",
	...fields
}));
const HubSpotDealUpdateContentSchema = z.object({
	deal_id: z.number().int().positive(),
	deal_name: nullableText(),
	deal_url: nullableText(),
	dealname: nullableText(),
	dealstage: nullableText(),
	amount: nullableText(),
	closedate: nullableText(),
	pipeline: nullableText(),
	hubspot_owner_id: nullableText(),
	previous_dealname: nullableText(),
	previous_dealstage: nullableText(),
	previous_amount: nullableText(),
	previous_closedate: nullableText(),
	previous_pipeline: nullableText(),
	previous_hubspot_owner_id: nullableText(),
	account_label: nullableText(),
	value_labels: valueLabels()
}).transform((fields) => ({
	type: "hubspot_deal_update",
	...fields
}));
const HubSpotTaskContentSchema = z.object({
	target_type: z.enum(["contacts", "deals"]),
	target_id: z.number().int().positive(),
	target_name: nullableText(),
	target_url: nullableText(),
	hs_task_subject: z.string(),
	hs_task_body: nullableText(),
	hs_timestamp: nullableText(),
	hubspot_owner_id: nullableText(),
	hs_task_priority: nullableText(),
	account_label: nullableText(),
	value_labels: valueLabels()
}).transform((fields) => ({
	type: "hubspot_task",
	...fields
}));
const HubSpotTaskUpdateContentSchema = z.object({
	task_id: z.number().int().positive(),
	task_subject: nullableText(),
	task_url: nullableText(),
	hs_task_status: z.enum(["COMPLETED", "NOT_STARTED"]).nullable(),
	hs_timestamp: nullableText(),
	hubspot_owner_id: nullableText(),
	previous_hs_task_status: nullableText(),
	previous_hs_timestamp: nullableText(),
	previous_hubspot_owner_id: nullableText(),
	account_label: nullableText(),
	value_labels: valueLabels()
}).transform((fields) => ({
	type: "hubspot_task_update",
	...fields
}));
const MicrosoftTeamsMessageContentSchema = z.object({
	target_type: z.enum([
		"chat",
		"channel",
		"reply"
	]),
	chat_id: nullableText(),
	team_id: nullableText(),
	channel_id: nullableText(),
	message_id: nullableText(),
	content: z.string(),
	content_type: z.enum(["text", "html"]).default("text"),
	account_label: nullableText(),
	author: optionalActionCardPerson,
	target_label: nullableText(),
	parent_preview: nullableText()
}).transform((fields) => ({
	type: "microsoft_teams_message",
	...fields
}));
const toPreviewState = (status, editing) => (status === "draft" || status === "completed") && editing ? "editing" : status;
//#endregion
//#region src/fields.ts
const actionCardStatusLabel = (kind, state) => {
	if (state === "editing") return "Editing";
	if (state === "draft") return kind.draftLabel ?? "Needs review";
	if (state === "running") return kind.runningLabel;
	if (state === "completed") return kind.completedLabel;
	if (state === "failed") return "Failed";
	if (state === "dismissed") return kind.finalDismissal?.label ?? kind.dismissedLabel ?? "Dismissed";
	if (state === "completed_externally") return kind.acknowledge?.completedLabel ?? "Done";
	return "Withdrawn by Littlebird";
};
const isInformationalActionType = (actionType) => actionType === "informational";
const isTodoActionCard = (content) => content?.type === "informational" && content.kind === "todo";
/** An informational card that is not a to-do */
const isNoticeActionCard = (actionType, content) => isInformationalActionType(actionType) && !isTodoActionCard(content);
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const readonlyField = (field) => ({
	...field,
	editable: false
});
const editableField = (field) => ({
	...field,
	editable: true
});
const splitAddresses = (value) => value.split(",").map((address) => address.trim()).filter(Boolean);
/**
* Whether a string holds at least one HTML tag. For field values with no
* explicit is_html flag: a bare `<` comparison in plain text must stay text,
* because htmlToPlainText would silently swallow it.
*/
const looksLikeHtml = (value) => /<[a-z][a-z0-9-]*(\s[^<>]*)?\/?>/i.test(value);
//#endregion
//#region src/kinds/agent-team.ts
const agentTeamActionCardKind = {
	actionType: "agent_team",
	provider: {
		id: "agents",
		label: "Littlebird"
	},
	capability: "display-only",
	actionLabel: "Your starter team",
	confirmationLabel: "Add agents",
	confirmationVerb: "Add",
	runningLabel: "Adding agents…",
	completedLabel: "Team added",
	decode: (content) => AgentTeamContentSchema.safeParse(content).data ?? null,
	getSummary: (content) => content.agents.map((agent) => agent.name).join(", "),
	getFields: (content) => content.agents.map((agent) => readonlyField({
		key: agent.id,
		label: agent.name,
		value: agent.description,
		kind: "multiline"
	})),
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: "Team added" })
};
//#endregion
//#region src/kinds/ask-user.ts
const askUserActionCardKind = {
	actionType: "ask_user",
	provider: {
		id: "Littlebird",
		label: "Littlebird"
	},
	capability: "display-only",
	actionLabel: "Answer questions",
	confirmationLabel: "Answer",
	confirmationVerb: "Answer",
	runningLabel: "Sending answers…",
	completedLabel: "Answered",
	decode: (content) => AskUserContentSchema.safeParse(content).data ?? null,
	getFields: () => [],
	getSummary: (content) => content.questions[0].prompt,
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: "Answered" })
};
//#endregion
//#region src/kinds/coding-agent.ts
const CODING_AGENT_LABELS = {
	"claude-code": "Claude Code",
	codex: "Codex",
	cursor: "Cursor",
	devin: "Devin"
};
const folderName = (cwd) => cwd.replace(/[\\/]+$/, "").split(/[\\/]/).pop() || cwd;
/** What an access level lets that agent do. Codex's sandbox still runs commands, inside the folder */
const codingAgentAccessLabel = (agent, access) => {
	if (access === "full") return "Full access";
	return agent === "codex" ? "Sandboxed to this folder" : "Edits in this folder only";
};
/** The access levels the user may pick. Cursor's CLI only proposes changes unless it has full access */
const codingAgentAccessChoices = (agent) => agent === "cursor" ? ["full"] : ["edit", "full"];
const OPENABLE_AGENTS = /* @__PURE__ */ new Set([
	"codex",
	"claude-code",
	"cursor"
]);
const isCodingAgentRunning = (content) => content.run?.state === "starting" || content.run?.state === "working";
const canOpenCodingAgent = (content) => Boolean(content.run && content.session_id) && OPENABLE_AGENTS.has(content.agent) && !isCodingAgentRunning(content);
const canStartCodingAgent = (content) => !content.run || content.run.state === "failed" || content.run.state === "stopped";
const codingAgentSessionToContinue = (content) => content.session_id ?? null;
const codingAgentActionCardKind = {
	actionType: "coding_agent",
	provider: {
		id: "coding-agents",
		label: "Coding agents"
	},
	capability: "display-only",
	actionLabel: "Start a coding agent",
	getActionLabel: (content) => {
		if (content.title?.trim()) return content.title.trim();
		const agent = CODING_AGENT_LABELS[content.agent];
		if (content.run && !canStartCodingAgent(content)) return `${agent} run`;
		return codingAgentSessionToContinue(content) ? `Continue ${agent}` : `Start ${agent}`;
	},
	confirmationLabel: "Start",
	confirmationVerb: "Start",
	runningLabel: "Working…",
	completedLabel: "Finished",
	decode: (content) => CodingAgentContentSchema.safeParse(content).data ?? null,
	getSummary: (content) => content.title?.trim() || `${CODING_AGENT_LABELS[content.agent]} in ${folderName(content.cwd)}`,
	getFields: (content) => [
		readonlyField({
			key: "prompt",
			label: "Task",
			value: content.prompt,
			kind: "multiline"
		}),
		canStartCodingAgent(content) ? editableField({
			key: "cwd",
			label: "Folder",
			value: content.cwd,
			kind: "single",
			editor: "input"
		}) : readonlyField({
			key: "cwd",
			label: "Folder",
			value: content.cwd,
			kind: "single"
		}),
		canStartCodingAgent(content) ? editableField({
			key: "access",
			label: "Access",
			value: codingAgentAccessLabel(content.agent, content.access),
			kind: "single",
			editor: "select",
			options: codingAgentAccessChoices(content.agent).map((access) => codingAgentAccessLabel(content.agent, access))
		}) : readonlyField({
			key: "access",
			label: "Access",
			value: codingAgentAccessLabel(content.agent, content.access),
			kind: "single"
		})
	],
	withUpdatedField: (content, fieldKey, value) => {
		if (!canStartCodingAgent(content)) return content;
		if (fieldKey === "cwd") return {
			...content,
			cwd: value.trim()
		};
		if (fieldKey !== "access") return content;
		const access = codingAgentAccessChoices(content.agent).find((choice) => codingAgentAccessLabel(content.agent, choice) === value);
		return access ? {
			...content,
			access
		} : content;
	},
	getCompletion: () => ({ label: "Finished" })
};
//#endregion
//#region src/issue-content.ts
const githubTargetFields = {
	person: optionalActionCardPerson,
	owner: z.string(),
	repo: z.string(),
	account_label: z.string().nullable()
};
const GithubCommentContentSchema = z.object({
	...githubTargetFields,
	issue_number: z.number().int().positive(),
	title: z.string().nullish(),
	is_pull_request: z.boolean().nullish(),
	body: z.string()
}).transform((fields) => ({
	type: "github_comment",
	...fields
}));
const GithubPullRequestContentSchema = z.object({
	...githubTargetFields,
	title: z.string(),
	body: z.string(),
	head: z.string(),
	base: z.string(),
	draft: z.boolean(),
	maintainer_can_modify: z.boolean().nullable(),
	reviewers: z.array(z.string())
}).transform((fields) => ({
	type: "github_pull_request",
	...fields
}));
const githubExistingPullRequestFields = {
	...githubTargetFields,
	pull_number: z.number().int().positive(),
	title: z.string().min(1)
};
const GithubPullRequestUpdateContentSchema = z.object({
	...githubExistingPullRequestFields,
	body: z.string()
}).transform((fields) => ({
	type: "github_pull_request_update",
	...fields
}));
const GithubPullRequestCloseContentSchema = z.object(githubExistingPullRequestFields).transform((fields) => ({
	type: "github_pull_request_close",
	...fields
}));
const GithubPullRequestMergeContentSchema = z.object({
	...githubExistingPullRequestFields,
	head: z.string().min(1),
	base: z.string().min(1),
	expected_head_sha: z.string().min(1),
	merge_status: z.string().nullish(),
	merge_method: z.enum([
		"merge",
		"squash",
		"rebase"
	]).default("merge"),
	commit_title: z.string().nullish(),
	commit_message: z.string().nullish()
}).transform((fields) => ({
	type: "github_pull_request_merge",
	...fields
}));
const GithubPullRequestApproveContentSchema = z.object({
	...githubExistingPullRequestFields,
	head: z.string().min(1),
	expected_head_sha: z.string().min(1),
	body: z.string().default("")
}).transform((fields) => ({
	type: "github_pull_request_approve",
	...fields
}));
const githubPullRequestReviewFields = {
	...githubExistingPullRequestFields,
	head: z.string().min(1),
	expected_head_sha: z.string().min(1),
	body: z.string()
};
const GithubPullRequestReviewCommentContentSchema = z.object(githubPullRequestReviewFields).transform((fields) => ({
	type: "github_pull_request_review_comment",
	...fields
}));
const GithubPullRequestRequestChangesContentSchema = z.object(githubPullRequestReviewFields).transform((fields) => ({
	type: "github_pull_request_request_changes",
	...fields
}));
const linearIssueFields = {
	person: optionalActionCardPerson,
	state: z.string().nullable(),
	state_name: z.string().nullable(),
	assignee: z.string().nullable(),
	assignee_name: z.string().nullable(),
	account_label: z.string().nullable(),
	attachments: z.array(z.object({
		url: z.string().url().refine((url) => url.startsWith("https://")),
		title: z.string().nullable(),
		fallback_url: z.string().url().refine((url) => url.startsWith("https://")).nullish()
	})).catch([]).optional()
};
const LinearIssueContentSchema = z.object({
	...linearIssueFields,
	team: z.string(),
	team_name: z.string().nullable(),
	parent_id: z.string().nullish(),
	parent_label: z.string().nullish(),
	title: z.string(),
	description: z.string(),
	priority: z.number().int().min(0).max(4).nullable(),
	project: z.string().nullish(),
	project_name: z.string().nullish(),
	due_date: z.string().nullish(),
	labels: z.array(z.string()).optional(),
	label_names: z.array(z.string()).nullish()
}).transform((fields) => ({
	type: "linear_issue",
	...fields
}));
const LinearIssueUpdateContentSchema = z.object({
	...linearIssueFields,
	issue_id: z.string(),
	issue_label: z.string().nullable(),
	issue_title: z.string().nullish(),
	issue_url: z.string().nullish(),
	title: z.string().nullable(),
	description: z.string().nullable(),
	assignee_set: z.boolean(),
	previous_status: z.string().nullish(),
	previous_priority: z.number().int().min(0).max(4).nullish(),
	priority: z.number().int().min(0).max(4).nullable()
}).transform((fields) => ({
	type: "linear_issue_update",
	...fields
}));
const LinearCommentContentSchema = z.object({
	issue_id: z.string(),
	issue_label: z.string().nullable(),
	issue_title: z.string().nullish(),
	issue_url: z.string().nullish(),
	body: z.string(),
	attachments: linearIssueFields.attachments,
	account_label: z.string().nullable()
}).transform((fields) => ({
	type: "linear_comment",
	...fields
}));
//#endregion
//#region src/kinds/issue-fields.ts
const targetField = (label, value, href) => readonlyField({
	key: "target",
	label,
	value,
	kind: "single",
	href
});
const titleField = (value) => editableField({
	key: "title",
	label: "Title",
	value,
	kind: "single",
	editor: "input"
});
const bodyField = (key, label, value) => editableField({
	key,
	label,
	value,
	kind: "multiline",
	editor: "markdown",
	format: "markdown"
});
const detailField = (key, label, value, options) => readonlyField({
	key,
	label,
	value,
	kind: "single",
	hideWhenEmpty: true,
	advanced: options?.advanced
});
const whenChanged = (value, field) => value === null ? [] : [field(value)];
const providerResultUrl = (data, hostname) => {
	if (!isRecord(data)) return void 0;
	let result = data;
	if (typeof data.text === "string") try {
		const parsed = JSON.parse(data.text);
		if (!isRecord(parsed)) return void 0;
		result = parsed;
	} catch {
		return;
	}
	const url = result.html_url ?? result.url;
	if (typeof url !== "string") return void 0;
	try {
		const parsed = new URL(url);
		return parsed.protocol === "https:" && parsed.hostname === hostname ? url : void 0;
	} catch {
		return;
	}
};
//#endregion
//#region src/kinds/github.ts
const provider$5 = {
	id: "github",
	label: "GitHub"
};
const externalUrl$8 = (ref) => providerResultUrl(ref, "github.com");
const buildExternalRef$8 = (data) => {
	const url = providerResultUrl(data, "github.com");
	return url ? {
		provider: "github",
		url
	} : void 0;
};
const repository = (content) => `${content.owner}/${content.repo}`;
const githubCommentActionCardKind = {
	actionType: "github_comment",
	provider: provider$5,
	capability: "confirmable",
	actionLabel: "Post comment",
	confirmationLabel: "Post comment",
	confirmationVerb: "Post",
	runningLabel: "Posting comment…",
	completedLabel: "Posted",
	decode: (content) => GithubCommentContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [targetField("On", `${repository(content)} #${content.issue_number}`, `https://github.com/${encodeURIComponent(content.owner)}/${encodeURIComponent(content.repo)}/${content.is_pull_request ? "pull" : "issues"}/${content.issue_number}`), bodyField("body", "Comment", content.body)],
	getSummary: (content) => content.title?.trim() || `${content.is_pull_request ? "PR" : "Issue"} #${content.issue_number}`,
	withUpdatedField: (content, key, value) => key === "body" ? {
		...content,
		body: value
	} : content,
	getCompletion: (url) => ({
		label: "Posted",
		linkLabel: "View comment",
		url
	}),
	externalUrl: externalUrl$8,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:github",
			toolSlug: "mcp_github__add_issue_comment",
			accountLabel: content.account_label,
			arguments: {
				owner: content.owner,
				repo: content.repo,
				issue_number: content.issue_number,
				body: content.body
			}
		}],
		buildExternalRef: buildExternalRef$8,
		externalUrl: externalUrl$8,
		validate: (content) => !content.body.trim() ? "Write a comment before posting." : !content.owner.trim() || !content.repo.trim() ? "Choose a repository in chat before posting." : null
	}
};
const githubPullRequestActionCardKind = {
	actionType: "github_pull_request",
	provider: provider$5,
	capability: "confirmable",
	actionLabel: "Create pull request",
	getActionLabel: (content) => content.draft ? "Create draft pull request" : "Create pull request",
	confirmationLabel: "Create pull request",
	confirmationVerb: "Create",
	runningLabel: "Creating pull request…",
	completedLabel: "Created",
	decode: (content) => GithubPullRequestContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		targetField("Repository", repository(content)),
		detailField("branches", "Branches", `${content.head} → ${content.base}`),
		titleField(content.title),
		bodyField("body", "Description", content.body),
		detailField("draft", "Review", content.draft ? "Draft" : "Ready for review", { advanced: true }),
		detailField("reviewers", "Reviewers", content.reviewers.join(", ")),
		...content.maintainer_can_modify === null ? [] : [detailField("maintainer_can_modify", "Maintainer edits", content.maintainer_can_modify ? "Allowed" : "Not allowed", { advanced: true })]
	],
	getSummary: (content) => content.title,
	withUpdatedField: (content, key, value) => key === "title" || key === "body" ? {
		...content,
		[key]: value
	} : content,
	getCompletion: (url) => ({
		label: "Created",
		linkLabel: "View pull request",
		url
	}),
	externalUrl: externalUrl$8,
	execution: {
		buildCalls: (content) => {
			const args = {
				owner: content.owner,
				repo: content.repo,
				title: content.title,
				body: content.body,
				head: content.head,
				base: content.base,
				draft: content.draft,
				reviewers: content.reviewers
			};
			if (content.maintainer_can_modify !== null) args.maintainer_can_modify = content.maintainer_can_modify;
			return [{
				integrationName: "mcp:github",
				toolSlug: "mcp_github__create_pull_request",
				accountLabel: content.account_label,
				arguments: args
			}];
		},
		buildExternalRef: buildExternalRef$8,
		externalUrl: externalUrl$8,
		validate: (content) => !content.title.trim() ? "Add a pull request title before creating." : !content.owner.trim() || !content.repo.trim() || !content.head.trim() || !content.base.trim() ? "Choose a repository and branches in chat before creating." : null
	}
};
//#endregion
//#region src/kinds/github-pull-request.ts
const provider$4 = {
	id: "github",
	label: "GitHub"
};
const externalUrl$7 = (ref) => providerResultUrl(ref, "github.com");
const buildExternalRef$7 = (data) => {
	const url = providerResultUrl(data, "github.com");
	return url ? {
		provider: "github",
		url
	} : void 0;
};
const targetArguments = (content) => ({
	owner: content.owner,
	repo: content.repo,
	pullNumber: content.pull_number
});
const targetFields = (content) => [targetField("Pull request", `${content.owner}/${content.repo} #${content.pull_number}`, `https://github.com/${encodeURIComponent(content.owner)}/${encodeURIComponent(content.repo)}/pull/${content.pull_number}`), detailField("title", "Title", content.title)];
const getSummary$2 = (content) => content.title?.trim() || `PR #${content.pull_number}`;
const validateTarget = (content) => content.owner.trim() && content.repo.trim() && content.pull_number > 0 ? null : "Choose a pull request in chat before continuing.";
const githubPullRequestUpdateActionCardKind = {
	actionType: "github_pull_request_update",
	provider: provider$4,
	capability: "confirmable",
	actionLabel: "Update PR description",
	confirmationLabel: "Update description",
	confirmationVerb: "Update",
	runningLabel: "Updating description…",
	completedLabel: "Updated",
	decode: (content) => GithubPullRequestUpdateContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [...targetFields(content), bodyField("body", content.body ? "Description" : "Clear description", content.body)],
	getSummary: getSummary$2,
	withUpdatedField: (content, key, value) => key === "body" ? {
		...content,
		body: value
	} : content,
	getCompletion: (url) => ({
		label: "Updated",
		linkLabel: "View pull request",
		url
	}),
	externalUrl: externalUrl$7,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:github",
			toolSlug: "mcp_github__update_pull_request",
			accountLabel: content.account_label,
			arguments: {
				...targetArguments(content),
				body: content.body
			}
		}],
		buildExternalRef: buildExternalRef$7,
		externalUrl: externalUrl$7,
		validate: validateTarget
	}
};
const githubPullRequestCloseActionCardKind = {
	actionType: "github_pull_request_close",
	provider: provider$4,
	capability: "confirmable",
	actionLabel: "Close pull request",
	confirmationLabel: "Close PR",
	confirmationVerb: "Close",
	runningLabel: "Closing pull request…",
	completedLabel: "Closed",
	decode: (content) => GithubPullRequestCloseContentSchema.safeParse(content).data ?? null,
	getFields: targetFields,
	getSummary: getSummary$2,
	withUpdatedField: (content) => content,
	getCompletion: (url) => ({
		label: "Closed",
		linkLabel: "View pull request",
		url
	}),
	externalUrl: externalUrl$7,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:github",
			toolSlug: "mcp_github__update_pull_request",
			accountLabel: content.account_label,
			arguments: {
				...targetArguments(content),
				state: "closed"
			}
		}],
		buildExternalRef: buildExternalRef$7,
		externalUrl: externalUrl$7,
		validate: validateTarget
	}
};
const MERGE_METHODS = {
	"Merge commit": "merge",
	"Squash and merge": "squash",
	"Rebase and merge": "rebase"
};
const mergeMethodLabel = (method) => method === "squash" ? "Squash and merge" : method === "rebase" ? "Rebase and merge" : "Merge commit";
const mergeStatusLabel = (status) => {
	switch (status) {
		case "dirty": return "Conflicts must be resolved before merging";
		case "blocked": return "GitHub reports this merge is blocked";
		case "clean": return "No conflicts reported";
		case "behind": return "The branch needs updating before merging";
		case "unstable": return "Some checks have not passed";
		case "draft": return "This pull request is still a draft";
		default: return "GitHub has not confirmed the merge status";
	}
};
const githubPullRequestMergeActionCardKind = {
	actionType: "github_pull_request_merge",
	provider: provider$4,
	capability: "confirmable",
	actionLabel: "Merge pull request",
	confirmationLabel: "Merge PR",
	confirmationVerb: "Merge",
	runningLabel: "Merging pull request…",
	completedLabel: "Merged",
	decode: (content) => GithubPullRequestMergeContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		...targetFields(content),
		detailField("branches", "Branches", `${content.head} → ${content.base}`),
		...content.merge_status ? [detailField("merge_status", "Merge status", `${mergeStatusLabel(content.merge_status)} · Checked when drafted`)] : [],
		editableField({
			key: "merge_method",
			label: "Merge method",
			value: mergeMethodLabel(content.merge_method),
			kind: "single",
			editor: "select",
			options: Object.keys(MERGE_METHODS),
			advanced: true
		}),
		...typeof content.commit_title !== "string" ? [] : [editableField({
			key: "commit_title",
			label: "Commit title",
			value: content.commit_title,
			kind: "single",
			editor: "input"
		})],
		...typeof content.commit_message !== "string" ? [] : [editableField({
			key: "commit_message",
			label: "Commit message",
			value: content.commit_message,
			kind: "multiline",
			editor: "textarea"
		})]
	],
	getSummary: getSummary$2,
	withUpdatedField: (content, key, value) => {
		if (key === "merge_method") {
			if (!Object.hasOwn(MERGE_METHODS, value)) return content;
			const method = MERGE_METHODS[value];
			return {
				...content,
				merge_method: method
			};
		}
		return key === "commit_title" || key === "commit_message" ? {
			...content,
			[key]: value
		} : content;
	},
	getCompletion: (url) => ({
		label: "Merged",
		linkLabel: "View pull request",
		url
	}),
	externalUrl: externalUrl$7,
	execution: {
		buildCalls: (content) => {
			const args = {
				...targetArguments(content),
				merge_method: content.merge_method,
				expectedHeadSha: content.expected_head_sha
			};
			if (typeof content.commit_title === "string") args.commit_title = content.commit_title;
			if (typeof content.commit_message === "string") args.commit_message = content.commit_message;
			return [{
				integrationName: "mcp:github",
				toolSlug: "mcp_github__merge_pull_request",
				accountLabel: content.account_label,
				arguments: args
			}];
		},
		buildExternalRef: buildExternalRef$7,
		externalUrl: externalUrl$7,
		validate: validateTarget
	}
};
const githubPullRequestApproveActionCardKind = {
	actionType: "github_pull_request_approve",
	provider: provider$4,
	capability: "confirmable",
	actionLabel: "Approve pull request",
	confirmationLabel: "Approve PR",
	confirmationVerb: "Approve",
	runningLabel: "Approving pull request…",
	completedLabel: "Approved",
	decode: (content) => GithubPullRequestApproveContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		...targetFields(content),
		detailField("head", "Branch", content.head),
		bodyField("body", "Review comment (optional)", content.body)
	],
	getSummary: getSummary$2,
	withUpdatedField: (content, key, value) => key === "body" ? {
		...content,
		body: value
	} : content,
	getCompletion: (url) => ({
		label: "Approved",
		linkLabel: "View review",
		url
	}),
	externalUrl: externalUrl$7,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:github",
			toolSlug: "mcp_github__pull_request_review_write",
			accountLabel: content.account_label,
			arguments: {
				...targetArguments(content),
				method: "create",
				event: "APPROVE",
				commitID: content.expected_head_sha,
				body: content.body
			}
		}],
		buildExternalRef: buildExternalRef$7,
		externalUrl: externalUrl$7,
		validate: validateTarget
	}
};
const createGithubPullRequestReviewActionCardKind = ({ fieldLabel, event, ...kind }) => ({
	...kind,
	provider: provider$4,
	capability: "confirmable",
	getFields: (content) => [
		...targetFields(content),
		detailField("head", "Branch", content.head),
		bodyField("body", fieldLabel, content.body)
	],
	getSummary: getSummary$2,
	withUpdatedField: (content, key, value) => key === "body" ? {
		...content,
		body: value
	} : content,
	getCompletion: (url) => ({
		label: kind.completedLabel,
		linkLabel: "View review",
		url
	}),
	externalUrl: externalUrl$7,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:github",
			toolSlug: "mcp_github__pull_request_review_write",
			accountLabel: content.account_label,
			arguments: {
				...targetArguments(content),
				method: "create",
				event,
				commitID: content.expected_head_sha,
				body: content.body
			}
		}],
		buildExternalRef: buildExternalRef$7,
		externalUrl: externalUrl$7,
		validate: (content) => validateTarget(content) ?? (content.body.trim() ? null : "Add a review comment before continuing.")
	}
});
const githubPullRequestReviewCommentActionCardKind = createGithubPullRequestReviewActionCardKind({
	actionType: "github_pull_request_review_comment",
	actionLabel: "Post review",
	confirmationLabel: "Post review",
	confirmationVerb: "Post",
	runningLabel: "Posting review…",
	completedLabel: "Review posted",
	fieldLabel: "Review comment",
	event: "COMMENT",
	decode: (content) => GithubPullRequestReviewCommentContentSchema.safeParse(content).data ?? null
});
const githubPullRequestRequestChangesActionCardKind = createGithubPullRequestReviewActionCardKind({
	actionType: "github_pull_request_request_changes",
	actionLabel: "Request changes",
	confirmationLabel: "Request changes",
	confirmationVerb: "Request",
	runningLabel: "Requesting changes…",
	completedLabel: "Changes requested",
	fieldLabel: "Requested changes",
	event: "REQUEST_CHANGES",
	decode: (content) => GithubPullRequestRequestChangesContentSchema.safeParse(content).data ?? null
});
//#endregion
//#region src/kinds/email-shared.ts
const emailAttachmentFields = (content) => {
	if (content.type !== "gmail_email_draft_with_attachments") return [];
	return [readonlyField({
		key: "attachment",
		label: "Attachment",
		value: content.attachment.filename,
		kind: "single"
	})];
};
const emailFields = (content) => {
	const fields = [];
	if (content.account_label) fields.push(readonlyField({
		key: "account_label",
		label: "From",
		value: content.account_label,
		kind: "single"
	}));
	fields.push(editableField({
		key: "to",
		label: "To",
		value: content.to.join(", "),
		kind: "single",
		editor: "input"
	}), editableField({
		key: "cc",
		label: "Cc",
		value: content.cc.join(", "),
		kind: "single",
		editor: "input",
		hideWhenEmpty: true
	}), editableField({
		key: "bcc",
		label: "Bcc",
		value: content.bcc.join(", "),
		kind: "single",
		editor: "input",
		hideWhenEmpty: true
	}), editableField({
		key: "subject",
		label: "Subject",
		value: content.subject,
		kind: "single",
		editor: "input"
	}), editableField({
		key: "body",
		label: "Body",
		value: content.body,
		format: content.is_html ? "html" : "text",
		kind: "multiline",
		editor: content.is_html ? "html" : "textarea",
		placeholder: "Write your email"
	}));
	fields.push(...emailAttachmentFields(content));
	return fields;
};
const updateEmailField = (content, fieldKey, value) => {
	if (fieldKey === "to") return {
		...content,
		to: splitAddresses(value)
	};
	if (fieldKey === "cc") return {
		...content,
		cc: splitAddresses(value)
	};
	if (fieldKey === "bcc") return {
		...content,
		bcc: splitAddresses(value)
	};
	if (fieldKey === "subject") return {
		...content,
		subject: value
	};
	if (fieldKey === "body") return {
		...content,
		body: value
	};
	return content;
};
//#endregion
//#region src/kinds/gmail.ts
const buildExternalRef$6 = (data) => {
	if (!isRecord(data) || typeof data.id !== "string") return void 0;
	return {
		provider: "gmail",
		message_id: data.id,
		thread_id: data.threadId,
		account_email: data.account_email
	};
};
const externalUrl$6 = (externalRef) => {
	const messageId = externalRef?.message_id;
	if (typeof messageId !== "string") return void 0;
	const threadId = externalRef?.thread_id;
	const id = typeof threadId === "string" && threadId ? threadId : messageId;
	const accountEmail = externalRef?.account_email;
	return `https://mail.google.com/mail/${typeof accountEmail === "string" && accountEmail ? `?authuser=${encodeURIComponent(accountEmail)}` : ""}#all/${id}`;
};
const isReply = (externalRef) => typeof externalRef?.requested_thread_id === "string" && externalRef.requested_thread_id === externalRef.thread_id;
const hasReplyThread = (externalRef) => typeof externalRef?.requested_thread_id === "string" || typeof externalRef?.thread_id === "string";
const threadSubject = (content, externalRef) => typeof externalRef?.thread_subject === "string" && externalRef.thread_subject ? externalRef.thread_subject : content.subject || "Original subject unavailable";
const gmailFields = (content, externalRef) => {
	const fields = emailFields(content);
	if (!hasReplyThread(externalRef)) return [readonlyField({
		key: "delivery",
		label: "Delivery",
		value: "New email",
		kind: "single"
	}), ...fields];
	return [readonlyField({
		key: "delivery",
		label: "Delivery",
		value: isReply(externalRef) ? "Reply in existing Gmail thread" : "Reply thread could not be verified",
		kind: "single"
	}), ...fields.map((field) => field.key === "subject" ? readonlyField({
		key: "thread_subject",
		label: "Original subject",
		value: threadSubject(content, externalRef),
		kind: "single"
	}) : field)];
};
const hasOnlyEditableReplyChanges = (content, externalRef) => {
	const original = externalRef.draft_content;
	return isRecord(original) && original.type === content.type && original.subject === content.subject && original.is_html === content.is_html && original.account_label === content.account_label && JSON.stringify(original.attachment ?? null) === JSON.stringify(content.type === "gmail_email_draft_with_attachments" ? content.attachment : null);
};
const threadedDraftError = (content, externalRef) => {
	if (!hasReplyThread(externalRef)) return null;
	if (typeof externalRef?.draft_id !== "string" || !externalRef.draft_id) return "This reply draft is missing its Gmail draft ID. Create a new reply draft.";
	if (typeof externalRef.requested_thread_id !== "string") return "This draft's reply thread cannot be verified. Create a new reply draft.";
	if (typeof externalRef.requested_thread_id === "string" && externalRef.thread_id !== externalRef.requested_thread_id) return "Gmail did not create this draft in the requested thread. Create a new reply draft.";
	if (typeof externalRef.thread_subject !== "string" || !externalRef.thread_subject) return "The original subject is unavailable. Create a new reply draft before sending.";
	if (!hasOnlyEditableReplyChanges(content, externalRef)) return "Only recipients and message text can be changed on this reply draft.";
	return null;
};
const buildArguments$2 = (content) => {
	const args = {
		recipient_email: content.to[0] ?? "",
		extra_recipients: content.to.slice(1),
		cc: [...content.cc],
		bcc: [...content.bcc],
		subject: content.subject,
		body: content.body,
		is_html: content.is_html
	};
	if (content.type !== "gmail_email_draft_with_attachments") return args;
	return {
		...args,
		attachment: {
			lb_file_id: content.attachment.file_id,
			name: content.attachment.filename,
			mimetype: content.attachment.mimetype
		}
	};
};
const buildReplyCall = (content, externalRef, draftId) => {
	const draftSubject = externalRef.thread_subject;
	const threadId = externalRef.requested_thread_id;
	if (typeof draftSubject !== "string" || typeof threadId !== "string") return [];
	return [{
		integrationName: "gmail",
		toolSlug: "LB_GMAIL_SEND_CARD_REPLY",
		arguments: {
			draft_id: draftId,
			thread_id: threadId,
			thread_subject: draftSubject,
			to: content.to,
			cc: content.cc,
			bcc: content.bcc,
			body: content.body,
			is_html: content.is_html
		},
		accountLabel: content.account_label
	}];
};
const sharedKind = {
	provider: {
		id: "gmail",
		label: "Gmail"
	},
	capability: "confirmable",
	actionLabel: "Send new email",
	confirmationLabel: "Send",
	confirmationVerb: "Send",
	runningLabel: "Sending…",
	completedLabel: "Sent",
	getFields: gmailFields,
	getActionLabel: (_content, externalRef) => isReply(externalRef) ? "Reply in existing thread" : hasReplyThread(externalRef) ? "Reply needs attention" : "Send new email",
	getSummary: (content, externalRef) => hasReplyThread(externalRef) ? threadSubject(content, externalRef) : content.subject,
	withUpdatedField: updateEmailField,
	getCompletion: (url) => ({
		label: "Sent",
		linkLabel: "View in Gmail",
		url
	}),
	externalUrl: externalUrl$6,
	execution: {
		buildCalls: (content, externalRef) => {
			if (threadedDraftError(content, externalRef)) return [];
			const toolArguments = buildArguments$2(content);
			const draftId = externalRef?.draft_id;
			if (typeof draftId !== "string" || draftId.length === 0) return [{
				integrationName: "gmail",
				toolSlug: "GMAIL_SEND_EMAIL",
				arguments: toolArguments,
				accountLabel: content.account_label
			}];
			if (externalRef && hasReplyThread(externalRef)) return buildReplyCall(content, externalRef, draftId);
			return [{
				integrationName: "gmail",
				toolSlug: "GMAIL_SEND_EMAIL",
				arguments: toolArguments,
				accountLabel: content.account_label
			}, {
				integrationName: "gmail",
				toolSlug: "GMAIL_DELETE_DRAFT",
				arguments: { draft_id: draftId },
				accountLabel: content.account_label,
				bestEffort: true
			}];
		},
		buildExternalRef: buildExternalRef$6,
		externalUrl: externalUrl$6,
		validate: (content, externalRef) => {
			if (content.to.length === 0) return "Add at least one recipient before sending.";
			const draftError = threadedDraftError(content, externalRef);
			if (draftError) return draftError;
			return content.body.trim().length === 0 ? "Write your email before sending." : null;
		}
	}
};
const gmailActionCardKind = {
	...sharedKind,
	actionType: "gmail_email_draft",
	decode: (content) => GmailEmailDraftContentSchema.safeParse(content).data ?? null
};
const gmailWithAttachmentsActionCardKind = {
	...sharedKind,
	actionType: "gmail_email_draft_with_attachments",
	decode: (content) => GmailEmailDraftWithAttachmentsContentSchema.safeParse(content).data ?? null
};
//#endregion
//#region src/kinds/gmail-label-review.ts
const gmailLabelReviewActionCardKind = {
	actionType: "gmail_label_review",
	provider: {
		id: "gmail",
		label: "Gmail"
	},
	capability: "confirmable",
	actionLabel: "Apply selected labels",
	confirmationLabel: "Apply selected labels",
	confirmationVerb: "Apply",
	runningLabel: "Applying labels…",
	completedLabel: "Labels applied",
	decode: (content) => GmailLabelReviewContentSchema.safeParse(content).data ?? null,
	getSummary: (content) => content.result_summary || `${content.items.length} suggested Gmail labels`,
	getFields: (content) => [readonlyField({
		key: "account",
		label: "Account",
		value: content.account_label,
		kind: "single"
	})],
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: "Label request completed" }),
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: (content) => content.selected_source_ids.length === 0 ? "Select at least one email." : null
	}
};
//#endregion
//#region src/kinds/gmail-noise-archive.ts
const gmailNoiseArchiveActionCardKind = {
	actionType: "gmail_noise_archive",
	provider: {
		id: "gmail",
		label: "Gmail"
	},
	capability: "confirmable",
	actionLabel: "Archive selected",
	confirmationLabel: "Archive selected",
	confirmationVerb: "Archive",
	runningLabel: "Archiving…",
	completedLabel: "Archived selected",
	decode: (content) => GmailNoiseArchiveContentSchema.safeParse(content).data ?? null,
	getSummary: (content) => content.result_summary || `${content.items.length} unwanted email${content.items.length === 1 ? "" : "s"}`,
	getFields: (content) => [readonlyField({
		key: "account",
		label: "Account",
		value: content.account_label,
		kind: "single"
	})],
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: "Archive request completed" }),
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: (content) => content.selected_source_ids.length === 0 ? "Select at least one email." : null
	}
};
//#endregion
//#region src/kinds/google-calendar/shared.ts
const OFFSETLESS_ZONE_LABEL = /^(?:GMT|UTC)$/;
const DATE_FORMAT = {
	weekday: "short",
	month: "short",
	day: "numeric"
};
const TIME_FORMAT = {
	hour: "numeric",
	minute: "2-digit"
};
const zonedFormatters = /* @__PURE__ */ new Map();
const zonedFormatter = (timeZone, options) => {
	const key = `${timeZone} ${JSON.stringify(options)}`;
	let formatter = zonedFormatters.get(key);
	if (!formatter) {
		formatter = new Intl.DateTimeFormat("en-US", {
			...options,
			timeZone
		});
		zonedFormatters.set(key, formatter);
	}
	return formatter;
};
const isUsableTimeZone = (timeZone) => {
	try {
		zonedFormatter(timeZone, {});
		return true;
	} catch {
		return false;
	}
};
const offsetZoneLabel = (offsetMinutes) => {
	const sign = offsetMinutes < 0 ? "-" : "+";
	const magnitude = Math.abs(offsetMinutes);
	const hours = Math.floor(magnitude / 60);
	const minutes = magnitude % 60;
	return `GMT${sign}${minutes === 0 ? `${hours}` : `${hours}:${minutes.toString().padStart(2, "0")}`}`;
};
const timeZoneLabel = (instant, timeZone) => {
	if (!isUsableTimeZone(timeZone)) return timeZone;
	const label = zonedFormatter(timeZone, { timeZoneName: "short" }).formatToParts(instant).find((part) => part.type === "timeZoneName")?.value;
	if (!label) return timeZone;
	if (!OFFSETLESS_ZONE_LABEL.test(label)) return label;
	const offsetMinutes = Math.round(zoneOffsetMs(instant, timeZone) / 6e4);
	return offsetMinutes === 0 ? label : offsetZoneLabel(offsetMinutes);
};
const formatEventTime = (startValue, endValue, timeZone) => {
	const start = new Date(startValue);
	const end = new Date(endValue);
	const formatterZone = isUsableTimeZone(timeZone) ? timeZone : "UTC";
	const dateFormatter = zonedFormatter(formatterZone, DATE_FORMAT);
	const timeFormatter = zonedFormatter(formatterZone, TIME_FORMAT);
	const zone = timeZoneLabel(start, timeZone);
	const startDate = dateFormatter.format(start);
	const endDate = dateFormatter.format(end);
	const startTime = timeFormatter.format(start);
	const endTime = timeFormatter.format(end);
	if (startDate === endDate) return `${startDate} · ${startTime}–${endTime} ${zone}`;
	return `${startDate} · ${startTime} ${zone} – ${endDate} · ${endTime} ${timeZoneLabel(end, timeZone)}`;
};
const NAIVE_FORMAT = {
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
	hour: "2-digit",
	minute: "2-digit",
	second: "2-digit",
	hourCycle: "h23"
};
const toNaiveDateTime = (instant, timeZone) => {
	const formatter = zonedFormatter(timeZone, NAIVE_FORMAT);
	const parts = Object.fromEntries(formatter.formatToParts(instant).map((part) => [part.type, part.value]));
	return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
};
const zoneOffsetMs = (instant, timeZone) => Date.parse(`${toNaiveDateTime(instant, timeZone)}Z`) - instant.getTime();
/**
* Inverse of `instantToZonedIso`: the UTC instant whose wall clock in `timeZone` reads
* `wall` ('YYYY-MM-DDTHH:mm'). Standard double-adjust: a first guess from the zone's
* offset at that wall time can land on the wrong side of a DST transition, so a second
* pass re-reads the offset at the adjusted instant. Wall times inside a spring-forward
* gap or fall-back fold have no single correct instant; this still returns one
* deterministically rather than throwing.
*/
const zonedWallClockToInstant = (wall, timeZone) => {
	const guess = /* @__PURE__ */ new Date(`${wall}:00Z`);
	const adjusted = new Date(guess.getTime() - zoneOffsetMs(guess, timeZone));
	return new Date(guess.getTime() - zoneOffsetMs(adjusted, timeZone));
};
/** The UTC `instant`, formatted as a wall clock in `timeZone` with that zone's offset. */
const instantToZonedIso = (instant, timeZone) => {
	const wall = toNaiveDateTime(instant, timeZone);
	const offsetMinutes = Math.round(zoneOffsetMs(instant, timeZone) / 6e4);
	return `${wall}${offsetMinutes < 0 ? "-" : "+"}${Math.floor(Math.abs(offsetMinutes) / 60).toString().padStart(2, "0")}:${(Math.abs(offsetMinutes) % 60).toString().padStart(2, "0")}`;
};
const onOffLabel = (enabled) => enabled ? "On" : "Off";
const buildExternalRef$5 = (data) => {
	const event = isRecord(data) && isRecord(data.response_data) ? data.response_data : data;
	if (!isRecord(event) || typeof event.id !== "string") return void 0;
	return {
		provider: "google_calendar",
		event_id: event.id,
		html_link: typeof event.htmlLink === "string" ? event.htmlLink : void 0
	};
};
const externalUrl$5 = (externalRef) => {
	const htmlLink = externalRef?.html_link;
	return typeof htmlLink === "string" ? htmlLink : void 0;
};
const parseWhenRange = (value) => {
	let parsed;
	try {
		parsed = JSON.parse(value);
	} catch {
		return null;
	}
	if (!isRecord(parsed)) return null;
	const { start, end, timezone } = parsed;
	if (typeof start !== "string" || typeof end !== "string") return null;
	if (Number.isNaN(new Date(start).getTime())) return null;
	if (Number.isNaN(new Date(end).getTime())) return null;
	if (timezone === void 0) return {
		start,
		end
	};
	if (typeof timezone !== "string" || timezone.length === 0) return null;
	return {
		start,
		end,
		timezone
	};
};
/**
* The write half of the `when` field contract `parseWhenRange` reads: the event
* moved to `nextStart`, with its end kept the same distance away and its zone
* kept unless the surface offers one. A surface that also edits the length or
* the zone passes them in; one that only moves the event does not.
*/
const buildWhenRangeValue = (range, nextStart, options) => {
	const timezone = options?.timezone ?? range.timezone;
	const durationMs = options?.durationMs ?? new Date(range.end).getTime() - new Date(range.start).getTime();
	return JSON.stringify({
		start: instantToZonedIso(nextStart, timezone),
		end: instantToZonedIso(new Date(nextStart.getTime() + durationMs), timezone),
		timezone
	});
};
const calendarLabels = (confirmationLabel, confirmationVerb, runningLabel, completedLabel) => ({
	confirmationLabel,
	confirmationVerb,
	runningLabel,
	completedLabel,
	getCompletion: (url) => ({
		label: completedLabel,
		linkLabel: "Open in Calendar",
		url
	})
});
const DATE_ONLY$1 = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 864e5;
const formatAllDay = (start, end) => {
	const formatter = zonedFormatter("UTC", DATE_FORMAT);
	const first = Date.parse(`${start}T00:00:00Z`);
	const last = end !== null && DATE_ONLY$1.test(end) ? Date.parse(`${end}T00:00:00Z`) - DAY_MS : first;
	const firstLabel = formatter.format(first);
	if (Number.isNaN(last) || last <= first) return `${firstLabel} · All day`;
	return `${firstLabel} – ${formatter.format(last)} · All day`;
};
const displayTimeZone = (timezone) => timezone ?? new Intl.DateTimeFormat().resolvedOptions().timeZone;
const timedRange = (start, end, timezone) => {
	if (start === null || end === null || DATE_ONLY$1.test(start)) return null;
	if (Number.isNaN(Date.parse(start)) || Number.isNaN(Date.parse(end))) return null;
	return {
		start,
		end,
		timezone
	};
};
//#endregion
//#region src/kinds/google-calendar/event.ts
const wallClockMinutes = (start, end, timeZone) => {
	const startWallClock = Date.parse(`${toNaiveDateTime(start, timeZone)}Z`);
	const wallClockDiff = Date.parse(`${toNaiveDateTime(end, timeZone)}Z`) - startWallClock;
	const instantDiff = end.getTime() - start.getTime();
	return Math.round((wallClockDiff < 0 && instantDiff > 0 ? instantDiff : wallClockDiff) / 6e4);
};
const identityFields = (content) => [
	editableField({
		key: "title",
		label: "Title",
		value: content.title,
		kind: "single",
		editor: "input"
	}),
	editableField({
		key: "when",
		label: "When",
		value: formatEventTime(content.start, content.end, content.timezone),
		kind: "single",
		editor: "datetime-range",
		range: {
			start: content.start,
			end: content.end,
			timezone: content.timezone
		}
	}),
	editableField({
		key: "attendees",
		label: "Guests",
		value: content.attendees.join(", "),
		kind: "single",
		editor: "input",
		hideWhenEmpty: true
	})
];
const effectiveCreateMeetingRoom = (content) => {
	if (content.create_meeting_room !== null) return content.create_meeting_room;
	if (content.attendees.length > 0) return true;
	return null;
};
const getFields$6 = (content) => {
	const fields = identityFields(content);
	const descriptionIsHtml = looksLikeHtml(content.description);
	fields.push(editableField({
		key: "location",
		label: "Location",
		value: content.location,
		kind: "single",
		editor: "input",
		hideWhenEmpty: true
	}), editableField({
		key: "description",
		label: "Description",
		value: content.description,
		kind: "multiline",
		editor: descriptionIsHtml ? "html" : "textarea",
		hideWhenEmpty: true,
		collapsible: !descriptionIsHtml,
		format: descriptionIsHtml ? "html" : "text"
	}));
	fields.push(editableField({
		key: "create_meeting_room",
		label: "Google Meet",
		value: onOffLabel(effectiveCreateMeetingRoom(content) === true),
		kind: "single",
		editor: "select",
		options: ["On", "Off"],
		advanced: true
	}), editableField({
		key: "send_updates",
		label: "Notify guests",
		value: onOffLabel(content.send_updates === true),
		kind: "single",
		editor: "select",
		options: ["On", "Off"],
		advanced: true
	}));
	return fields;
};
const buildArguments$1 = (content) => {
	const start = new Date(content.start);
	const end = new Date(content.end);
	const totalMinutes = wallClockMinutes(start, end, content.timezone);
	const args = {
		summary: content.title,
		start_datetime: toNaiveDateTime(start, content.timezone),
		timezone: content.timezone,
		event_duration_hour: Math.floor(totalMinutes / 60),
		event_duration_minutes: totalMinutes % 60,
		attendees: [...content.attendees],
		location: content.location,
		description: content.description
	};
	const createMeetingRoom = effectiveCreateMeetingRoom(content);
	if (createMeetingRoom !== null) args.create_meeting_room = createMeetingRoom;
	if (content.send_updates !== null) args.send_updates = content.send_updates;
	return args;
};
const reviseEventId = (externalRef) => {
	const eventId = externalRef?.event_id;
	return typeof eventId === "string" && eventId.length > 0 ? eventId : null;
};
const canRevise = (externalRef) => reviseEventId(externalRef) !== null;
const buildPatchArguments = (content, eventId) => {
	const args = {
		calendar_id: "primary",
		event_id: eventId,
		summary: content.title,
		start_time: content.start,
		end_time: content.end,
		timezone: content.timezone,
		attendees: [...content.attendees],
		location: content.location,
		description: content.description
	};
	if (content.send_updates !== null) args.send_updates = content.send_updates;
	return args;
};
const validate = (content) => {
	const start = new Date(content.start);
	const end = new Date(content.end);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "This event needs a valid start and end time.";
	if (end.getTime() <= start.getTime()) return "End time must be after the start time.";
	if (!isUsableTimeZone(content.timezone)) return `"${content.timezone}" is not a timezone this event can be created in. Edit the time to pick another.`;
	const totalMinutes = wallClockMinutes(start, end, content.timezone);
	if (totalMinutes < 1) return "This event is too short to create.";
	if (totalMinutes > 1499) return "Events can be at most 24 hours and 59 minutes long.";
	return null;
};
const googleCalendarActionCardKind = {
	actionType: "google_calendar_event",
	provider: {
		id: "google-calendar",
		label: "Google Calendar"
	},
	capability: "confirmable",
	actionLabel: "Create event",
	confirmationLabel: "Create event",
	confirmationVerb: "Create",
	runningLabel: "Creating event…",
	completedLabel: "Created",
	revision: {
		confirmationLabel: "Update event",
		runningLabel: "Updating event…",
		canRevise
	},
	decode: (content) => GoogleCalendarEventContentSchema.safeParse(content).data ?? null,
	getCalendarProposal: (content, externalRef) => ({
		title: content.title,
		start: content.start,
		end: content.end,
		timezone: content.timezone,
		replacesEventId: reviseEventId(externalRef)
	}),
	getFields: getFields$6,
	getSummary: (content) => content.title,
	withUpdatedField: (content, fieldKey, value) => {
		if (fieldKey === "title" || fieldKey === "location" || fieldKey === "description") return {
			...content,
			[fieldKey]: value
		};
		if (fieldKey === "when") {
			const range = parseWhenRange(value);
			if (!range) return content;
			return {
				...content,
				start: range.start,
				end: range.end,
				timezone: range.timezone ?? content.timezone
			};
		}
		if (fieldKey === "attendees") return {
			...content,
			attendees: splitAddresses(value)
		};
		if (fieldKey === "create_meeting_room") {
			if (value === "Off") return {
				...content,
				create_meeting_room: false
			};
			if (value === "On") return {
				...content,
				create_meeting_room: true
			};
			return content;
		}
		if (fieldKey === "send_updates") {
			if (value === "Off") return {
				...content,
				send_updates: false
			};
			if (value === "On") return {
				...content,
				send_updates: true
			};
			return content;
		}
		return content;
	},
	getCompletion: (url) => ({
		label: "Created",
		linkLabel: "Open in Calendar",
		url
	}),
	externalUrl: externalUrl$5,
	execution: {
		buildCalls: (content, externalRef) => {
			const eventId = reviseEventId(externalRef);
			if (eventId) return [{
				integrationName: "googlecalendar",
				toolSlug: "GOOGLECALENDAR_PATCH_EVENT",
				arguments: buildPatchArguments(content, eventId),
				accountLabel: content.account_label
			}];
			return [{
				integrationName: "googlecalendar",
				toolSlug: "GOOGLECALENDAR_CREATE_EVENT",
				arguments: buildArguments$1(content),
				accountLabel: content.account_label
			}];
		},
		buildExternalRef: buildExternalRef$5,
		externalUrl: externalUrl$5,
		validate
	}
};
//#endregion
//#region src/kinds/google-calendar/event-update.ts
const MOVE_LABELS = calendarLabels("Move event", "Move", "Moving…", "Moved");
const UPDATE_LABELS = calendarLabels("Update event", "Update", "Updating…", "Updated");
const eventUpdateTitle = (content) => content.title || "(No title)";
const eventUpdateRange = (content) => timedRange(content.start, content.end, displayTimeZone(content.timezone));
const sameInstant = (next, previous) => previous !== null && Date.parse(next) === Date.parse(previous);
const guestKey = (email) => email.trim().toLowerCase();
const guestsMissingFrom = (emails, others) => {
	const otherKeys = new Set(others.map(guestKey));
	return emails.filter((email) => !otherKeys.has(guestKey(email)));
};
const eventUpdateChanges = (content) => {
	const { start, end } = content;
	const timeChanged = start !== null && end !== null && !(sameInstant(start, content.previous_start) && sameInstant(end, content.previous_end));
	return {
		title: content.title !== content.previous_title,
		time: timeChanged,
		location: content.location !== content.previous_location,
		description: content.description !== content.previous_description,
		addedGuests: guestsMissingFrom(content.attendees, content.previous_attendees),
		removedGuests: guestsMissingFrom(content.previous_attendees, content.attendees)
	};
};
const changesOnlyTime = (changes) => changes.time && !changes.title && !changes.location && !changes.description && changes.addedGuests.length === 0 && changes.removedGuests.length === 0;
const eventUpdateLabels = (content) => changesOnlyTime(eventUpdateChanges(content)) ? MOVE_LABELS : UPDATE_LABELS;
const previousTimeLabel = (content) => {
	const range = timedRange(content.previous_start, content.previous_end, displayTimeZone(content.timezone));
	return range ? formatEventTime(range.start, range.end, range.timezone) : "";
};
const eventUpdateWhenField = (content, timeChanged) => {
	const range = eventUpdateRange(content);
	if (range === null) {
		const label = content.start !== null && DATE_ONLY$1.test(content.start) ? formatAllDay(content.start, content.end) : "";
		return detailField("when", "When", label);
	}
	return editableField({
		key: "when",
		label: timeChanged ? "New time" : "When",
		value: formatEventTime(range.start, range.end, range.timezone),
		kind: "single",
		editor: "datetime-range",
		range
	});
};
const previousValue = (changed, value) => changed ? value : "";
const eventUpdateGuestFields = (content, changes) => [
	editableField({
		key: "attendees",
		label: "Guests",
		value: content.attendees.join(", "),
		kind: "single",
		editor: "input",
		hideWhenEmpty: true
	}),
	detailField("added_attendees", "Adds", changes.addedGuests.join(", ")),
	detailField("removed_attendees", "Removes", changes.removedGuests.join(", "))
];
const eventUpdateNotifyFields = (content) => content.attendees.length > 0 || content.previous_attendees.length > 0 ? [editableField({
	key: "notify_guests",
	label: "Notify guests",
	value: onOffLabel(content.notify_guests),
	kind: "single",
	editor: "select",
	options: ["On", "Off"]
})] : [];
const eventUpdateDescriptionFields = (content, changes) => {
	const descriptionIsHtml = looksLikeHtml(content.description);
	return [readonlyField({
		key: "previous_description",
		label: "Previous description",
		value: previousValue(changes.description, content.previous_description),
		kind: "multiline",
		hideWhenEmpty: true,
		collapsible: true,
		format: looksLikeHtml(content.previous_description) ? "html" : "text"
	}), editableField({
		key: "description",
		label: "Description",
		value: content.description,
		kind: "multiline",
		editor: descriptionIsHtml ? "html" : "textarea",
		hideWhenEmpty: true,
		collapsible: !descriptionIsHtml,
		format: descriptionIsHtml ? "html" : "text"
	})];
};
const eventUpdateFields = (content) => {
	const changes = eventUpdateChanges(content);
	return [
		detailField("previous_title", "Previous title", previousValue(changes.title, content.previous_title)),
		editableField({
			key: "title",
			label: "Title",
			value: content.title,
			kind: "single",
			editor: "input"
		}),
		detailField("previous_when", "Now", previousValue(changes.time, previousTimeLabel(content))),
		eventUpdateWhenField(content, changes.time),
		detailField("organizer", "Organizer", content.organizer ?? ""),
		...eventUpdateGuestFields(content, changes),
		detailField("previous_location", "Previous location", previousValue(changes.location, content.previous_location)),
		editableField({
			key: "location",
			label: "Location",
			value: content.location,
			kind: "single",
			editor: "input",
			hideWhenEmpty: true
		}),
		...eventUpdateDescriptionFields(content, changes),
		...eventUpdateNotifyFields(content)
	];
};
const updateNotifyGuests = (content, value) => {
	if (value === "Off") return {
		...content,
		notify_guests: false
	};
	if (value === "On") return {
		...content,
		notify_guests: true
	};
	return content;
};
const FIELD_UPDATERS = {
	title: (content, title) => ({
		...content,
		title
	}),
	location: (content, location) => ({
		...content,
		location
	}),
	description: (content, description) => ({
		...content,
		description
	}),
	attendees: (content, value) => ({
		...content,
		attendees: splitAddresses(value)
	}),
	when: (content, value) => {
		if (eventUpdateRange(content) === null) return content;
		const range = parseWhenRange(value);
		return range ? {
			...content,
			start: range.start,
			end: range.end
		} : content;
	},
	notify_guests: updateNotifyGuests
};
const isEditableKey = (fieldKey) => Object.hasOwn(FIELD_UPDATERS, fieldKey);
const googleCalendarEventUpdateActionCardKind = {
	actionType: "google_calendar_event_update",
	provider: {
		id: "google-calendar",
		label: "Google Calendar"
	},
	capability: "confirmable",
	actionLabel: "Update event",
	getActionLabel: (content) => eventUpdateLabels(content).confirmationLabel,
	...UPDATE_LABELS,
	getLabels: eventUpdateLabels,
	decode: (content) => GoogleCalendarEventUpdateContentSchema.safeParse(content).data ?? null,
	getCalendarProposal: (content) => {
		const range = eventUpdateRange(content);
		return range ? {
			title: content.title,
			...range,
			replacesEventId: content.event_id
		} : null;
	},
	getFields: eventUpdateFields,
	getSummary: eventUpdateTitle,
	withUpdatedField: (content, fieldKey, value) => isEditableKey(fieldKey) ? FIELD_UPDATERS[fieldKey](content, value) : content,
	externalUrl: externalUrl$5,
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: () => null
	}
};
//#endregion
//#region src/kinds/google-calendar/rsvp.ts
const RSVP_CHOICES = {
	accepted: {
		option: "Accept",
		labels: calendarLabels("Accept", "Accept", "Accepting…", "Accepted")
	},
	tentative: {
		option: "Maybe",
		labels: calendarLabels("Reply maybe", "Reply", "Replying…", "Replied maybe")
	},
	declined: {
		option: "Decline",
		labels: calendarLabels("Decline", "Decline", "Declining…", "Declined")
	}
};
const RSVP_RESPONSES = GoogleCalendarRsvpResponseSchema.options;
const rsvpTimedRange = (content) => timedRange(content.start, content.end, displayTimeZone(content.timezone));
const rsvpWhen = (content) => {
	const { start, end } = content;
	if (start !== null && DATE_ONLY$1.test(start)) return Number.isNaN(Date.parse(start)) ? "" : formatAllDay(start, end);
	const range = rsvpTimedRange(content);
	return range ? formatEventTime(range.start, range.end, range.timezone) : "";
};
const googleCalendarRsvpActionCardKind = {
	actionType: "google_calendar_rsvp",
	provider: {
		id: "google-calendar",
		label: "Google Calendar"
	},
	capability: "confirmable",
	actionLabel: "Reply to invite",
	...RSVP_CHOICES.accepted.labels,
	getLabels: (content) => RSVP_CHOICES[content.response].labels,
	decode: (content) => GoogleCalendarRsvpContentSchema.safeParse(content).data ?? null,
	getCalendarProposal: (content) => {
		const range = rsvpTimedRange(content);
		return range ? {
			title: content.title,
			...range,
			replacesEventId: content.event_id
		} : null;
	},
	getFields: (content) => [
		targetField("Event", content.title || "(No title)"),
		detailField("when", "When", rsvpWhen(content)),
		detailField("organizer", "Organizer", content.organizer ?? ""),
		editableField({
			key: "response",
			label: "Response",
			value: RSVP_CHOICES[content.response].option,
			kind: "single",
			editor: "select",
			options: RSVP_RESPONSES.map((response) => RSVP_CHOICES[response].option)
		})
	],
	getSummary: (content) => content.title || "(No title)",
	withUpdatedField: (content, fieldKey, value) => {
		if (fieldKey !== "response") return content;
		const response = RSVP_RESPONSES.find((candidate) => RSVP_CHOICES[candidate].option === value);
		return response ? {
			...content,
			response
		} : content;
	},
	externalUrl: externalUrl$5,
	execution: {
		buildCalls: (content) => [{
			integrationName: "googlecalendar",
			toolSlug: "GOOGLECALENDAR_PATCH_EVENT",
			arguments: {
				calendar_id: "primary",
				event_id: content.event_id,
				rsvp_response: content.response
			},
			accountLabel: content.account_label
		}],
		buildExternalRef: buildExternalRef$5,
		externalUrl: externalUrl$5
	}
};
//#endregion
//#region src/kinds/google-tasks.ts
const provider$3 = {
	id: "google-tasks",
	label: "Google Tasks"
};
const changeLabel$1 = (previous, next) => previous !== null && previous !== next ? `${previous} → ${next}` : next;
const isTaskId$1 = (value) => typeof value === "string" && value.length > 0;
const taskFromData = (data) => {
	if (!isRecord(data)) return void 0;
	const task = isRecord(data.task) ? data.task : data;
	return isRecord(task) && isTaskId$1(task.id) ? task : void 0;
};
const buildExternalRef$4 = (data) => {
	const task = taskFromData(data);
	if (!task) return void 0;
	const ref = {
		provider: "google_tasks",
		task_id: task.id
	};
	if (typeof task.webViewLink === "string" && task.webViewLink) ref.web_view_link = task.webViewLink;
	return ref;
};
const externalUrl$4 = (externalRef) => {
	const link = externalRef?.web_view_link;
	return typeof link === "string" ? link : void 0;
};
const nothingHappened = (verb) => (data) => taskFromData(data) ? void 0 : `Google Tasks did not ${verb} the task. Retry in a moment.`;
const getCreateFields = (content) => [
	editableField({
		key: "title",
		label: "Task",
		value: content.title,
		kind: "single",
		editor: "input"
	}),
	editableField({
		key: "notes",
		label: "Notes",
		value: content.notes ?? "",
		kind: "multiline",
		editor: "textarea",
		hideWhenEmpty: true,
		collapsible: true
	}),
	readonlyField({
		key: "tasklist",
		label: "List",
		value: content.tasklist_title ?? "",
		kind: "single",
		hideWhenEmpty: true,
		advanced: true
	}),
	editableField({
		key: "due",
		label: "Due",
		value: content.due ?? "",
		kind: "single",
		editor: "input",
		hideWhenEmpty: true
	})
];
const buildCreateArguments = (content) => {
	const args = {
		title: content.title,
		tasklist_id: content.tasklist_id
	};
	if (content.notes) args.notes = content.notes;
	if (content.due) args.due = content.due;
	if (content.task_parent) args.task_parent = content.task_parent;
	return args;
};
const googleTaskActionCardKind = {
	actionType: "google_task",
	provider: provider$3,
	capability: "confirmable",
	actionLabel: "Create task",
	confirmationLabel: "Create task",
	confirmationVerb: "Create",
	runningLabel: "Creating task…",
	completedLabel: "Created",
	decode: (content) => GoogleTaskContentSchema.safeParse(content).data ?? null,
	getFields: getCreateFields,
	getSummary: (content) => content.title,
	withUpdatedField: (content, fieldKey, value) => {
		if (fieldKey === "title") return {
			...content,
			title: value
		};
		if (fieldKey === "notes" || fieldKey === "due") return {
			...content,
			[fieldKey]: value || null
		};
		return content;
	},
	getCompletion: (url) => ({
		label: "Created",
		linkLabel: "Open in Google Tasks",
		url
	}),
	externalUrl: externalUrl$4,
	execution: {
		buildCalls: (content) => [{
			integrationName: "googletasks",
			toolSlug: "GOOGLETASKS_INSERT_TASK",
			arguments: buildCreateArguments(content),
			accountLabel: content.account_label
		}],
		buildExternalRef: buildExternalRef$4,
		externalUrl: externalUrl$4,
		resultError: nothingHappened("create"),
		validate: (content) => content.title.length === 0 ? "Add a task title before creating." : null
	}
};
const statusLabel = (status) => {
	if (status === "completed") return "Completed";
	if (status === "needsAction") return "Not completed";
	return "";
};
const updateChangeFields$1 = (content) => [
	...whenChanged(content.title, (value) => editableField({
		key: "title",
		label: "New title",
		value,
		kind: "single",
		editor: "input"
	})),
	...whenChanged(content.notes, (value) => editableField({
		key: "notes",
		label: "Notes",
		value,
		kind: "multiline",
		editor: "textarea"
	})),
	...whenChanged(content.due, (value) => detailField("due", "Due", changeLabel$1(content.previous_due, value))),
	...whenChanged(content.status, (value) => detailField("status", "Status", changeLabel$1(statusLabel(content.previous_status), statusLabel(value))))
];
const buildUpdateArguments$1 = (content) => {
	const args = {
		tasklist_id: content.tasklist_id,
		task_id: content.task_id
	};
	if (content.title !== null) args.title = content.title;
	if (content.notes !== null) args.notes = content.notes;
	if (content.due !== null) args.due = content.due;
	if (content.status !== null) args.status = content.status;
	return args;
};
const hasUpdateChange$1 = (content) => content.title !== null || content.notes !== null || content.due !== null || content.status !== null;
const googleTaskUpdateActionCardKind = {
	actionType: "google_task_update",
	provider: provider$3,
	capability: "confirmable",
	actionLabel: "Update task",
	confirmationLabel: "Update task",
	confirmationVerb: "Update",
	runningLabel: "Updating task…",
	completedLabel: "Updated",
	decode: (content) => GoogleTaskUpdateContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		targetField("Task", content.task_title),
		detailField("list", "List", content.tasklist_title ?? ""),
		...updateChangeFields$1(content)
	],
	getSummary: (content) => content.task_title,
	withUpdatedField: (content, fieldKey, value) => (fieldKey === "title" || fieldKey === "notes") && content[fieldKey] !== null ? {
		...content,
		[fieldKey]: value
	} : content,
	getCompletion: (url) => ({
		label: "Updated",
		linkLabel: "Open in Google Tasks",
		url
	}),
	externalUrl: externalUrl$4,
	execution: {
		buildCalls: (content) => [{
			integrationName: "googletasks",
			toolSlug: "GOOGLETASKS_PATCH_TASK",
			arguments: buildUpdateArguments$1(content),
			accountLabel: content.account_label
		}],
		buildExternalRef: buildExternalRef$4,
		externalUrl: externalUrl$4,
		resultError: nothingHappened("update"),
		validate: (content) => {
			if (content.task_id.length === 0 || content.tasklist_id.length === 0) return "Choose a task in chat before updating.";
			if (content.title !== null && content.title.trim().length === 0) return "Add a task title before updating.";
			return hasUpdateChange$1(content) ? null : "Specify a task change in chat before updating.";
		}
	}
};
const googleTaskDeleteActionCardKind = {
	actionType: "google_task_delete",
	provider: provider$3,
	capability: "confirmable",
	actionLabel: "Delete",
	confirmationLabel: "Delete",
	confirmationVerb: "Delete",
	runningLabel: "Deleting…",
	completedLabel: "Deleted",
	decode: (content) => GoogleTaskDeleteContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		targetField("Task", content.task_title),
		detailField("list", "List", content.tasklist_title ?? ""),
		detailField("due", "Due", content.due ?? "")
	],
	getSummary: (content) => content.task_title,
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: "Deleted" }),
	execution: {
		buildCalls: (content) => [{
			integrationName: "googletasks",
			toolSlug: "GOOGLETASKS_DELETE_TASK",
			arguments: {
				tasklist_id: content.tasklist_id,
				task_id: content.task_id
			},
			accountLabel: content.account_label
		}],
		buildExternalRef: () => void 0,
		externalUrl: () => void 0,
		resultError: () => void 0,
		validate: (content) => content.task_id.length === 0 || content.tasklist_id.length === 0 ? "Choose a task in chat before deleting." : null
	}
};
const buildMoveArguments = (content) => {
	const args = {
		tasklist: content.tasklist_id,
		task: content.task_id
	};
	if (content.parent !== null) args.parent = content.parent;
	if (content.previous !== null) args.previous = content.previous;
	if (content.destination_tasklist !== null) args.destinationTasklist = content.destination_tasklist;
	return args;
};
const moveChangeFields = (content) => [
	...whenChanged(content.destination_tasklist, (value) => detailField("destination", "Move to list", changeLabel$1(content.tasklist_title, content.destination_tasklist_title ?? value))),
	...whenChanged(content.parent, (value) => detailField("parent", "New parent task id", value, { advanced: true })),
	...whenChanged(content.previous, (value) => detailField("previous", "Position after task id", value, { advanced: true }))
];
const hasMoveChange = (content) => content.parent !== null || content.previous !== null || content.destination_tasklist !== null;
const googleTaskMoveActionCardKind = {
	actionType: "google_task_move",
	provider: provider$3,
	capability: "confirmable",
	actionLabel: "Move task",
	confirmationLabel: "Move task",
	confirmationVerb: "Move",
	runningLabel: "Moving task…",
	completedLabel: "Moved",
	decode: (content) => GoogleTaskMoveContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		targetField("Task", content.task_title),
		detailField("list", "List", content.tasklist_title ?? ""),
		...moveChangeFields(content)
	],
	getSummary: (content) => content.task_title,
	withUpdatedField: (content) => content,
	getCompletion: (url) => ({
		label: "Moved",
		linkLabel: "Open in Google Tasks",
		url
	}),
	externalUrl: externalUrl$4,
	execution: {
		buildCalls: (content) => [{
			integrationName: "googletasks",
			toolSlug: "GOOGLETASKS_MOVE_TASK",
			arguments: buildMoveArguments(content),
			accountLabel: content.account_label
		}],
		buildExternalRef: buildExternalRef$4,
		externalUrl: externalUrl$4,
		resultError: nothingHappened("move"),
		validate: (content) => {
			if (content.task_id.length === 0 || content.tasklist_id.length === 0) return "Choose a task in chat before moving.";
			return hasMoveChange(content) ? null : "Specify where to move the task in chat before moving.";
		}
	}
};
//#endregion
//#region src/kinds/hubspot.ts
const provider$2 = {
	id: "hubspot",
	label: "HubSpot"
};
const hubspotUrl = (value) => {
	if (typeof value !== "string") return void 0;
	try {
		const url = new URL(value);
		return url.protocol === "https:" && url.hostname.endsWith(".hubspot.com") ? value : void 0;
	} catch {
		return;
	}
};
const parseBody = (data) => {
	let body = data;
	if (isRecord(data) && typeof data.text === "string") try {
		body = JSON.parse(data.text);
	} catch {
		return;
	}
	return isRecord(body) ? body : void 0;
};
const requestResults = (data) => {
	const body = parseBody(data);
	const results = body?.createResults || body?.updateResults;
	return isRecord(results) ? results : void 0;
};
const buildExternalRef$3 = (data) => {
	const results = requestResults(data)?.results;
	const saved = Array.isArray(results) ? results[0] : void 0;
	const url = isRecord(saved) && isRecord(saved.object) ? hubspotUrl(saved.object.url) : void 0;
	return url ? {
		provider: "hubspot",
		url
	} : void 0;
};
const resultError = (data) => {
	const summary = requestResults(data)?.summary;
	const failed = isRecord(summary) ? summary.failed : void 0;
	return typeof failed === "number" && failed > 0 ? "HubSpot did not save this change." : void 0;
};
const externalUrl$3 = (ref) => hubspotUrl(ref?.url);
const manageCall = (request, accountLabel) => [{
	integrationName: "mcp:hubspot",
	toolSlug: "mcp_hubspot__manage_crm_objects",
	accountLabel,
	arguments: {
		...request,
		confirmationStatus: "CONFIRMED"
	}
}];
const contactTargets = (contactIds) => contactIds.map((contactId) => ["contacts", contactId]);
const createCall = (objectType, properties, targets, accountLabel) => {
	const object = {
		objectType,
		properties
	};
	if (targets.length > 0) object.associations = targets.map(([targetObjectType, targetObjectId]) => ({
		targetObjectId,
		targetObjectType
	}));
	return manageCall({ createRequest: { objects: [object] } }, accountLabel);
};
const textOf = (content, key) => {
	const value = content[key];
	return typeof value === "string" ? value : null;
};
const setValues = (content, properties) => {
	const values = {};
	for (const property of properties) {
		const value = textOf(content, property);
		if (value !== null) values[property] = value;
	}
	return values;
};
const asIs = (value) => value;
const byName = (property) => (value, labels) => labels[property]?.[value] ?? value;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const asTime = (value) => {
	const dateOnly = DATE_ONLY.test(value);
	const instant = new Date(dateOnly ? `${value}T00:00:00Z` : /^\d+$/.test(value) ? Number(value) : value);
	if (Number.isNaN(instant.getTime())) return value;
	return instant.toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		...dateOnly ? { timeZone: "UTC" } : {
			hour: "numeric",
			minute: "2-digit",
			timeZoneName: "short"
		}
	});
};
const detailRows = (content, rows, labels = {}) => rows.flatMap(([property, label, show = asIs, key = property]) => whenChanged(textOf(content, property), (value) => detailField(key, label, show(value, labels))));
const changeRows = (content, rows, labels) => rows.flatMap(([property, label, show = asIs, key = property]) => whenChanged(textOf(content, property), (value) => {
	const previous = textOf(content, `previous_${property}`);
	const next = value === "" ? "Cleared" : show(value, labels);
	const before = previous && previous !== value && show(previous, labels);
	return detailField(key, label, before ? `${before} → ${next}` : next);
}));
const contactName = (content) => content.contact_name ?? content.contact_email ?? `Contact ${content.contact_id}`;
const contactValue = (content) => [content.contact_name, content.contact_email].filter(Boolean).join(" · ") || `Contact ${content.contact_id}`;
const recordField = (key, label, value, url) => readonlyField({
	key,
	label,
	value,
	kind: "single",
	href: hubspotUrl(url)
});
const contactField = (content) => recordField("contact", "Contact", contactValue(content), content.contact_url);
const inputField = (key, label, value) => editableField({
	key,
	label,
	value,
	kind: "single",
	editor: "input"
});
const getCompletion$1 = (label) => (url) => ({
	label,
	linkLabel: "View in HubSpot",
	url
});
const VERB_FORMS = {
	Add: ["Adding", "Added"],
	Create: ["Creating", "Created"],
	Update: ["Updating", "Updated"],
	Log: ["Logging", "Logged"],
	Complete: ["Completing", "Completed"],
	Reopen: ["Reopening", "Reopened"]
};
const buttonLabels = (verb, noun) => {
	const [running, done] = VERB_FORMS[verb];
	return {
		confirmationLabel: `${verb} ${noun}`,
		confirmationVerb: verb,
		runningLabel: `${running} ${noun}…`,
		completedLabel: done,
		getCompletion: getCompletion$1(done)
	};
};
const cardBasics = (verb, noun) => ({
	provider: provider$2,
	capability: "confirmable",
	actionLabel: `${verb} ${noun}`,
	...buttonLabels(verb, noun),
	externalUrl: externalUrl$3
});
const results = {
	buildExternalRef: buildExternalRef$3,
	externalUrl: externalUrl$3,
	resultError
};
const ACTIVITIES = {
	note: {
		objectType: "notes",
		label: "Note",
		bodyLabel: "Note",
		labels: buttonLabels("Add", "note"),
		properties: {
			body: "hs_note_body",
			timestamp: "hs_timestamp"
		}
	},
	call: {
		objectType: "calls",
		label: "Call",
		bodyLabel: "Call notes",
		labels: buttonLabels("Log", "call"),
		properties: {
			body: "hs_call_body",
			title: "hs_call_title",
			timestamp: "hs_timestamp",
			call_outcome: "hs_call_disposition"
		}
	},
	meeting: {
		objectType: "meetings",
		label: "Meeting",
		bodyLabel: "Meeting notes",
		labels: buttonLabels("Log", "meeting"),
		properties: {
			body: "hs_meeting_body",
			title: "hs_meeting_title",
			timestamp: "hs_timestamp",
			start_time: "hs_meeting_start_time",
			end_time: "hs_meeting_end_time"
		}
	},
	email: {
		objectType: "emails",
		label: "Email",
		bodyLabel: "Email text",
		labels: buttonLabels("Log", "email"),
		properties: {
			body: "hs_email_text",
			title: "hs_email_subject",
			timestamp: "hs_timestamp",
			email_direction: "hs_email_direction"
		}
	}
};
const activityProperties = (content) => {
	const properties = {};
	for (const [field, property] of Object.entries(ACTIVITIES[content.activity_type].properties)) {
		const value = textOf(content, field);
		if (value !== null) properties[property] = value;
	}
	return properties;
};
const hubspotContactNoteActionCardKind = {
	actionType: "hubspot_contact_note",
	...cardBasics("Add", "note"),
	getActionLabel: (content) => ACTIVITIES[content.activity_type].labels.confirmationLabel,
	getLabels: (content) => ACTIVITIES[content.activity_type].labels,
	decode: (content) => HubSpotContactNoteContentSchema.safeParse(content).data ?? null,
	getFields: (content) => {
		const activity = ACTIVITIES[content.activity_type];
		const isEmail = content.activity_type === "email";
		return [
			targetField("Contact", contactValue(content), hubspotUrl(content.contact_url)),
			...content.activity_type === "note" ? [] : [detailField("activity_type", "Type", activity.label)],
			...detailRows(content, [
				[
					"title",
					isEmail ? "Subject" : "Title",
					asIs,
					isEmail ? "subject" : "title"
				],
				[
					"timestamp",
					"When",
					asTime,
					"when"
				],
				[
					"start_time",
					"Starts",
					asTime
				],
				[
					"end_time",
					"Ends",
					asTime
				],
				[
					"call_outcome",
					"Outcome",
					byName("call_outcome")
				],
				[
					"email_direction",
					"Direction",
					byName("email_direction")
				]
			], content.value_labels),
			editableField({
				key: "body",
				label: activity.bodyLabel,
				value: content.body,
				kind: "multiline",
				editor: "textarea"
			})
		];
	},
	getSummary: (content) => content.activity_type === "note" ? `Note on ${contactName(content)}` : `${ACTIVITIES[content.activity_type].label} with ${contactName(content)}`,
	withUpdatedField: (content, key, value) => key === "body" ? {
		...content,
		body: value
	} : content,
	execution: {
		...results,
		buildCalls: (content) => createCall(ACTIVITIES[content.activity_type].objectType, activityProperties(content), contactTargets([content.contact_id]), content.account_label),
		validate: (content) => {
			if (content.activity_type === "note") return content.body.trim() ? null : "Write a note before adding.";
			return content.body.trim() || content.title?.trim() ? null : "Add a title or notes before logging.";
		}
	}
};
const updateKind = (config) => {
	const properties = config.rows.map(([property]) => property);
	return {
		actionType: config.actionType,
		...cardBasics("Update", config.noun),
		decode: config.decode,
		getFields: (content) => [config.target(content), ...changeRows(content, config.rows, content.value_labels)],
		getSummary: config.summary,
		withUpdatedField: (content) => content,
		execution: {
			...results,
			buildCalls: (content) => manageCall({ updateRequest: { objects: [{
				objectType: config.objectType,
				objectId: config.objectId(content),
				properties: setValues(content, properties)
			}] } }, content.account_label),
			validate: (content) => Object.keys(setValues(content, properties)).length > 0 ? null : `Specify a ${config.noun} change in chat before updating.`
		}
	};
};
const OWNER_ROW = [
	"hubspot_owner_id",
	"Owner",
	byName("hubspot_owner_id")
];
const hubspotContactUpdateActionCardKind = updateKind({
	actionType: "hubspot_contact_update",
	noun: "contact",
	objectType: "contacts",
	rows: [
		[
			"lifecyclestage",
			"Lifecycle stage",
			byName("lifecyclestage")
		],
		[
			"hs_lead_status",
			"Lead status",
			byName("hs_lead_status")
		],
		["firstname", "First name"],
		["lastname", "Last name"],
		["phone", "Phone"],
		["jobtitle", "Job title"],
		OWNER_ROW
	],
	decode: (content) => HubSpotContactUpdateContentSchema.safeParse(content).data ?? null,
	objectId: (content) => content.contact_id,
	target: contactField,
	summary: contactName
});
const DEAL_ROWS = [
	[
		"dealstage",
		"Deal stage",
		byName("dealstage")
	],
	["amount", "Amount"],
	[
		"closedate",
		"Close date",
		asTime
	],
	[
		"pipeline",
		"Pipeline",
		byName("pipeline")
	],
	OWNER_ROW
];
const hubspotDealActionCardKind = {
	actionType: "hubspot_deal",
	...cardBasics("Create", "deal"),
	decode: (content) => HubSpotDealContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		contactField(content),
		inputField("dealname", "Deal name", content.dealname),
		...detailRows(content, DEAL_ROWS, content.value_labels)
	],
	getSummary: (content) => content.dealname,
	withUpdatedField: (content, key, value) => key === "dealname" ? {
		...content,
		dealname: value
	} : content,
	execution: {
		...results,
		buildCalls: (content) => createCall("deals", setValues(content, ["dealname", ...DEAL_ROWS.map(([property]) => property)]), contactTargets([content.contact_id]), content.account_label),
		validate: (content) => content.dealname.trim() ? null : "Add a deal name before creating."
	}
};
const companyContacts = (content) => content.contact_labels.length === content.contact_ids.length ? content.contact_labels : content.contact_ids.map((contactId) => `Contact ${contactId}`);
const hubspotCompanyActionCardKind = {
	actionType: "hubspot_company",
	...cardBasics("Create", "company"),
	decode: (content) => HubSpotCompanyContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		inputField("name", "Company name", content.name),
		...whenChanged(content.domain, (value) => inputField("domain", "Domain", value)),
		detailField("contacts", "Contacts", companyContacts(content).join(", ")),
		...whenChanged(content.possible_duplicate, (value) => detailField("possible_duplicate", "Possible duplicate", value))
	],
	getSummary: (content) => content.name,
	withUpdatedField: (content, key, value) => key === "name" || key === "domain" ? {
		...content,
		[key]: value
	} : content,
	execution: {
		...results,
		buildCalls: (content) => createCall("companies", setValues(content, ["name", "domain"]), contactTargets(content.contact_ids), content.account_label),
		validate: (content) => content.name.trim() ? null : "Add a company name before creating."
	}
};
const CONTACT_CREATE_INPUTS = [
	["email", "Email"],
	["firstname", "First name"],
	["lastname", "Last name"],
	["company", "Company name"]
];
const hubspotContactCreateActionCardKind = {
	actionType: "hubspot_contact_create",
	...cardBasics("Create", "contact"),
	decode: (content) => HubSpotContactCreateContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		...CONTACT_CREATE_INPUTS.map(([key, label]) => editableField({
			key,
			label,
			value: content[key] ?? "",
			kind: "single",
			editor: "input",
			hideWhenEmpty: true
		})),
		...whenChanged(content.company_id, (companyId) => detailField("company_id", "Company record", content.company_name ?? `Company ${companyId}`)),
		...whenChanged(content.possible_duplicate, (value) => detailField("possible_duplicate", "Possible duplicate", value))
	],
	getSummary: (content) => [content.firstname, content.lastname].filter(Boolean).join(" ") || content.email || "New contact",
	withUpdatedField: (content, key, value) => CONTACT_CREATE_INPUTS.some(([inputKey]) => inputKey === key) ? {
		...content,
		[key]: value
	} : content,
	execution: {
		...results,
		buildCalls: (content) => createCall("contacts", setValues(content, CONTACT_CREATE_INPUTS.map(([key]) => key)), content.company_id === null ? [] : [["companies", content.company_id]], content.account_label),
		validate: (content) => [
			content.email,
			content.firstname,
			content.lastname
		].some((value) => value?.trim()) ? null : "Add an email or name before creating."
	}
};
const hubspotDealUpdateActionCardKind = updateKind({
	actionType: "hubspot_deal_update",
	noun: "deal",
	objectType: "deals",
	rows: [["dealname", "Deal name"], ...DEAL_ROWS],
	decode: (content) => HubSpotDealUpdateContentSchema.safeParse(content).data ?? null,
	objectId: (content) => content.deal_id,
	target: (content) => recordField("deal", "Deal", content.deal_name ?? `Deal ${content.deal_id}`, content.deal_url),
	summary: (content) => content.deal_name ?? `Deal ${content.deal_id}`
});
const TASK_TARGETS = {
	contacts: {
		key: "contact",
		label: "Contact"
	},
	deals: {
		key: "deal",
		label: "Deal"
	}
};
const TASK_PROPERTIES = [
	"hs_task_subject",
	"hs_task_body",
	"hs_timestamp",
	"hubspot_owner_id",
	"hs_task_priority"
];
const hubspotTaskActionCardKind = {
	actionType: "hubspot_task",
	...cardBasics("Create", "task"),
	decode: (content) => HubSpotTaskContentSchema.safeParse(content).data ?? null,
	getFields: (content) => {
		const target = TASK_TARGETS[content.target_type];
		return [
			recordField(target.key, target.label, content.target_name ?? `${target.label} ${content.target_id}`, content.target_url),
			inputField("hs_task_subject", "Task", content.hs_task_subject),
			...detailRows(content, [
				[
					"hs_timestamp",
					"Due",
					asTime,
					"due"
				],
				[
					"hs_task_priority",
					"Priority",
					byName("hs_task_priority"),
					"priority"
				],
				OWNER_ROW
			], content.value_labels),
			editableField({
				key: "hs_task_body",
				label: "Notes",
				value: content.hs_task_body ?? "",
				kind: "multiline",
				editor: "textarea",
				hideWhenEmpty: true
			})
		];
	},
	getSummary: (content) => content.hs_task_subject,
	withUpdatedField: (content, key, value) => key === "hs_task_subject" || key === "hs_task_body" ? {
		...content,
		[key]: value
	} : content,
	execution: {
		...results,
		buildCalls: (content) => createCall("tasks", setValues(content, TASK_PROPERTIES), [[content.target_type, content.target_id]], content.account_label),
		validate: (content) => content.hs_task_subject.trim() ? null : "Add a task title before creating."
	}
};
const TASK_STATUS_LABELS = {
	COMPLETED: buttonLabels("Complete", "task"),
	NOT_STARTED: buttonLabels("Reopen", "task")
};
const taskUpdateLabels = (content) => content.hs_task_status === null ? buttonLabels("Update", "task") : TASK_STATUS_LABELS[content.hs_task_status];
const hubspotTaskUpdateActionCardKind = {
	...updateKind({
		actionType: "hubspot_task_update",
		noun: "task",
		objectType: "tasks",
		rows: [
			[
				"hs_task_status",
				"Status",
				byName("hs_task_status")
			],
			[
				"hs_timestamp",
				"Due",
				asTime,
				"due"
			],
			OWNER_ROW
		],
		decode: (content) => HubSpotTaskUpdateContentSchema.safeParse(content).data ?? null,
		objectId: (content) => content.task_id,
		target: (content) => recordField("task", "Task", content.task_subject ?? `Task ${content.task_id}`, content.task_url),
		summary: (content) => content.task_subject ?? `Task ${content.task_id}`
	}),
	getActionLabel: (content) => taskUpdateLabels(content).confirmationLabel,
	getLabels: taskUpdateLabels
};
//#endregion
//#region src/source-link.ts
const URL_IN_TEXT = /https:\/\/[^\s<>"'`]+/gi;
const isSlackPermalink = (href) => {
	try {
		const { hostname, pathname } = new URL(href);
		const host = hostname.replace(/^www\./, "");
		return (host === "slack.com" || host.endsWith(".slack.com")) && /\/archives\/[CDGUW][A-Z0-9]+(?:\/p\d+)?/i.test(pathname);
	} catch {
		return false;
	}
};
const isGmailPermalink = (href) => {
	try {
		const { hostname, pathname } = new URL(href);
		return hostname.replace(/^www\./, "") === "mail.google.com" && pathname.startsWith("/mail/");
	} catch {
		return false;
	}
};
const urlsIn = (text) => {
	return (text.match(URL_IN_TEXT) ?? []).map((raw) => raw.replace(/[),.;!?]+$/g, ""));
};
const classifiedSourceLink = (href) => {
	if (!href) return void 0;
	if (isSlackPermalink(href)) return {
		href,
		label: "Open in Slack"
	};
	if (isGmailPermalink(href)) return {
		href,
		label: "Open email"
	};
};
const SITE_NAMES = {
	"github.com": "GitHub",
	"linear.app": "Linear"
};
const anyWebSourceLink = (href) => {
	const classified = classifiedSourceLink(href);
	if (classified || !href) return classified;
	try {
		const { protocol, hostname } = new URL(href);
		if (protocol !== "https:" && protocol !== "http:") return void 0;
		const site = SITE_NAMES[hostname.replace(/^www\./, "")];
		return {
			href,
			label: site ? `Open in ${site}` : "Open link"
		};
	} catch {
		return;
	}
};
const classifiedSlackMessagePermalink = (href) => {
	const link = classifiedSourceLink(href);
	if (!link) return void 0;
	try {
		return /\/archives\/[CDGUW][A-Z0-9]+\/p\d+/i.test(new URL(link.href).pathname) ? link : void 0;
	} catch {
		return;
	}
};
const slackArchivesUrl = (hostHref, channelId, messageTs) => {
	try {
		const { origin, hostname } = new URL(hostHref);
		const host = hostname.replace(/^www\./, "");
		if (!(host === "slack.com" || host.endsWith(".slack.com"))) return void 0;
		if (!/^[CDGUW][A-Z0-9]+$/i.test(channelId)) return void 0;
		if (!messageTs) return `${origin}/archives/${channelId}`;
		if (!/^\d+\.\d+$/.test(messageTs)) return void 0;
		return `${origin}/archives/${channelId}/p${messageTs.replaceAll(".", "")}`;
	} catch {
		return;
	}
};
const sourceLinkFromText = (text) => {
	if (!text) return void 0;
	const urls = urlsIn(text);
	const slack = urls.find(isSlackPermalink);
	if (slack) return classifiedSourceLink(slack);
	const gmail = urls.find(isGmailPermalink);
	return gmail ? classifiedSourceLink(gmail) : void 0;
};
const contextWithoutSourceHref = (context, href) => {
	if (context === null || context === void 0) return void 0;
	if (!href) return context;
	const stripped = context.replaceAll(href, "").replace(/[ \t]{2,}/g, " ").trim();
	return stripped.length > 0 ? stripped : void 0;
};
//#endregion
//#region src/kinds/informational.ts
const headingsAsBold = (markdown) => {
	let activeFence;
	return markdown.split("\n").map((line) => {
		const delimiter = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);
		if (delimiter) {
			if (!activeFence) activeFence = delimiter[1];
			else if (delimiter[1][0] === activeFence[0] && delimiter[1].length >= activeFence.length && !delimiter[2].trim()) activeFence = void 0;
			return line;
		}
		if (activeFence) return line;
		return line.replace(/^ {0,3}#{1,6}[ \t]+(.+?)(?:[ \t]+#+)?[ \t]*$/, (_line, text) => `**${text.replaceAll("**", "")}**`);
	}).join("\n");
};
const getFields$5 = (content) => {
	const fields = [];
	if (content.body !== content.title) fields.push(readonlyField({
		key: "body",
		label: "Details",
		value: headingsAsBold(content.body),
		kind: "multiline",
		format: "markdown"
	}));
	if (!anyWebSourceLink(content.url)) fields.push(readonlyField({
		key: "url",
		label: "Link",
		value: content.url ?? "",
		href: content.url ?? void 0,
		kind: "single",
		hideWhenEmpty: true
	}));
	return fields;
};
const informationalActionCardKind = {
	actionType: "informational",
	provider: {
		id: "Littlebird",
		label: "Littlebird"
	},
	capability: "display-only",
	actionLabel: "For you",
	draftLabel: "For you",
	confirmationLabel: "Chat",
	confirmationVerb: "Chat",
	runningLabel: "Asking…",
	completedLabel: "Read",
	decode: (content) => InformationalContentSchema.safeParse(content).data ?? null,
	getActionLabel: (content) => content.title,
	getSummary: (content) => content.title,
	getFields: getFields$5,
	withUpdatedField: (content) => content,
	getCompletion: (_externalUrl, result) => ({ label: result?.acknowledged === true ? "Read" : "Completed" })
};
//#endregion
//#region src/kinds/linear.ts
const provider$1 = {
	id: "linear",
	label: "Linear"
};
const externalUrl$2 = (ref) => providerResultUrl(ref, "linear.app");
const buildExternalRef$2 = (data) => {
	const url = providerResultUrl(data, "linear.app");
	return url ? {
		provider: "linear",
		url
	} : void 0;
};
const issueAttachments = (content) => (content.attachments ?? []).map((attachment) => ({
	url: attachment.url,
	title: attachment.title,
	fallbackUrl: attachment.fallback_url
}));
const issueTargetField = (content) => targetField("Issue", content.issue_label ?? content.issue_id, providerResultUrl({ url: content.issue_url }, "linear.app"));
const issueSummary = (content) => {
	if (content.issue_title?.trim()) return content.issue_title.trim();
	const title = (content.issue_label?.trim())?.replace(/^(?:[A-Za-z][A-Za-z0-9]*-\d+|[a-f0-9-]{36})(?:: | · )/, "");
	if (title && !/^[a-f0-9-]{36}$/i.test(title)) return title;
	return /^[A-Za-z][A-Za-z0-9]*-\d+$/.test(content.issue_id) ? content.issue_id : "Issue";
};
const PRIORITY_LABELS = [
	"None",
	"Urgent",
	"High",
	"Medium",
	"Low"
];
const assignmentFields = (content) => {
	const fields = [];
	if (content.state !== null) {
		const status = content.state_name ?? content.state;
		const previousStatus = content.type === "linear_issue_update" ? content.previous_status : null;
		fields.push(detailField("state", "Status", previousStatus && previousStatus !== status ? `${previousStatus} → ${status}` : status));
	}
	if (content.assignee !== null || "assignee_set" in content && content.assignee_set) fields.push(detailField("assignee", "Assignee", content.assignee_name ?? content.assignee ?? "Unassigned"));
	if (content.priority !== null) {
		const priority = PRIORITY_LABELS[content.priority];
		const previousPriority = content.type === "linear_issue_update" ? content.previous_priority : null;
		fields.push(detailField("priority", "Priority", typeof previousPriority === "number" && previousPriority !== content.priority ? `${PRIORITY_LABELS[previousPriority]} → ${priority}` : priority));
	}
	return fields;
};
const planningFields = (content) => {
	const fields = [];
	if (content.project) fields.push(detailField("project", "Project", content.project_name ?? content.project));
	if (content.due_date) fields.push(detailField("due_date", "Due date", content.due_date));
	const labels = content.labels ?? [];
	if (labels.length) {
		const names = content.label_names?.length === labels.length ? content.label_names : labels;
		fields.push(detailField("labels", "Labels", names.join(", ")));
	}
	return fields;
};
const linearIssueActionCardKind = {
	actionType: "linear_issue",
	provider: provider$1,
	capability: "confirmable",
	actionLabel: "Create issue",
	getActionLabel: (content) => content.parent_id ? "Create sub-issue" : "Create issue",
	confirmationLabel: "Create issue",
	confirmationVerb: "Create",
	runningLabel: "Creating issue…",
	completedLabel: "Created",
	decode: (content) => LinearIssueContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		targetField("Team", content.team_name ?? content.team),
		...content.parent_id ? [detailField("parent", "Parent issue", content.parent_label ?? content.parent_id)] : [],
		titleField(content.title),
		bodyField("description", "Description", content.description),
		...assignmentFields(content),
		...planningFields(content)
	],
	getAttachments: issueAttachments,
	getSummary: (content) => content.title,
	withUpdatedField: (content, key, value) => key === "title" || key === "description" ? {
		...content,
		[key]: value
	} : content,
	getCompletion: (url) => ({
		label: "Created",
		linkLabel: "View issue",
		url
	}),
	externalUrl: externalUrl$2,
	execution: {
		buildCalls: (content) => {
			const args = {
				team: content.team,
				title: content.title,
				description: content.description
			};
			if (content.parent_id) args.parentId = content.parent_id;
			if (content.state !== null) args.state = content.state;
			if (content.assignee !== null) args.assignee = content.assignee;
			if (content.priority !== null) args.priority = content.priority;
			if (content.project) args.project = content.project;
			if (content.due_date) args.dueDate = content.due_date;
			if (content.labels?.length) args.labels = content.labels;
			return [{
				integrationName: "mcp:linear",
				toolSlug: "mcp_linear__save_issue",
				accountLabel: content.account_label,
				arguments: args
			}];
		},
		buildExternalRef: buildExternalRef$2,
		externalUrl: externalUrl$2,
		validate: (content) => !content.title.trim() ? "Add an issue title before creating." : !content.team.trim() ? "Choose a team in chat before creating." : null
	}
};
const linearIssueUpdateActionCardKind = {
	actionType: "linear_issue_update",
	provider: provider$1,
	capability: "confirmable",
	actionLabel: "Update issue",
	confirmationLabel: "Update issue",
	confirmationVerb: "Update",
	runningLabel: "Updating issue…",
	completedLabel: "Updated",
	decode: (content) => LinearIssueUpdateContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		issueTargetField(content),
		...content.title === null ? [] : [titleField(content.title)],
		...content.description === null ? [] : [bodyField("description", content.description === "" ? "Clear description" : "Description", content.description)],
		...assignmentFields(content)
	],
	getAttachments: issueAttachments,
	getSummary: issueSummary,
	withUpdatedField: (content, key, value) => (key === "title" || key === "description") && content[key] !== null ? {
		...content,
		[key]: value
	} : content,
	getCompletion: (url) => ({
		label: "Updated",
		linkLabel: "View issue",
		url
	}),
	externalUrl: externalUrl$2,
	execution: {
		buildCalls: (content) => {
			const args = { id: content.issue_id };
			if (content.title !== null) args.title = content.title;
			if (content.description !== null) args.description = content.description;
			if (content.state !== null) args.state = content.state;
			if (content.assignee_set) args.assignee = content.assignee;
			if (content.priority !== null) args.priority = content.priority;
			return [{
				integrationName: "mcp:linear",
				toolSlug: "mcp_linear__save_issue",
				accountLabel: content.account_label,
				arguments: args
			}];
		},
		buildExternalRef: buildExternalRef$2,
		externalUrl: externalUrl$2,
		validate: (content) => {
			if (!content.issue_id.trim()) return "Choose an issue in chat before updating.";
			if (content.title !== null && !content.title.trim()) return "Add an issue title before updating.";
			if (content.title === null && content.description === null && content.state === null && !content.assignee_set && content.priority === null) return "Specify an issue change in chat before updating.";
			return null;
		}
	}
};
const linearCommentActionCardKind = {
	actionType: "linear_comment",
	provider: provider$1,
	capability: "confirmable",
	actionLabel: "Post comment",
	confirmationLabel: "Post comment",
	confirmationVerb: "Post",
	runningLabel: "Posting comment…",
	completedLabel: "Posted",
	decode: (content) => LinearCommentContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [issueTargetField(content), bodyField("body", "Comment", content.body)],
	getAttachments: issueAttachments,
	getSummary: issueSummary,
	withUpdatedField: (content, key, value) => key === "body" ? {
		...content,
		body: value
	} : content,
	getCompletion: (url) => ({
		label: "Posted",
		linkLabel: "View comment",
		url
	}),
	externalUrl: externalUrl$2,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:linear",
			toolSlug: "mcp_linear__save_comment",
			accountLabel: content.account_label,
			arguments: {
				issueId: content.issue_id,
				body: content.body
			}
		}],
		buildExternalRef: buildExternalRef$2,
		externalUrl: externalUrl$2,
		validate: (content) => !content.body.trim() ? "Write a comment before posting." : !content.issue_id.trim() ? "Choose an issue in chat before posting." : null
	}
};
//#endregion
//#region src/kinds/microsoft-teams.ts
const EMPTY_MESSAGE = "Write your message before sending.";
const sentMessageUrl = (externalRef) => {
	const url = externalRef?.web_url ?? externalRef?.webUrl;
	return typeof url === "string" && url.length > 0 ? url : void 0;
};
const microsoftTeamsMessageActionCardKind = {
	actionType: "microsoft_teams_message",
	provider: {
		id: "microsoft-teams",
		label: "Microsoft Teams"
	},
	capability: "confirmable",
	actionLabel: "Send message",
	confirmationLabel: "Send",
	confirmationVerb: "Send",
	runningLabel: "Sending…",
	completedLabel: "Sent",
	decode: (content) => MicrosoftTeamsMessageContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		readonlyField({
			key: "target_label",
			label: "To",
			value: content.target_label ?? "",
			kind: "single",
			hideWhenEmpty: true
		}),
		readonlyField({
			key: "parent_preview",
			label: "Replying to",
			value: content.parent_preview ?? "",
			kind: "multiline",
			hideWhenEmpty: true
		}),
		editableField({
			key: "content",
			label: "Message",
			value: content.content,
			kind: "multiline",
			editor: "textarea",
			format: "text"
		})
	],
	getSummary: (content) => content.content.trim() || "Teams message",
	withUpdatedField: (content, fieldKey, value) => fieldKey === "content" ? {
		...content,
		content: value
	} : content,
	getCompletion: (url) => ({
		label: "Sent",
		linkLabel: "View in Teams",
		url
	}),
	externalUrl: sentMessageUrl,
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: (content) => content.content.trim() ? null : EMPTY_MESSAGE
	}
};
//#endregion
//#region src/kinds/outlook.ts
const outlookActionCardKind = {
	actionType: "outlook_email_draft",
	provider: {
		id: "outlook",
		label: "Outlook"
	},
	capability: "confirmable",
	actionLabel: "Send new email",
	confirmationLabel: "Send",
	confirmationVerb: "Send",
	runningLabel: "Sending…",
	completedLabel: "Sent",
	decode: (content) => OutlookEmailDraftPreviewContentSchema.safeParse(content).data ?? null,
	getFields: emailFields,
	getSummary: (content) => content.subject,
	withUpdatedField: updateEmailField,
	getCompletion: () => ({ label: "Sent" }),
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: (content) => {
			if (content.to.length === 0) return "Add at least one recipient before sending.";
			if (!content.subject.trim()) return "Add a subject before sending.";
			return content.body.trim().length === 0 ? "Write your email before sending." : null;
		}
	}
};
//#endregion
//#region src/kinds/question.ts
const getFields$4 = (content) => [
	readonlyField({
		key: "prompt",
		label: "Question",
		value: content.prompt,
		kind: "multiline"
	}),
	editableField({
		key: "selected",
		label: content.allow_multiple ? "Pick any that apply" : "Pick an answer",
		value: content.selected.join(", "),
		kind: "single",
		editor: "select",
		options: content.options,
		multiple: content.allow_multiple,
		selectedValues: content.selected
	}),
	...content.allow_free_text ? [editableField({
		key: "free_text",
		label: ASK_USER_FREE_TEXT_PLACEHOLDER,
		value: content.free_text,
		kind: "multiline",
		editor: "textarea",
		placeholder: ASK_USER_FREE_TEXT_PLACEHOLDER
	})] : []
];
const withAnswer = (content, answer) => ({
	...content,
	selected: [...answer.selected],
	free_text: answer.freeText
});
const answerOf = (content) => ({
	selected: content.selected,
	freeText: content.free_text
});
const questionActionCardKind = {
	actionType: "question",
	provider: {
		id: "Littlebird",
		label: "Littlebird"
	},
	capability: "confirmable",
	actionLabel: "Question",
	confirmationLabel: "Send",
	dismissLabel: "Skip",
	dismissedLabel: "Skipped",
	confirmationVerb: "Send",
	runningLabel: "Sending…",
	completedLabel: "Sent",
	decode: (content) => QuestionContentSchema.safeParse(content).data ?? null,
	getFields: getFields$4,
	getSummary: (content) => content.prompt,
	withUpdatedField: (content, fieldKey, value) => {
		if (fieldKey === "selected") return withAnswer(content, withAskUserPick(answerOf(content), value, content.allow_multiple));
		if (fieldKey === "free_text" && content.allow_free_text) return withAnswer(content, withAskUserFreeText(answerOf(content), value, content.allow_multiple));
		return content;
	},
	getCompletion: () => ({ label: "Answer sent" }),
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: (content) => {
			if (content.selected.length > 0 || content.free_text.trim()) return null;
			return content.allow_free_text ? "Pick an answer or write one first" : "Pick an answer first";
		}
	}
};
//#endregion
//#region src/kinds/routine-offer.ts
const ROUTINE_OFFER_PROMPT_MAX_LENGTH = 4e3;
const getFields$3 = (content) => [
	readonlyField({
		key: "description",
		label: "What it does",
		value: content.description,
		kind: "multiline"
	}),
	readonlyField({
		key: "runs_label",
		label: "Runs",
		value: content.runs_label,
		kind: "single"
	}),
	editableField({
		key: "prompt",
		label: "Instructions",
		value: content.prompt,
		kind: "multiline",
		editor: "textarea"
	})
];
const routineOfferActionCardKind = {
	actionType: "routine_offer",
	provider: {
		id: "Littlebird",
		label: "Littlebird"
	},
	capability: "confirmable",
	actionLabel: "Routine offer",
	getActionLabel: (content) => content.title,
	confirmationLabel: "Add routine",
	runsOnlyOpen: true,
	confirmationVerb: "Add",
	runningLabel: "Adding…",
	completedLabel: "Routine added",
	decode: (content) => RoutineOfferContentSchema.safeParse(content).data ?? null,
	getFields: getFields$3,
	getSummary: (content) => content.title,
	withUpdatedField: (content, fieldKey, value) => fieldKey === "prompt" ? {
		...content,
		prompt: value
	} : content,
	getCompletion: () => ({ label: "Routine added" }),
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: (content) => {
			const prompt = content.prompt.trim();
			if (prompt.length === 0) return "Write the routine's instructions";
			return prompt.length > ROUTINE_OFFER_PROMPT_MAX_LENGTH ? `Keep the instructions to ${ROUTINE_OFFER_PROMPT_MAX_LENGTH.toLocaleString("en-US")} characters or fewer.` : null;
		}
	}
};
//#endregion
//#region src/kinds/routine-reach-out.ts
const ANSWERED = "Answered";
const routineReachOutActionCardKind = {
	actionType: "routine_reach_out",
	provider: {
		id: "Littlebird",
		label: "Littlebird"
	},
	capability: "display-only",
	actionLabel: "Needs you",
	draftLabel: "Needs you",
	confirmationLabel: "Reply",
	confirmationVerb: "Reply",
	runningLabel: "Answering…",
	completedLabel: ANSWERED,
	chatLabel: "Reply",
	finalDismissal: { label: "Finished without you" },
	getActionLabel: (content) => content.title,
	getSummary: (content) => content.title,
	getOwnChat: (content) => ({
		chatId: content.chat_id,
		withName: "Littlebird"
	}),
	decode: (content) => RoutineReachOutContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [readonlyField({
		key: "body",
		label: "Message",
		value: content.body,
		kind: "multiline",
		format: "markdown",
		hideWhenEmpty: true
	})],
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: ANSWERED })
};
//#endregion
//#region src/slack-mentions.ts
const SLACK_USER_ID = "[UW][A-Z0-9]+";
const WRAPPED_MENTION = new RegExp(`<@(${SLACK_USER_ID})>([\\s\\S]*?)</@\\1>`, "g");
const LABELED_MENTION = new RegExp(`<@${SLACK_USER_ID}\\|([^>]+)>`, "g");
const BROADCAST_MENTION = /<!(here|channel|everyone)(?:\|[^>]*)?>/g;
const LABELED_USER_GROUP = /<!subteam\^[A-Z0-9]+\|([^>]+)>/g;
const withSingleAt = (label) => `@${label.replace(/^@/, "")}`;
/** Slack send needs `<@ID|Name>`; the wrapped form is not valid mrkdwn. */
const normalizeSlackMessageMentions = (text) => text.replace(WRAPPED_MENTION, (_match, userId, inner) => {
	const name = inner.trim();
	return name ? `<@${userId}|${name}>` : `<@${userId}>`;
});
/** Turns Slack mention tokens into names for on-screen action-card text. */
const formatSlackMentionsForDisplay = (text) => text.replace(WRAPPED_MENTION, (_match, userId, inner) => {
	const name = inner.trim();
	return name ? name : `<@${userId}>`;
}).replace(LABELED_MENTION, (_match, label) => withSingleAt(label)).replace(BROADCAST_MENTION, (_match, name) => `@${name}`).replace(LABELED_USER_GROUP, (_match, label) => withSingleAt(label));
//#endregion
//#region src/kinds/slack.ts
const isInternalSlackConversationName = (channelName) => channelName?.toLowerCase().startsWith("mpdm-") ?? false;
const isSlackMessage = (content) => content.type === "slack_message" || content.type === "slack_message_with_attachments";
const getReplyLabel = (content) => {
	const parentAuthor = content.parent_message?.person.name.trim() ?? content.person?.name.trim();
	if (content.thread_ts) return parentAuthor ? `Reply to ${parentAuthor}` : "Reply in thread";
	return content.type === "slack_message_with_attachments" ? "Send file" : "Send message";
};
const slackMessageSourceLink = (content) => classifiedSourceLink(content.parent_message?.message_link ?? content.message_link);
const getFields$2 = (content) => {
	const fields = [];
	if (content.account_label) fields.push(readonlyField({
		key: "account_label",
		label: "From",
		value: content.account_label,
		kind: "single"
	}));
	if (!isInternalSlackConversationName(content.channel_name)) fields.push(readonlyField({
		key: "channel",
		label: "Channel",
		value: content.channel_name ?? content.channel_id,
		kind: "single",
		href: slackMessageSourceLink(content)?.href,
		subtleLink: true
	}));
	fields.push(editableField({
		key: "message",
		label: "Message",
		value: formatSlackMentionsForDisplay(content.message),
		kind: "multiline",
		editor: "textarea",
		placeholder: content.type === "slack_message_with_attachments" ? void 0 : "Write your message"
	}));
	if (content.type === "slack_message_with_attachments") fields.push(readonlyField({
		key: "attachment",
		label: "Attachment",
		value: content.attachment.filename,
		kind: "single"
	}));
	return fields;
};
const withDisplayedParentMessage = (parsed) => {
	if (!parsed?.parent_message) return parsed ?? null;
	return {
		...parsed,
		parent_message: {
			...parsed.parent_message,
			text: formatSlackMentionsForDisplay(parsed.parent_message.text)
		}
	};
};
const decode = (content) => withDisplayedParentMessage(SlackMessageContentSchema.safeParse(content).data);
const decodeWithAttachments = (content) => withDisplayedParentMessage(SlackMessageWithAttachmentsContentSchema.safeParse(content).data);
const unwrapRecord = (data) => {
	if (!isRecord(data)) return void 0;
	if (typeof data.text !== "string") return data;
	try {
		const parsed = JSON.parse(data.text);
		return isRecord(parsed) ? parsed : data;
	} catch {
		return data;
	}
};
const buildExternalRef$1 = (data) => {
	const payload = unwrapRecord(data);
	if (!payload) return void 0;
	const ref = { provider: "slack" };
	if (typeof payload.message_link === "string") ref.message_link = payload.message_link;
	if (isRecord(payload.message_context)) {
		if (typeof payload.message_context.message_ts === "string") ref.message_ts = payload.message_context.message_ts;
		if (typeof payload.message_context.channel_id === "string") ref.channel_id = payload.message_context.channel_id;
	}
	return Object.keys(ref).length > 1 ? ref : void 0;
};
const buildFileExternalRef = (data) => {
	const payload = unwrapRecord(data);
	if (typeof payload?.file_id !== "string") return void 0;
	const ref = {
		provider: "slack",
		file_id: payload.file_id
	};
	if (typeof payload.file_link === "string" && payload.file_link.startsWith("https://")) ref.file_link = payload.file_link;
	return ref;
};
const hostHrefFrom = (content, externalRef) => {
	if (typeof externalRef?.message_link === "string") return externalRef.message_link;
	if (typeof externalRef?.channel_link === "string") return externalRef.channel_link;
	if (isSlackMessage(content)) return content.message_link ?? content.parent_message?.message_link ?? void 0;
	return content.message_link ?? void 0;
};
const sentSlackMessageLink = (content, externalRef) => {
	const fromRef = classifiedSlackMessagePermalink(typeof externalRef?.message_link === "string" ? externalRef.message_link : void 0);
	if (fromRef) return fromRef;
	const messageTs = typeof externalRef?.message_ts === "string" ? externalRef.message_ts : content.type === "slack_reaction" ? content.message_ts : void 0;
	const channelId = typeof externalRef?.channel_id === "string" ? externalRef.channel_id : content.channel_id;
	const hostHref = hostHrefFrom(content, externalRef);
	if (!messageTs || !hostHref) return void 0;
	return classifiedSlackMessagePermalink(slackArchivesUrl(hostHref, channelId, messageTs));
};
const externalUrl$1 = (externalRef) => {
	const sent = classifiedSlackMessagePermalink(typeof externalRef?.message_link === "string" ? externalRef.message_link : void 0);
	if (sent) return sent.href;
	const messageTs = typeof externalRef?.message_ts === "string" ? externalRef.message_ts : void 0;
	const channelId = typeof externalRef?.channel_id === "string" ? externalRef.channel_id : void 0;
	const hostHref = (typeof externalRef?.message_link === "string" ? externalRef.message_link : void 0) ?? (typeof externalRef?.channel_link === "string" ? externalRef.channel_link : void 0);
	if (messageTs && channelId && hostHref) {
		const constructed = classifiedSlackMessagePermalink(slackArchivesUrl(hostHref, channelId, messageTs));
		if (constructed) return constructed.href;
	}
	if (typeof externalRef?.message_link === "string") return externalRef.message_link;
	return typeof externalRef?.channel_link === "string" ? externalRef.channel_link : void 0;
};
const fileExternalUrl = (externalRef) => typeof externalRef?.file_link === "string" && externalRef.file_link.startsWith("https://") ? externalRef.file_link : externalUrl$1(externalRef);
const getSummary$1 = (content) => isInternalSlackConversationName(content.channel_name) ? content.thread_ts ? getReplyLabel(content) : content.person?.name ?? getReplyLabel(content) : content.channel_name ?? content.channel_id;
const withUpdatedMessage = (content, fieldKey, value) => fieldKey === "message" ? {
	...content,
	message: value
} : content;
const getCompletion = (url) => ({
	label: "Sent",
	linkLabel: "Open in Slack",
	url
});
const slackActionCardKind = {
	actionType: "slack_message",
	provider: {
		id: "slack",
		label: "Slack"
	},
	capability: "confirmable",
	actionLabel: "Send message",
	getActionLabel: getReplyLabel,
	confirmationLabel: "Send",
	confirmationVerb: "Send",
	runningLabel: "Sending message…",
	completedLabel: "Sent",
	decode,
	getFields: getFields$2,
	getQuotedMessage: (content) => content.parent_message ?? null,
	getSummary: getSummary$1,
	withUpdatedField: withUpdatedMessage,
	getCompletion,
	externalUrl: externalUrl$1,
	execution: {
		buildCalls: (content, externalRef) => {
			const draftId = externalRef?.draft_id;
			return [{
				integrationName: "mcp:slack",
				toolSlug: "mcp_slack__slack_send_message",
				arguments: {
					channel_id: content.channel_id,
					message: normalizeSlackMessageMentions(content.message),
					...content.thread_ts ? { thread_ts: content.thread_ts } : {},
					...typeof draftId === "string" && draftId.length > 0 ? { draft_id: draftId } : {}
				},
				accountLabel: content.account_label
			}];
		},
		buildExternalRef: buildExternalRef$1,
		externalUrl: externalUrl$1,
		validate: (content) => {
			if (content.channel_id.length === 0) return "Add a channel before sending.";
			return content.message.trim().length === 0 ? "Write your message before sending." : null;
		}
	}
};
const slackWithAttachmentsActionCardKind = {
	actionType: "slack_message_with_attachments",
	provider: {
		id: "slack",
		label: "Slack"
	},
	capability: "confirmable",
	actionLabel: "Send file",
	getActionLabel: getReplyLabel,
	confirmationLabel: "Send",
	confirmationVerb: "Send",
	runningLabel: "Sending file…",
	completedLabel: "Sent",
	decode: decodeWithAttachments,
	getFields: getFields$2,
	getQuotedMessage: (content) => content.parent_message ?? null,
	getSummary: getSummary$1,
	withUpdatedField: withUpdatedMessage,
	getCompletion,
	externalUrl: fileExternalUrl,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:slack",
			toolSlug: "mcp_slack__slack_upload_file",
			arguments: {
				channel_id: content.channel_id,
				attachment: {
					lb_file_id: content.attachment.file_id,
					name: content.attachment.filename,
					mimetype: content.attachment.mimetype,
					size: content.attachment.size
				},
				...content.message ? { initial_comment: normalizeSlackMessageMentions(content.message) } : {},
				...content.thread_ts ? { thread_ts: content.thread_ts } : {}
			},
			accountLabel: content.account_label
		}],
		buildExternalRef: buildFileExternalRef,
		externalUrl: fileExternalUrl,
		validate: (content) => content.channel_id.length === 0 ? "Add a channel before sending." : null
	}
};
const SLACK_EMOJI_GLYPHS = {
	eyes: "👀",
	thumbsup: "👍",
	"+1": "👍",
	white_check_mark: "✅",
	heavy_check_mark: "✔️",
	tada: "🎉",
	rocket: "🚀",
	heart: "❤️",
	fire: "🔥",
	raised_hands: "🙌",
	clap: "👏",
	pray: "🙏",
	thinking_face: "🤔",
	smile: "🙂",
	joy: "😂",
	laughing: "😆",
	warning: "⚠️",
	x: "❌",
	question: "❓",
	"100": "💯"
};
const SLACK_EMOJI_NAMES_BY_GLYPH = Object.fromEntries(Object.entries(SLACK_EMOJI_GLYPHS).map(([name, glyph]) => [glyph, name]));
const reactionEmoji = (value) => {
	const emoji = value.trim();
	const name = emoji.length > 2 && emoji.startsWith(":") && emoji.endsWith(":") ? emoji.slice(1, -1) : emoji;
	return SLACK_EMOJI_NAMES_BY_GLYPH[name] ?? name;
};
const reactionEmojiDisplay = (value) => {
	const name = reactionEmoji(value);
	return SLACK_EMOJI_GLYPHS[name] ?? `:${name}:`;
};
const getReactionFields = (content) => [
	...content.account_label ? [readonlyField({
		key: "account_label",
		label: "From",
		value: content.account_label,
		kind: "single"
	})] : [],
	readonlyField({
		key: "channel",
		label: "Channel",
		value: content.channel_name ?? content.channel_id,
		kind: "single"
	}),
	readonlyField({
		key: "message",
		label: "Message",
		value: content.message ?? `Slack message ${content.message_ts}`,
		kind: content.message ? "multiline" : "single",
		href: content.message_link ?? void 0,
		subtleLink: true
	}),
	editableField({
		key: "emoji",
		label: "Emoji",
		value: reactionEmojiDisplay(content.emoji),
		kind: "single",
		editor: "input"
	})
];
const slackReactionActionCardKind = {
	actionType: "slack_reaction",
	provider: {
		id: "slack",
		label: "Slack"
	},
	capability: "confirmable",
	actionLabel: "Add reaction",
	confirmationLabel: "React",
	confirmationVerb: "React",
	runningLabel: "Adding reaction…",
	completedLabel: "Reaction added",
	decode: (content) => SlackReactionContentSchema.safeParse(content).data ?? null,
	getFields: getReactionFields,
	getSummary: (content) => `${reactionEmojiDisplay(content.emoji)} in ${content.channel_name ?? content.channel_id}`,
	withUpdatedField: (content, fieldKey, value) => fieldKey === "emoji" ? {
		...content,
		emoji: reactionEmoji(value)
	} : content,
	getCompletion: (url) => ({
		label: "Reaction added",
		linkLabel: url ? "Open in Slack" : void 0,
		url
	}),
	externalUrl: externalUrl$1,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:slack",
			toolSlug: "mcp_slack__slack_add_reaction",
			arguments: {
				channel_id: content.channel_id,
				message_ts: content.message_ts,
				emoji: reactionEmoji(content.emoji)
			},
			accountLabel: content.account_label
		}],
		buildExternalRef: buildExternalRef$1,
		externalUrl: externalUrl$1,
		validate: (content) => {
			if (content.channel_id.length === 0) return "Add a channel before reacting.";
			if (content.message_ts.length === 0) return "Choose a Slack message before reacting.";
			return reactionEmoji(content.emoji).length === 0 ? "Add an emoji before reacting." : null;
		}
	}
};
//#endregion
//#region src/people.ts
const firstNameOf = (name) => name.trim().split(/[\s@]/)[0] || name;
const teammateDisplayName = (teammate) => teammate.name || teammate.email;
function personOption(person, provider, account) {
	if (!person?.id.trim()) return void 0;
	const email = person.email?.trim();
	const scope = provider.id === "slack" ? "slack:" + encodeURIComponent(account) : provider.id + ":id";
	return {
		id: email && isValidRecipient(email) ? "email:" + normalizeRecipient(email).toLowerCase() : scope + ":" + encodeURIComponent(person.id.trim()),
		label: person.name.trim() || person.id,
		avatarUrl: person.avatar_url
	};
}
function informationalPersonOption(person, provider, account) {
	if ((provider.id === "gmail" || provider.id === "outlook" || provider.id === "google-calendar") && isValidRecipient(person.id)) return {
		id: "email:" + normalizeRecipient(person.id).toLowerCase(),
		label: person.name.trim() || person.id,
		avatarUrl: person.avatar_url
	};
	return personOption(person, provider, account);
}
function recipientOptions(recipients) {
	const options = [];
	for (const raw of recipients.flatMap(parseRecipients)) {
		if (!isValidRecipient(raw)) continue;
		const email = normalizeRecipient(raw).toLowerCase();
		const name = raw.includes("<") ? raw.slice(0, raw.lastIndexOf("<")).trim().replace(/^"(.*)"$/, "$1") : "";
		options.push({
			id: "email:" + email,
			label: name || email
		});
	}
	return options;
}
function todoistAssigneeOption(content, account) {
	const assignee = content.assignee?.trim();
	if (!assignee || assignee === "unassign") return void 0;
	return {
		id: assignee === "me" ? "todoist:account:" + encodeURIComponent(account) + ":me" : "todoist:id:" + encodeURIComponent(assignee),
		label: assignee === "me" ? "Me" : assignee
	};
}
function githubReviewerOption(reviewer) {
	const login = reviewer.trim().replace(/^@/, "");
	if (!login) return void 0;
	return {
		id: "github:login:" + encodeURIComponent(login.toLowerCase()),
		label: login
	};
}
function teammateOption(teammate) {
	return {
		id: "teammate:id:" + teammate.id,
		label: teammate.name
	};
}
function getActionCardPeople(content, provider) {
	const people = /* @__PURE__ */ new Map();
	const account = content.type === "informational" ? content.source?.account_label ?? "" : "account_label" in content ? content.account_label ?? "" : "";
	const add = (person) => {
		if (person && !people.has(person.id)) people.set(person.id, person);
	};
	if ("person" in content) add(personOption(content.person, provider, account));
	switch (content.type) {
		case "informational":
			for (const person of content.source?.people ?? []) add(informationalPersonOption(person, provider, account));
			break;
		case "slack_message":
		case "slack_message_with_attachments":
			add(personOption(content.parent_message?.person, provider, account));
			break;
		case "gmail_email_draft":
		case "gmail_email_draft_with_attachments":
		case "outlook_email_draft":
			recipientOptions([
				...content.to,
				...content.cc,
				...content.bcc
			]).forEach(add);
			break;
		case "google_calendar_event":
			recipientOptions(content.attendees).forEach(add);
			break;
		case "github_pull_request":
			for (const reviewer of content.reviewers) add(githubReviewerOption(reviewer));
			break;
		case "linear_issue":
		case "linear_issue_update":
			if (!content.person && content.assignee?.trim()) add({
				id: "linear:id:" + encodeURIComponent(content.assignee.trim()),
				label: content.assignee_name?.trim() || content.assignee.trim()
			});
			break;
		case "todoist_task":
		case "todoist_task_update":
			add(todoistAssigneeOption(content, account));
			break;
		case "teammate_message": add(teammateOption(content.teammate));
	}
	return [...people.values()];
}
//#endregion
//#region src/kinds/teammate-message.ts
const SUMMARY_SNIPPET_LENGTH = 80;
const teammateFirstName = (content) => firstNameOf(content.teammate.name);
const snippet = (text) => {
	const flat = text.replace(/\s+/g, " ").trim();
	return flat.length <= SUMMARY_SNIPPET_LENGTH ? flat : `${flat.slice(0, SUMMARY_SNIPPET_LENGTH).trimEnd()}…`;
};
const sentLabel = (content) => `${content.sent_automatically ? "Sent automatically" : "Sent"} to ${teammateFirstName(content)}`;
const isWaiting = (content, status) => status === "completed" && content.awaiting_reply;
const getSummary = (content) => content.incoming_text ? `${content.teammate.name}: ${snippet(content.incoming_text)}` : `Message to ${content.teammate.name}`;
const getFields$1 = (content) => [...content.incoming_text ? [readonlyField({
	key: "incoming_text",
	label: `${content.teammate.name}'s Littlebird`,
	value: content.incoming_text,
	kind: "multiline",
	format: "markdown"
})] : [], editableField({
	key: "reply",
	label: content.incoming_text ? "Your reply" : "Your message",
	value: content.sent_text ?? content.draft,
	kind: "multiline",
	format: "markdown",
	editor: "textarea"
})];
const teammateMessageActionCardKind = {
	actionType: "teammate_message",
	provider: {
		id: "agents",
		label: "Littlebird"
	},
	capability: "confirmable",
	actionLabel: "Message",
	getActionLabel: (content) => {
		if (content.sent_text !== null) return sentLabel(content);
		return content.incoming_text ? `Reply to ${teammateFirstName(content)}` : getSummary(content);
	},
	confirmationLabel: "Send",
	confirmationVerb: "Send",
	runningLabel: "Sending…",
	completedLabel: "Sent",
	getLabels: (content) => {
		const firstName = teammateFirstName(content);
		const sent = sentLabel(content);
		return {
			confirmationLabel: `Send to ${firstName}`,
			confirmationVerb: "Send",
			runningLabel: "Sending…",
			completedLabel: sent,
			getCompletion: () => ({ label: sent })
		};
	},
	getProgressLabel: (content, status) => isWaiting(content, status) ? `Waiting on ${teammateFirstName(content)}` : void 0,
	getOwnChat: (content) => ({
		chatId: content.chat_id,
		withName: content.teammate.name
	}),
	acknowledge: {
		label: "OK",
		completedLabel: "Acknowledged"
	},
	waiting: {
		isWaiting,
		isDismissed: (content) => content.dismissed === true
	},
	feedType: "teammate",
	decode: (content) => TeammateMessageContentSchema.safeParse(content).data ?? null,
	getFields: getFields$1,
	getSummary,
	withUpdatedField: (content, fieldKey, value) => fieldKey === "reply" ? {
		...content,
		draft: value
	} : content,
	getCompletion: () => ({ label: "Sent" }),
	serverExecution: {
		endpoint: (cardId) => `hivemind/threads/cards/${cardId}/send`,
		exactRevision: true,
		validate: (content) => content.draft.trim().length === 0 ? "Write a reply before sending." : null
	}
};
//#endregion
//#region src/kinds/todoist.ts
const provider = {
	id: "todoist",
	label: "Todoist"
};
const priorityArgument = (priority) => `p${5 - priority}`;
const priorityLabel = (priority) => priority === null ? "" : `P${5 - priority}`;
const changeLabel = (previous, next) => previous && previous !== next ? `${previous} → ${next}` : next;
const getFields = (content) => [
	editableField({
		key: "content",
		label: "Task",
		value: content.content,
		kind: "single",
		editor: "input"
	}),
	editableField({
		key: "description",
		label: "Description",
		value: content.description ?? "",
		format: "markdown",
		kind: "multiline",
		editor: "markdown",
		hideWhenEmpty: true,
		collapsible: true
	}),
	readonlyField({
		key: "project",
		label: "Project",
		value: content.project_name ?? content.project ?? "",
		kind: "single",
		hideWhenEmpty: true,
		advanced: true
	}),
	editableField({
		key: "due",
		label: "Due",
		value: content.due ?? "",
		kind: "single",
		editor: "input",
		hideWhenEmpty: true
	}),
	editableField({
		key: "priority",
		label: "Priority",
		value: priorityLabel(content.priority),
		kind: "single",
		editor: "input",
		hideWhenEmpty: true,
		advanced: true
	})
];
const buildArguments = (content) => {
	const task = { content: content.content };
	if (content.description) task.description = content.description;
	if (content.due) task.dueString = content.due;
	if (content.deadline) task.deadlineDate = content.deadline;
	if (content.duration) task.duration = content.duration;
	if (content.labels.length > 0) task.labels = [...content.labels];
	if (content.project) task.projectId = content.project;
	if (content.section) task.sectionId = content.section;
	if (content.parent) task.parentId = content.parent;
	if (content.order !== null) task.order = content.order;
	if (content.assignee) task.responsibleUser = content.assignee;
	if (content.uncompletable !== null) task.isUncompletable = content.uncompletable;
	if (content.priority !== null) task.priority = priorityArgument(content.priority);
	return { tasks: [task] };
};
const isTaskId = (value) => typeof value === "string" || typeof value === "number";
const taskIdExternalRef = (taskId) => isTaskId(taskId) ? {
	provider: "todoist",
	task_id: taskId
} : void 0;
const buildExternalRef = (data) => {
	if (!isRecord(data) || !Array.isArray(data.tasks)) return void 0;
	const first = data.tasks[0];
	return isRecord(first) ? taskIdExternalRef(first.id) : void 0;
};
const firstFailure = (data) => {
	const failures = data.failures;
	if (!Array.isArray(failures) || failures.length === 0) return void 0;
	const first = failures[0];
	return isRecord(first) && typeof first.error === "string" ? `Todoist rejected the change: ${first.error}` : "Todoist rejected the change.";
};
const bulkResultError = (echoKey, nothingHappened) => (data) => {
	if (!isRecord(data)) return nothingHappened;
	const echoed = data[echoKey];
	if (Array.isArray(echoed) && echoed.length > 0) return void 0;
	return firstFailure(data) ?? nothingHappened;
};
const externalUrl = (externalRef) => {
	const taskId = externalRef?.task_id;
	return isTaskId(taskId) ? `https://app.todoist.com/app/task/${taskId}` : void 0;
};
const todoistActionCardKind = {
	actionType: "todoist_task",
	provider,
	capability: "confirmable",
	actionLabel: "Create task",
	confirmationLabel: "Create task",
	confirmationVerb: "Create",
	runningLabel: "Creating task…",
	completedLabel: "Created",
	decode: (content) => TodoistTaskContentSchema.safeParse(content).data ?? null,
	getFields,
	getSummary: (content) => content.content,
	withUpdatedField: (content, fieldKey, value) => {
		if (fieldKey === "content") return {
			...content,
			content: value
		};
		if (fieldKey === "description" || fieldKey === "due") return {
			...content,
			[fieldKey]: value || null
		};
		if (fieldKey === "priority") {
			const displayValue = value.replace(/^P/i, "").trim();
			const displayPriority = Number(displayValue);
			const priority = displayValue && Number.isInteger(displayPriority) ? 5 - displayPriority : null;
			return {
				...content,
				priority
			};
		}
		return content;
	},
	getCompletion: (url) => ({
		label: "Created",
		linkLabel: "View in Todoist",
		url
	}),
	externalUrl,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:todoist",
			toolSlug: "mcp_todoist__add_tasks",
			arguments: buildArguments(content),
			accountLabel: content.account_label
		}],
		buildExternalRef,
		externalUrl,
		resultError: bulkResultError("tasks", "Todoist did not create the task. Retry in a moment."),
		validate: (content) => content.content.length === 0 ? "Add a task name before creating." : null
	}
};
const buildIdListExternalRef = (key) => (data) => {
	if (!isRecord(data)) return void 0;
	const ids = data[key];
	return Array.isArray(ids) ? taskIdExternalRef(ids[0]) : void 0;
};
const taskFields = (content) => [
	targetField("Task", content.task_content),
	detailField("project", "Project", content.project_name ?? ""),
	detailField("due", "Due", content.due ?? ""),
	detailField("priority", "Priority", priorityLabel(content.priority))
];
const taskStateKind = (options) => ({
	actionType: options.actionType,
	provider,
	capability: "confirmable",
	actionLabel: `${options.verb} task`,
	confirmationLabel: `${options.verb} task`,
	confirmationVerb: options.verb,
	runningLabel: options.runningLabel,
	completedLabel: options.completedLabel,
	decode: options.decode,
	getFields: taskFields,
	getSummary: (content) => content.task_content,
	withUpdatedField: (content) => content,
	getCompletion: (url) => ({
		label: options.completedLabel,
		linkLabel: "View in Todoist",
		url
	}),
	externalUrl,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:todoist",
			toolSlug: options.toolSlug,
			arguments: { ids: [content.task_id] },
			accountLabel: content.account_label
		}],
		buildExternalRef: buildIdListExternalRef(options.echoKey),
		externalUrl,
		resultError: bulkResultError(options.echoKey, options.nothingHappenedMessage),
		validate: (content) => content.task_id.length === 0 ? options.missingTaskMessage : null
	}
});
const todoistTaskCompleteActionCardKind = taskStateKind({
	actionType: "todoist_task_complete",
	decode: (content) => TodoistTaskCompleteContentSchema.safeParse(content).data ?? null,
	verb: "Complete",
	runningLabel: "Completing task…",
	completedLabel: "Completed",
	toolSlug: "mcp_todoist__complete_tasks",
	echoKey: "completed",
	missingTaskMessage: "Choose a task in chat before completing.",
	nothingHappenedMessage: "Todoist did not complete the task. Retry in a moment."
});
const todoistTaskUncompleteActionCardKind = taskStateKind({
	actionType: "todoist_task_uncomplete",
	decode: (content) => TodoistTaskUncompleteContentSchema.safeParse(content).data ?? null,
	verb: "Reopen",
	runningLabel: "Reopening task…",
	completedLabel: "Reopened",
	toolSlug: "mcp_todoist__uncomplete_tasks",
	echoKey: "uncompleted",
	missingTaskMessage: "Choose a task in chat before reopening.",
	nothingHappenedMessage: "Todoist did not reopen the task. Retry in a moment."
});
const todoistObjectDeleteActionCardKind = {
	actionType: "todoist_object_delete",
	provider,
	capability: "confirmable",
	actionLabel: "Delete",
	confirmationLabel: "Delete",
	confirmationVerb: "Delete",
	runningLabel: "Deleting…",
	completedLabel: "Deleted",
	decode: (content) => TodoistObjectDeleteContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [
		targetField(content.object_type === "task" ? "Task" : "Project", content.object_name),
		detailField("project", "Project", content.project_name ?? ""),
		detailField("due", "Due", content.due ?? "")
	],
	getSummary: (content) => content.object_name,
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: "Deleted" }),
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:todoist",
			toolSlug: "mcp_todoist__delete_object",
			arguments: {
				type: content.object_type,
				id: content.object_id
			},
			accountLabel: content.account_label
		}],
		buildExternalRef: () => void 0,
		externalUrl: () => void 0,
		resultError: (data, content) => {
			const deleted = isRecord(data) && data.success === true ? data.deletedEntity : void 0;
			return isRecord(deleted) && deleted.type === content.object_type && String(deleted.id) === content.object_id ? void 0 : "Todoist did not confirm the deletion. Check the item in Todoist before retrying.";
		},
		validate: (content) => content.object_id.length === 0 ? "Choose a task or project in chat before deleting." : null
	}
};
const clearableLabel = (value, clearedLabel) => value === "remove" ? clearedLabel : value;
const assigneeLabel = (assignee) => {
	if (assignee === "unassign") return "Unassign";
	if (assignee === "me") return "Me";
	return assignee;
};
const labelsField = (content) => {
	if (content.labels === null) return [];
	if (content.labels.length === 0) return [detailField("labels", "Labels", "Remove all labels")];
	const previous = content.previous_labels?.join(", ") ?? null;
	return [detailField("labels", "Labels (replaces existing)", changeLabel(previous, content.labels.join(", ")))];
};
const updateChangeFields = (content) => [
	...whenChanged(content.content, (value) => editableField({
		key: "content",
		label: "New title",
		value,
		kind: "single",
		editor: "input"
	})),
	...whenChanged(content.description, (value) => bodyField("description", "Description", value)),
	...whenChanged(content.due, (value) => detailField("due", "Due", changeLabel(content.previous_due, clearableLabel(value, "Remove due date")))),
	...whenChanged(content.deadline, (value) => detailField("deadline", "Deadline", clearableLabel(value, "Remove deadline"))),
	...whenChanged(content.duration, (value) => detailField("duration", "Duration", value)),
	...labelsField(content),
	...whenChanged(content.project, (value) => detailField("project", "Project", changeLabel(content.previous_project_name, content.project_name ?? value))),
	...whenChanged(content.assignee, (value) => detailField("assignee", "Assignee", assigneeLabel(value))),
	...whenChanged(content.uncompletable, (value) => detailField("uncompletable", "Uncompletable", value ? "Yes" : "No")),
	...whenChanged(content.priority, (value) => detailField("priority", "Priority", changeLabel(priorityLabel(content.previous_priority), priorityLabel(value))))
];
const buildUpdateArguments = (content) => {
	const task = { id: content.task_id };
	if (content.content !== null) task.content = content.content;
	if (content.description !== null) task.description = content.description;
	if (content.due !== null) task.dueString = content.due;
	if (content.deadline !== null) task.deadlineDate = content.deadline;
	if (content.duration !== null) task.duration = content.duration;
	if (content.labels !== null) task.labels = [...content.labels];
	if (content.project !== null) task.projectId = content.project;
	if (content.assignee !== null) task.responsibleUser = content.assignee;
	if (content.uncompletable !== null) task.isUncompletable = content.uncompletable;
	if (content.priority !== null) task.priority = priorityArgument(content.priority);
	return { tasks: [task] };
};
const hasUpdateChange = (content) => Object.keys(buildUpdateArguments(content).tasks[0]).length > 1;
const todoistTaskUpdateActionCardKind = {
	actionType: "todoist_task_update",
	provider,
	capability: "confirmable",
	actionLabel: "Update task",
	confirmationLabel: "Update task",
	confirmationVerb: "Update",
	runningLabel: "Updating task…",
	completedLabel: "Updated",
	decode: (content) => TodoistTaskUpdateContentSchema.safeParse(content).data ?? null,
	getFields: (content) => [targetField("Task", content.task_content), ...updateChangeFields(content)],
	getSummary: (content) => content.task_content,
	withUpdatedField: (content, key, value) => (key === "content" || key === "description") && content[key] !== null ? {
		...content,
		[key]: value
	} : content,
	getCompletion: (url) => ({
		label: "Updated",
		linkLabel: "View in Todoist",
		url
	}),
	externalUrl,
	execution: {
		buildCalls: (content) => [{
			integrationName: "mcp:todoist",
			toolSlug: "mcp_todoist__update_tasks",
			arguments: buildUpdateArguments(content),
			accountLabel: content.account_label
		}],
		buildExternalRef,
		externalUrl,
		resultError: bulkResultError("tasks", "Todoist did not update the task. Retry in a moment."),
		validate: (content) => {
			if (content.task_id.length === 0) return "Choose a task in chat before updating.";
			if (content.content !== null && !content.content.trim()) return "Add a task title before updating.";
			if (content.description !== null && !content.description.trim()) return "Add a description, or remove that change, before updating.";
			return hasUpdateChange(content) ? null : "Specify a task change in chat before updating.";
		}
	}
};
//#endregion
//#region src/kinds/tool-approval.ts
const toolApprovalActionCardKind = {
	actionType: "tool_approval",
	provider: {
		id: "Littlebird",
		label: "Littlebird"
	},
	capability: "confirmable",
	actionLabel: "Approve tool call",
	confirmationLabel: "Approve",
	confirmationVerb: "Approve",
	runningLabel: "Running…",
	completedLabel: "Approved",
	decode: (content) => ToolApprovalContentSchema.safeParse(content).data ?? null,
	getSummary: (content) => content.tool_name?.trim() || content.tool_slug,
	getFields: (content) => [readonlyField({
		key: "arguments",
		label: "Arguments",
		value: JSON.stringify(content.arguments, null, 2),
		kind: "multiline"
	})],
	withUpdatedField: (content) => content,
	getCompletion: () => ({ label: "Approved and ran" }),
	serverExecution: {
		endpoint: (cardId) => `action-cards/${cardId}/run`,
		validate: () => null
	}
};
//#endregion
//#region src/registry.ts
const INFORMATIONAL_PROVIDERS = {
	gmail: gmailActionCardKind.provider,
	outlook: outlookActionCardKind.provider,
	"google-calendar": googleCalendarActionCardKind.provider,
	todoist: todoistActionCardKind.provider,
	slack: slackActionCardKind.provider,
	github: githubCommentActionCardKind.provider,
	linear: linearIssueActionCardKind.provider
};
const failureMessageFromResult = (result) => {
	const error = result?.error;
	return typeof error === "string" && error.length > 0 ? error : void 0;
};
const failureMessageOf = (card) => (typeof card.result?.kind === "string" ? card.computed?.error?.message : void 0) ?? failureMessageFromResult(card.result);
const ACTION_CARD_KINDS = {
	ask_user: askUserActionCardKind,
	agent_team: agentTeamActionCardKind,
	routine_offer: routineOfferActionCardKind,
	teammate_message: teammateMessageActionCardKind,
	question: questionActionCardKind,
	tool_approval: toolApprovalActionCardKind,
	coding_agent: codingAgentActionCardKind,
	informational: informationalActionCardKind,
	routine_reach_out: routineReachOutActionCardKind,
	gmail_email_draft: gmailActionCardKind,
	gmail_email_draft_with_attachments: gmailWithAttachmentsActionCardKind,
	gmail_noise_archive: gmailNoiseArchiveActionCardKind,
	gmail_label_review: gmailLabelReviewActionCardKind,
	outlook_email_draft: outlookActionCardKind,
	google_calendar_event: googleCalendarActionCardKind,
	google_calendar_rsvp: googleCalendarRsvpActionCardKind,
	google_calendar_event_update: googleCalendarEventUpdateActionCardKind,
	google_task: googleTaskActionCardKind,
	google_task_update: googleTaskUpdateActionCardKind,
	google_task_delete: googleTaskDeleteActionCardKind,
	google_task_move: googleTaskMoveActionCardKind,
	todoist_task: todoistActionCardKind,
	todoist_task_complete: todoistTaskCompleteActionCardKind,
	todoist_task_update: todoistTaskUpdateActionCardKind,
	todoist_task_uncomplete: todoistTaskUncompleteActionCardKind,
	todoist_object_delete: todoistObjectDeleteActionCardKind,
	slack_message: slackActionCardKind,
	slack_message_with_attachments: slackWithAttachmentsActionCardKind,
	slack_reaction: slackReactionActionCardKind,
	github_comment: githubCommentActionCardKind,
	github_pull_request: githubPullRequestActionCardKind,
	github_pull_request_update: githubPullRequestUpdateActionCardKind,
	github_pull_request_close: githubPullRequestCloseActionCardKind,
	github_pull_request_merge: githubPullRequestMergeActionCardKind,
	github_pull_request_approve: githubPullRequestApproveActionCardKind,
	github_pull_request_review_comment: githubPullRequestReviewCommentActionCardKind,
	github_pull_request_request_changes: githubPullRequestRequestChangesActionCardKind,
	linear_issue: linearIssueActionCardKind,
	linear_issue_update: linearIssueUpdateActionCardKind,
	linear_comment: linearCommentActionCardKind,
	hubspot_contact_note: hubspotContactNoteActionCardKind,
	hubspot_contact_update: hubspotContactUpdateActionCardKind,
	hubspot_deal: hubspotDealActionCardKind,
	hubspot_company: hubspotCompanyActionCardKind,
	hubspot_contact_create: hubspotContactCreateActionCardKind,
	hubspot_deal_update: hubspotDealUpdateActionCardKind,
	hubspot_task: hubspotTaskActionCardKind,
	hubspot_task_update: hubspotTaskUpdateActionCardKind,
	microsoft_teams_message: microsoftTeamsMessageActionCardKind
};
const KIND_BY_ACTION_TYPE = ACTION_CARD_KINDS;
const getActionCardKind = (actionType) => KIND_BY_ACTION_TYPE[actionType];
const RUN_FINISHED_WITHOUT_YOU = { label: "Finished without you" };
/** A question a routine run asked in its chat, which waits on the user like the run's reach-out card */
const isRoutineQuestionCard = (card) => card.actionType === "ask_user" && card.origin === "routine";
/** What a dismissal of the card does when the server acts on it at once and refuses to restore the card. A routine
* run's questions wait on the user like its reach-out card, so dismissing either finishes the run without them */
const finalDismissalOf = (card) => getActionCardKind(card.actionType)?.finalDismissal ?? (isRoutineQuestionCard(card) ? RUN_FINISHED_WITHOUT_YOU : void 0);
const withEmoji = (field) => field.format === "html" || field.format === "markdown" ? field : {
	...field,
	value: emojiFromShortcodes(field.value)
};
const attachmentWithEmoji = (attachment) => typeof attachment.title === "string" ? {
	...attachment,
	title: emojiFromShortcodes(attachment.title)
} : attachment;
const quotedMessageWithEmoji = (message) => message && {
	...message,
	text: emojiFromShortcodes(message.text)
};
const contextWithEmoji = (context) => context && emojiFromShortcodes(context);
const resolveWithKind = (kind, content) => {
	const widened = kind;
	const labels = widened.getLabels?.(content);
	return {
		kind: labels ? {
			...widened,
			...labels
		} : widened,
		content,
		getFields: (externalRef) => widened.getFields(content, externalRef).map(withEmoji),
		getAttachments: () => (widened.getAttachments?.(content) ?? []).map(attachmentWithEmoji),
		getQuotedMessage: () => quotedMessageWithEmoji(widened.getQuotedMessage?.(content) ?? null),
		getCalendarProposal: (externalRef) => widened.getCalendarProposal?.(content, externalRef ?? null) ?? null,
		getSummary: (externalRef) => emojiFromShortcodes(widened.getSummary(content, externalRef)),
		getActionLabel: (externalRef) => emojiFromShortcodes(widened.getActionLabel?.(content, externalRef) ?? widened.actionLabel),
		withUpdatedField: (fieldKey, value) => widened.withUpdatedField(content, fieldKey, value),
		execution: "execution" in widened ? {
			validate: (externalRef) => widened.execution.validate?.(content, externalRef) ?? null,
			buildCalls: (externalRef) => widened.execution.buildCalls(content, externalRef),
			buildExternalRef: (data) => widened.execution.buildExternalRef(data),
			resultError: (data) => widened.execution.resultError?.(data, content)
		} : null,
		serverExecution: "serverExecution" in widened ? {
			validate: () => widened.serverExecution.validate(content),
			endpoint: widened.serverExecution.endpoint,
			exactRevision: widened.serverExecution.exactRevision ?? false
		} : null,
		revision: "execution" in widened ? widened.revision : void 0
	};
};
const resolveActionCard = (content) => resolveWithKind(ACTION_CARD_KINDS[content.type], content);
const displayContext = (content, context) => {
	if (context === null || context === void 0) return void 0;
	return isSlackMessage(content) || content.type === "slack_reaction" ? formatSlackMentionsForDisplay(context) : context;
};
const sourceLinkForContent = (content) => {
	if (isSlackMessage(content)) return slackMessageSourceLink(content);
	if (content.type === "slack_reaction") return classifiedSourceLink(content.message_link);
	if (content.type === "informational") return anyWebSourceLink(content.url);
};
const sourceLinkFromExternalRef = (externalRef) => {
	if (!externalRef) return void 0;
	return classifiedSourceLink(typeof externalRef.message_link === "string" ? externalRef.message_link : void 0) ?? classifiedSourceLink(typeof externalRef.channel_link === "string" ? externalRef.channel_link : void 0);
};
const sourceLinkFromSlackMessageBody = (content) => {
	if (!isSlackMessage(content)) return void 0;
	return sourceLinkFromText(content.parent_message?.text) ?? sourceLinkFromText(content.message);
};
const sourceLinkForCard = (content, context, externalRef) => {
	if (isSlackMessage(content) || content.type === "slack_reaction") {
		const sent = sentSlackMessageLink(content, externalRef);
		if (sent) return sent;
	}
	return sourceLinkForContent(content) ?? sourceLinkFromExternalRef(externalRef) ?? sourceLinkFromText(context) ?? sourceLinkFromSlackMessageBody(content);
};
const decodeActionCard = (card) => {
	const knownKind = getActionCardKind(card.actionType);
	const serverTitle = card.computed?.title;
	const kind = knownKind ?? informationalActionCardKind;
	const content = knownKind ? knownKind.decode(card.content) : serverTitle ? informationalActionCardKind.decode({
		title: serverTitle,
		body: card.computed?.summary ?? ""
	}) : null;
	if (!content) return null;
	const resolved = resolveWithKind(kind, content);
	const context = displayContext(content, card.context);
	const sourceLink = sourceLinkForCard(content, context, card.externalRef);
	const sentLink = isSlackMessage(content) || content.type === "slack_reaction" ? sentSlackMessageLink(content, card.externalRef) : void 0;
	return {
		cardId: card.id,
		provider: content.type === "informational" && content.source ? INFORMATIONAL_PROVIDERS[content.source.app] : kind.provider,
		capability: kind.capability,
		feedType: knownKind ? kind.feedType : "update",
		content,
		fields: resolved.getFields(card.externalRef),
		summary: resolved.getSummary(card.externalRef),
		actionLabel: resolved.getActionLabel(card.externalRef),
		confirmationLabel: resolved.kind.confirmationLabel,
		confirmationVerb: resolved.kind.confirmationVerb,
		runningLabel: resolved.kind.runningLabel,
		completedLabel: resolved.kind.completedLabel,
		status: card.status,
		externalUrl: kind.externalUrl?.(card.externalRef) ?? sentLink?.href,
		externalRef: card.externalRef,
		revision: resolved.revision,
		failureMessage: failureMessageOf(card),
		context: contextWithEmoji(contextWithoutSourceHref(context, sourceLink?.href)),
		sourceLink
	};
};
const narrowCardContent = (actionType, content) => {
	return getActionCardKind(actionType)?.decode(content) ?? null;
};
//#endregion
//#region src/analytics.ts
const INFORMATIONAL_SOURCE_APPS = /* @__PURE__ */ new Set([
	"gmail",
	"outlook",
	"google-calendar",
	"todoist",
	"slack",
	"github",
	"linear"
]);
const getInformationalCardKind = (card) => {
	if (card.actionType !== "informational") return void 0;
	const kind = card.content["kind"] ?? "info";
	return kind === "info" || kind === "todo" ? kind : void 0;
};
const getInformationalSourceApp = (card) => {
	if (card.actionType !== "informational") return void 0;
	const source = card.content["source"];
	if (!source || typeof source !== "object" || Array.isArray(source)) return void 0;
	const app = source["app"];
	return typeof app === "string" && INFORMATIONAL_SOURCE_APPS.has(app) ? app : void 0;
};
/**
* Bounded because a mobile session can run for days. Dropping the oldest keys
* can at worst re-fire the event for a card the user scrolled past thousands of
* cards ago, which is cheaper than growing without limit.
*/
const MAX_REMEMBERED_ACTION_CARDS = 2e3;
const firstOffer = () => {
	const offered = /* @__PURE__ */ new Set();
	return (key) => {
		if (offered.has(key)) return false;
		if (offered.size >= MAX_REMEMBERED_ACTION_CARDS) {
			const oldest = offered.values().next().value;
			if (oldest !== void 0) offered.delete(oldest);
		}
		offered.add(key);
		return true;
	};
};
const firstShown = firstOffer();
const firstViewed = firstOffer();
const contextOf = (context) => typeof context === "string" ? { surface: context } : context;
/** `undefined` once this surface has shown the card, so it fires once. */
const actionCardShownProps = (card, context, formFactor) => {
	const normalized = contextOf(context);
	if (!firstShown(`${card.id}:${normalized.surface}`)) return void 0;
	return buildActionCardProps(card, formFactor === void 0 ? normalized : {
		...normalized,
		form_factor: formFactor
	});
};
/** `undefined` once this surface has reported the card viewed, so it fires once */
const actionCardViewedProps = (card, context) => {
	const normalized = contextOf(context);
	if (!firstViewed(`${card.id}:${normalized.surface}`)) return void 0;
	return buildActionCardProps(card, normalized);
};
const secondsBetween = (from, to) => Math.trunc((to - from) / 1e3);
const buildActionCardProps = (card, extra) => ({
	card_id: card.id,
	action_type: card.actionType,
	card_kind: getInformationalCardKind(card),
	...card.followUpChatId === void 0 ? {} : {
		has_follow_up_chat: !!card.followUpChatId,
		follow_up_chat_id: card.followUpChatId ?? void 0
	},
	...card.followUpRequestId === void 0 ? {} : { follow_up_in_progress: !!card.followUpRequestId },
	...card.followUpReplyKind === void 0 ? {} : { follow_up_reply_kind: card.followUpReplyKind ?? void 0 },
	...card.followUpRepliedAt === void 0 ? {} : { follow_up_replied: !!card.followUpRepliedAt },
	provider_id: getActionCardKind(card.actionType)?.provider.id,
	source_app: getInformationalSourceApp(card) ?? (card.sourceMeetingId ? "littlebird-meeting" : void 0),
	source_meeting_id: card.sourceMeetingId ?? void 0,
	origin: card.origin,
	status: card.status,
	...card.parentId ? { parent_card_id: card.parentId } : {},
	...typeof card.importance === "number" ? { importance: card.importance } : {},
	...card.createdAt ? { seconds_since_created: secondsBetween(card.createdAt.getTime(), Date.now()) } : {},
	...card.expiresAt ? { seconds_until_expiry: secondsBetween(Date.now(), card.expiresAt.getTime()) } : {},
	...extra
});
//#endregion
//#region src/chat-prompt-cards.ts
const chatPromptEntryCache = /* @__PURE__ */ new WeakMap();
const toChatPromptEntry = (card) => {
	const cached = chatPromptEntryCache.get(card);
	if (cached?.content === card.content) return cached.entry;
	const content = ChatPromptContentSchema.safeParse(card.content);
	const entry = content.success ? {
		card,
		content: content.data
	} : null;
	chatPromptEntryCache.set(card, {
		content: card.content,
		entry
	});
	return entry;
};
const selectChatPromptCards = (cards) => cards.flatMap((card) => {
	if (card.origin !== "littlebird_admin" || card.actionType !== "chat_prompt" || card.status !== "draft" || card.deletedAt) return [];
	const entry = toChatPromptEntry(card);
	return entry ? [entry] : [];
}).sort((left, right) => right.card.createdAt.getTime() - left.card.createdAt.getTime() || right.card.id.localeCompare(left.card.id));
//#endregion
//#region src/commands.ts
const log = getLogger("action-cards");
var ActionCardMutationsStore = class {
	pending = /* @__PURE__ */ new Set();
	constructor() {
		makeAutoObservable(this);
	}
	isPending(cardId) {
		return this.pending.has(cardId);
	}
	markPending(cardId) {
		this.pending.add(cardId);
	}
	unmarkPending(cardId) {
		this.pending.delete(cardId);
	}
};
const actionCardMutationsStore = new ActionCardMutationsStore();
/** `undefined` when the surface should stay quiet; the choice is shared. */
const actionCardFailureMessage = (outcome) => outcome && outcome.status === "failed" && !outcome.silent ? outcome.error || void 0 : void 0;
const bodyErrorCode = (error) => extractBackendErrorCode(error);
const errorMessage = (error, fallback) => error instanceof Error ? error.message : fallback;
const failed = (cardId, error, options) => ({
	cardId,
	status: "failed",
	error,
	...options?.silent ? { silent: true } : {}
});
const silentFailure = (cardId, error) => failed(cardId, error, { silent: true });
const skipped = (cardId, error) => ({
	cardId,
	status: "skipped",
	error
});
const succeeded = (cardId) => ({
	cardId,
	status: "succeeded"
});
const postToolsExecute = (call) => getApiClient().fetch("tools/execute", {
	method: "POST",
	backoff: NO_BACKOFF,
	body: JSON.stringify({
		integration_name: call.integrationName,
		tool_slug: call.toolSlug,
		arguments: call.arguments,
		account_label: call.accountLabel
	})
});
const saveWith = async (cardId, content, write) => {
	const sdk = getSDK();
	if (!sdk.stores.actionCards.get(cardId)) return silentFailure(cardId, "This action card is no longer available.");
	try {
		await write(cardId, content);
		return {
			...succeeded(cardId),
			contentSaved: true
		};
	} catch (error) {
		const code = bodyErrorCode(error);
		if (code === "card_changed") {
			await sdk.syncNow();
			return failed(cardId, "This card changed elsewhere — refreshing.");
		}
		if (code === "content_type_mismatch") return failed(cardId, "This card can no longer be edited this way.");
		return failed(cardId, errorMessage(error, "Failed to save changes."));
	}
};
const saveDraftUnlocked = (cardId, content) => saveWith(cardId, content, (id, c) => getSDK().stores.actionCards.updateContent(id, c));
const saveContentBeforeConfirm = (cardId, content) => saveWith(cardId, content, (id, c) => getSDK().stores.actionCards.updateContentNow(id, c));
const runMutation = async (cardId, command) => {
	if (actionCardMutationsStore.isPending(cardId)) return skipped(cardId, "This action is already being updated.");
	actionCardMutationsStore.markPending(cardId);
	try {
		return await command();
	} catch (error) {
		log.error("Action card command threw", {
			cardId,
			error: errorMessage(error, "unknown")
		});
		return failed(cardId, errorMessage(error, "This action could not be completed."));
	} finally {
		actionCardMutationsStore.unmarkPending(cardId);
	}
};
const complete = async (cardId, params) => {
	const sdk = getSDK();
	try {
		await sdk.stores.actionCards.complete(cardId, params);
		return params.succeeded ? succeeded(cardId) : failed(cardId, typeof params.result?.error === "string" ? params.result.error : "The action failed.");
	} catch (error) {
		const message = errorMessage(error, "Failed to record the result.");
		try {
			await sdk.syncNow();
		} catch {}
		return failed(cardId, message);
	}
};
const failComplete = (cardId, error) => complete(cardId, {
	succeeded: false,
	result: { error }
});
const executeBestEffort = async (cardId, call) => {
	try {
		if (!(await postToolsExecute(call)).successful) log.error("Could not remove superseded Gmail draft", { cardId });
	} catch {
		log.error("Could not remove superseded Gmail draft", { cardId });
	}
};
const executeToolCalls = async (cardId, calls) => {
	let response;
	for (const call of calls) {
		if (call.bestEffort) {
			await executeBestEffort(cardId, call);
			continue;
		}
		response = await postToolsExecute(call);
		if (!response.successful) break;
	}
	return response;
};
const runClaimed = async (cardId, kind, contentOverride, claimedCard) => {
	const card = getSDK().stores.actionCards.get(cardId) ?? claimedCard;
	const content = contentOverride ?? kind.decode(card.content);
	if (!content || content.type !== kind.actionType) return failComplete(cardId, "This action card has invalid content.");
	const resolved = resolveActionCard(content);
	const calls = resolved.execution?.buildCalls(card.externalRef) ?? [];
	let response;
	try {
		response = await executeToolCalls(cardId, calls);
	} catch (error) {
		log.error("Action card tool execution failed", {
			cardId,
			actionType: card.actionType,
			error: errorMessage(error, "unknown")
		});
		return failComplete(cardId, errorMessage(error, "The action failed."));
	}
	if (!response) {
		log.error("Action card produced no tool result", {
			cardId,
			actionType: card.actionType,
			callCount: calls.length
		});
		return failComplete(cardId, "The action did not produce a result.");
	}
	if (!response.successful) return failComplete(cardId, response.error ?? "The action failed.");
	const resultError = resolved.execution?.resultError(response.data);
	if (resultError) return failComplete(cardId, resultError);
	return complete(cardId, {
		succeeded: true,
		external_ref: resolved.execution?.buildExternalRef(response.data)
	});
};
const executeClaimed = async (cardId, kind, contentOverride, claimedCard) => {
	try {
		return await runClaimed(cardId, kind, contentOverride, claimedCard);
	} catch (error) {
		log.error("Action card send threw after claiming", {
			cardId,
			actionType: claimedCard.actionType,
			error: errorMessage(error, "unknown")
		});
		return failComplete(cardId, errorMessage(error, "The action failed."));
	}
};
const CARD_CHANGED_MESSAGE = "This card changed — refreshing.";
const confirmOnServer = async (card, endpoint, version) => {
	const sdk = getSDK();
	try {
		const response = await sdk.stores.actionCards.sendOnServer(endpoint, version);
		const latest = response.status === "running" ? await sdk.stores.actionCards.pollUntilRunEnds(response) : response;
		if (latest.status === "running") return silentFailure(card.id, "Still working on it. Check back soon.");
		if (latest.status === "failed") return failed(card.id, failureMessageOf(latest) ?? "Couldn't complete the action. Try again.");
		if (latest.status === "completed") return succeeded(card.id);
		sdk.syncNow().catch(() => void 0);
		return failed(card.id, CARD_CHANGED_MESSAGE);
	} catch (error) {
		const code = bodyErrorCode(error);
		log.error("Failed to run an action card on the server", {
			cardId: card.id,
			actionType: card.actionType,
			status: error instanceof HttpError ? error.status : void 0,
			code
		});
		sdk.syncNow().catch(() => void 0);
		if (code === "card_changed") return failed(card.id, CARD_CHANGED_MESSAGE);
		return failed(card.id, error instanceof HttpError && error.userErrorMessage || "Couldn't complete the action. Try again.");
	}
};
const claimAndExecute = async (cardId, kind, contentOverride, card) => {
	const sdk = getSDK();
	try {
		await sdk.stores.actionCards.claim(cardId);
	} catch (error) {
		const status = error instanceof HttpError ? error.status : void 0;
		if (status === 409) await sdk.syncNow();
		log.error("Failed to claim an action card", {
			cardId,
			actionType: card.actionType,
			status,
			error: errorMessage(error, "unknown")
		});
		return failed(cardId, status === 409 ? "This card changed — refreshing." : "Couldn't start the send — check your connection and try again.");
	}
	return executeClaimed(cardId, kind, contentOverride, card);
};
const confirmUnlocked = async (cardId, contentOverride, expectedSyncId) => {
	const sdk = getSDK();
	let contentSaved = false;
	const card = sdk.stores.actionCards.get(cardId);
	if (!card) return silentFailure(cardId, "This action card is no longer available.");
	const kind = getActionCardKind(card.actionType);
	if (!kind) return silentFailure(cardId, "This action card is not supported.");
	const content = contentOverride ?? kind.decode(card.content);
	if (!content || content.type !== kind.actionType) return silentFailure(cardId, "This action card has invalid content.");
	const seenSyncId = card.syncId;
	const { execution, serverExecution } = resolveActionCard(content);
	if (!execution && !serverExecution) return skipped(cardId, "This action is display-only.");
	if (contentOverride) {
		const saveOutcome = await saveContentBeforeConfirm(cardId, contentOverride);
		if (saveOutcome.status !== "succeeded") return saveOutcome;
		contentSaved = true;
	}
	const withSaveState = (outcome) => contentSaved ? {
		...outcome,
		contentSaved: true
	} : outcome;
	const validationError = serverExecution ? serverExecution.validate() : execution?.validate(card.externalRef);
	if (validationError) return withSaveState(failed(cardId, validationError));
	if (serverExecution) {
		const current = sdk.stores.actionCards.get(cardId) ?? card;
		const version = serverExecution.exactRevision ? { expectedSyncId: expectedSyncId ?? current.syncId } : { seenSyncId };
		return withSaveState(await confirmOnServer(current, serverExecution.endpoint(cardId), version));
	}
	return withSaveState(await claimAndExecute(cardId, kind, contentOverride, card));
};
const storeCommand = async (cardId, failure, call) => {
	const { actionCards } = getSDK().stores;
	const card = actionCards.get(cardId);
	if (!card) return silentFailure(cardId, "This action card is no longer available.");
	try {
		await call(actionCards);
		return succeeded(cardId);
	} catch (error) {
		log.error(failure.log, {
			cardId,
			actionType: card.actionType,
			error: errorMessage(error, "unknown")
		});
		return failed(cardId, errorMessage(error, failure.message));
	}
};
const dismissUnlocked = (cardId, dismissal) => storeCommand(cardId, {
	message: "Failed to dismiss this card.",
	log: "Failed to dismiss an action card"
}, (actionCards) => actionCards.dismiss(cardId, dismissal));
const restoreUnlocked = (cardId) => storeCommand(cardId, {
	message: "Couldn't bring this card back.",
	log: "Failed to restore an action card"
}, (actionCards) => actionCards.restore(cardId));
const recordDismissalReasonUnlocked = (cardId, dismissal) => storeCommand(cardId, {
	message: "Couldn't send your feedback.",
	log: "Failed to record a dismissal reason"
}, (actionCards) => actionCards.recordDismissalReason(cardId, dismissal));
const acknowledgeUnlocked = async (cardId) => {
	const sdk = getSDK();
	const card = sdk.stores.actionCards.get(cardId);
	if (!card) return silentFailure(cardId, "This action card is no longer available.");
	try {
		await sdk.stores.actionCards.acknowledge(cardId);
		return succeeded(cardId);
	} catch (error) {
		log.error("Failed to acknowledge an action card", {
			cardId,
			actionType: card.actionType,
			error: errorMessage(error, "unknown")
		});
		return failed(cardId, errorMessage(error, "Failed to mark this as read."));
	}
};
const completeExternallyUnlocked = async (cardId) => {
	const sdk = getSDK();
	try {
		await sdk.stores.actionCards.completeExternally(cardId);
		return succeeded(cardId);
	} catch (error) {
		return failed(cardId, errorMessage(error, "Failed to mark this action as done."));
	}
};
const keepWatchedUnlocked = async (cardId) => {
	try {
		await getSDK().stores.actionCards.keepWatched(cardId);
		return succeeded(cardId);
	} catch (error) {
		return failed(cardId, errorMessage(error, "Failed to keep this card."));
	}
};
const moveWatchedToWorkingUnlocked = async (cardId) => {
	try {
		await getSDK().stores.actionCards.moveWatchedToWorking(cardId);
		return succeeded(cardId);
	} catch (error) {
		return failed(cardId, errorMessage(error, "Failed to move this card to Working."));
	}
};
const notWorkingOnWatchedUnlocked = async (cardId) => {
	try {
		await getSDK().stores.actionCards.notWorkingOnWatched(cardId);
		return succeeded(cardId);
	} catch (error) {
		return failed(cardId, errorMessage(error, "Failed to move this card out of Working."));
	}
};
const stopWaitingUnlocked = async (cardId, dismiss) => {
	try {
		await getSDK().stores.actionCards.stopWaiting(cardId, dismiss);
		return succeeded(cardId);
	} catch (error) {
		return failed(cardId, errorMessage(error, "Failed to update this card."));
	}
};
const settleGroup = async (cardIds, command) => {
	return (await Promise.allSettled(cardIds.map(command))).map((settlement, index) => settlement.status === "fulfilled" ? settlement.value : failed(cardIds[index], errorMessage(settlement.reason, "The action failed.")));
};
const actionCardCommands = {
	saveDraft: (cardId, content) => runMutation(cardId, () => saveDraftUnlocked(cardId, content)),
	confirm: (cardId, content, expectedSyncId) => runMutation(cardId, () => confirmUnlocked(cardId, content, expectedSyncId)),
	dismiss: (cardId, dismissal) => runMutation(cardId, () => dismissUnlocked(cardId, dismissal)),
	acknowledge: (cardId) => runMutation(cardId, () => acknowledgeUnlocked(cardId)),
	completeExternally: (cardId) => runMutation(cardId, () => completeExternallyUnlocked(cardId)),
	keepWatched: (cardId) => runMutation(cardId, () => keepWatchedUnlocked(cardId)),
	moveWatchedToWorking: (cardId) => runMutation(cardId, () => moveWatchedToWorkingUnlocked(cardId)),
	notWorkingOnWatched: (cardId) => runMutation(cardId, () => notWorkingOnWatchedUnlocked(cardId)),
	stopWaiting: (cardId, dismiss) => runMutation(cardId, () => stopWaitingUnlocked(cardId, dismiss)),
	dismissGroup: (cardIds, dismissal) => settleGroup(cardIds, (cardId) => runMutation(cardId, () => dismissUnlocked(cardId, dismissal))),
	restoreGroup: (cardIds) => settleGroup(cardIds, (cardId) => runMutation(cardId, () => restoreUnlocked(cardId))),
	recordDismissalReason: (cardId, dismissal) => runMutation(cardId, () => recordDismissalReasonUnlocked(cardId, dismissal)),
	isInFlight: (cardId) => actionCardMutationsStore.isPending(cardId)
};
//#endregion
//#region src/composer-chips.ts
const ACTION_CARD_MENTION_KIND = "action-card";
const ACTION_CARD_COMPOSER_ASK = "Help me with this action card";
const TODO_CTA_MESSAGE = "Help me do this!";
const MENTION_GAP_MARK = "mentionGap";
const mentionTrailingSpace = {
	type: "text",
	text: " ",
	marks: [{ type: MENTION_GAP_MARK }]
};
const toDocument = (text) => {
	const content = [];
	text.split("\n").forEach((line, index) => {
		if (index > 0) content.push({ type: "hardBreak" });
		if (line) content.push({
			type: "text",
			text: line
		});
	});
	return {
		type: "doc",
		content: [{
			type: "paragraph",
			content
		}]
	};
};
const actionCardMention = (card, title) => ({
	id: card.id,
	kind: ACTION_CARD_MENTION_KIND,
	name: title,
	data: { localId: card.localId }
});
const actionCardComposerText = (title) => `@${title}\n\n${ACTION_CARD_COMPOSER_ASK}`;
/**
* A message about a card attached to it: the card's link, which the user's message draws as the card's chip and the
* agent reads as the card, then `prompt`
*/
const attachedCardMessage = (card, prompt) => {
	return `[${(actionCardChipTitle(card) ?? "Action card").replace(/[[\]]/g, "")}](/action-cards/${card.localId})\n\n${prompt}`;
};
const actionCardChipTitle = (card) => {
	if (card.actionType === "chat_prompt") {
		const prompt = ChatPromptContentSchema.safeParse(card.content);
		return prompt.success ? prompt.data.title : null;
	}
	const decoded = decodeActionCard(card);
	if (!decoded) return null;
	return decoded.summary || decoded.actionLabel;
};
const mentionChipNodes = (card, title) => [{
	type: "mention",
	attrs: { ...actionCardMention(card, title) }
}, mentionTrailingSpace];
const nodesOf = (document) => document?.content ?? [];
const isActionCardMention = (node) => node.type === "mention" && node.attrs?.kind === "action-card";
const holdsActionCardMention = (nodes, cardId) => nodes.some((node) => isActionCardMention(node) && (cardId === void 0 || node.attrs?.id === cardId) || (node.content ? holdsActionCardMention(node.content, cardId) : false));
const isInlineNode = (node) => node.type === "text" || node.type === "mention" || node.type === "hardBreak" || node.type === "entityReference" || node.type === "routineReference";
const nodeText = (node) => {
	if (node.type === "mention") return `@${String(node.attrs?.name ?? "")}`;
	if (node.type === "entityReference") return String(node.attrs?.raw ?? "");
	if (node.type === "routineReference") return `::routine{id=${String(node.attrs?.id)}}`;
	if (node.type === "hardBreak") return "\n";
	if (typeof node.text === "string") return node.text;
	const children = node.content ?? [];
	const separator = children.every(isInlineNode) ? "" : "\n";
	return children.map(nodeText).join(separator);
};
const documentText = (nodes) => nodes.map(nodeText).join("\n\n").trim();
const composerSeed = (nodes) => ({
	content: documentText(nodes).replace(/[ \t]+$/gm, ""),
	draftDocument: {
		type: "doc",
		content: nodes
	}
});
const keptNodes = (existing) => {
	const documentNodes = nodesOf(existing?.draftDocument);
	if (documentNodes.length > 0 && documentText(documentNodes)) return documentNodes;
	return existing?.content.trim() ? nodesOf(toDocument(existing.content)) : [];
};
const strippedNodes = (nodes) => {
	const kept = [];
	let droppedMention = false;
	let previousHeldChip = false;
	for (const node of nodes) {
		if (isActionCardMention(node)) {
			droppedMention = true;
			continue;
		}
		if (droppedMention && node.type === "text" && !(node.text ?? "").trim()) {
			droppedMention = false;
			continue;
		}
		droppedMention = false;
		if (!node.content?.length) {
			previousHeldChip = false;
			kept.push(node);
			continue;
		}
		const heldChip = holdsActionCardMention(node.content);
		const content = strippedNodes(node.content);
		if (content.length === 0) {
			previousHeldChip = heldChip;
			continue;
		}
		const rebuilt = {
			...node,
			content
		};
		if (previousHeldChip && nodeText(rebuilt).trim() === "Help me with this action card") {
			previousHeldChip = false;
			continue;
		}
		previousHeldChip = heldChip;
		kept.push(rebuilt);
	}
	return kept;
};
const isChipParagraph = (node) => node.type === "paragraph" && (node.content ?? []).some(isActionCardMention) && (node.content ?? []).every((child) => child.type === "mention" || child.type === "text" && !(child.text ?? "").trim());
const countActionCardChips = (existing) => {
	const count = (nodes) => nodes.reduce((total, node) => total + (isActionCardMention(node) ? 1 : 0) + (node.content ? count(node.content) : 0), 0);
	return count(keptNodes(existing));
};
const appendActionCardChip = (existing, card) => {
	const title = actionCardChipTitle(card);
	if (!title) return null;
	const nodes = keptNodes(existing);
	if (holdsActionCardMention(nodes, card.id)) return null;
	const chipNodes = mentionChipNodes(card, title);
	const [first, ...rest] = nodes;
	const withChip = first && isChipParagraph(first) ? [{
		...first,
		content: [...first.content ?? [], ...chipNodes]
	}, ...rest] : [{
		type: "paragraph",
		content: chipNodes
	}, ...nodes];
	return composerSeed(withChip);
};
const leadingChipNodes = (existing) => {
	const [first] = nodesOf(existing?.draftDocument);
	return first && isChipParagraph(first) ? first.content ?? [] : [];
};
const composerDraft = (chipNodes, body) => chipNodes.some(isActionCardMention) ? composerSeed([{
	type: "paragraph",
	content: chipNodes
}, ...nodesOf(toDocument(body))]) : {
	content: body,
	draftDocument: void 0
};
const composerChips = (existing) => leadingChipNodes(existing).filter(isActionCardMention).map((node) => ({
	id: String(node.attrs?.id),
	name: String(node.attrs?.name ?? "")
}));
const composerBody = (existing) => {
	const [first, ...rest] = nodesOf(existing?.draftDocument);
	if (!first || !isChipParagraph(first)) return existing?.content ?? "";
	return rest.map(nodeText).join("\n\n");
};
const withComposerBody = (existing, body) => composerDraft(leadingChipNodes(existing), body);
const removeComposerChip = (existing, cardId) => {
	const chipNodes = leadingChipNodes(existing);
	const isRemovedChip = (node) => !!node && isActionCardMention(node) && node.attrs?.id === cardId;
	const keptChipNodes = chipNodes.filter((node, index) => !isRemovedChip(node) && !(node.type === "text" && isRemovedChip(chipNodes[index - 1])));
	return composerDraft(keptChipNodes, composerBody(existing));
};
//#endregion
//#region src/dismissal/use-deferred-dismiss.ts
const isEditableEventTarget = (target) => target instanceof HTMLElement && target.closest("input, textarea, [contenteditable=\"true\"], [role=\"textbox\"]") !== null;
const DISMISS_UNDO_WINDOW_MS = 5e3;
const DISMISS_THANKS_FLASH_MS = 1400;
const NOT_RELEVANT_DISMISSAL = {
	reason: "not_relevant",
	note: null
};
const findStrip = (ctx, stripId) => ctx.stripsRef.current.find((strip) => strip.id === stripId);
const clearTimer = (ctx, stripId) => {
	const timer = ctx.timersRef.current.get(stripId);
	if (timer !== void 0) {
		clearTimeout(timer);
		ctx.timersRef.current.delete(stripId);
	}
};
const removeStrip = (ctx, stripId) => {
	clearTimer(ctx, stripId);
	ctx.remainingMsRef.current.delete(stripId);
	ctx.startedAtRef.current.delete(stripId);
	ctx.writesRef.current.delete(stripId);
	ctx.unreportedRef.current.delete(stripId);
	const remaining = ctx.stripsRef.current.filter((strip) => strip.id !== stripId);
	ctx.stripsRef.current = remaining;
	ctx.setStrips(() => remaining);
};
const patchStrip = (ctx, stripId, patch) => {
	const next = ctx.stripsRef.current.map((strip) => strip.id === stripId ? {
		...strip,
		...patch
	} : strip);
	ctx.stripsRef.current = next;
	ctx.setStrips(() => next);
};
const scheduleClose = (ctx, stripId, delayMs) => {
	clearTimer(ctx, stripId);
	ctx.remainingMsRef.current.set(stripId, delayMs);
	ctx.startedAtRef.current.set(stripId, Date.now());
	ctx.timersRef.current.set(stripId, setTimeout(() => {
		reportCommitted(ctx, stripId);
		removeStrip(ctx, stripId);
	}, delayMs));
};
const reportCommitted = (ctx, stripId, chosenReason) => {
	const strip = findStrip(ctx, stripId);
	const fallbackReason = ctx.unreportedRef.current.get(stripId);
	if (!strip || fallbackReason === void 0) return;
	ctx.unreportedRef.current.delete(stripId);
	ctx.onEvent?.({
		kind: "committed",
		targets: strip.targets,
		reason: chosenReason ?? fallbackReason
	});
};
const reportOutcomes = (ctx, outcomes) => {
	for (const outcome of outcomes) ctx.onOutcome?.(outcome);
};
const reportFailedDismissals = (ctx, targets, outcomes) => {
	const failedIds = new Set(outcomes.filter((outcome) => outcome.status === "failed" && !outcome.silent).map((outcome) => outcome.cardId));
	const failedTargets = targets.filter((target) => failedIds.has(target.cardId));
	if (failedTargets.length > 0) ctx.onEvent?.({
		kind: "failed",
		targets: failedTargets
	});
};
const remainingMsOf = (ctx, stripId) => {
	const windowMs = ctx.remainingMsRef.current.get(stripId) ?? 0;
	const startedAt = ctx.startedAtRef.current.get(stripId);
	if (startedAt === void 0) return windowMs;
	return Math.max(0, windowMs - (Date.now() - startedAt));
};
const pause = (ctx, stripId) => {
	const strip = findStrip(ctx, stripId);
	if (!strip || strip.stage === "thanks" || strip.held) return;
	ctx.remainingMsRef.current.set(stripId, remainingMsOf(ctx, stripId));
	ctx.startedAtRef.current.delete(stripId);
	clearTimer(ctx, stripId);
	patchStrip(ctx, stripId, { held: true });
};
const resume = (ctx, stripId) => {
	const strip = findStrip(ctx, stripId);
	if (!strip || strip.stage === "thanks" || !strip.held) return;
	patchStrip(ctx, stripId, { held: false });
	scheduleClose(ctx, stripId, ctx.remainingMsRef.current.get(stripId) ?? 0);
};
const questionFor = (ctx, targets, dismissal) => {
	const [target] = targets;
	if (!target || targets.length > 1 || dismissal?.reason) return null;
	return ctx.actionCardsRef.current.get(target.cardId)?.dismissalQuestion ?? null;
};
const isFinalDismissal = (target) => finalDismissalOf(target) !== void 0;
const isOpenCard = (ctx, cardId) => {
	const status = ctx.actionCardsRef.current.get(cardId)?.status;
	return status === "draft" || status === "failed";
};
const beginDismiss = (ctx, targets, dismissal) => {
	const alreadyOpen = new Set(ctx.stripsRef.current.flatMap((strip) => strip.targets.map((target) => target.cardId)));
	const liveTargets = targets.filter((target) => !alreadyOpen.has(target.cardId) && isOpenCard(ctx, target.cardId));
	const [firstTarget] = liveTargets;
	if (!firstTarget) return;
	const question = questionFor(ctx, liveTargets, dismissal);
	const stripId = firstTarget.cardId;
	const committed = actionCardCommands.dismissGroup(liveTargets.map((target) => target.cardId), dismissal).then((outcomes) => {
		reportOutcomes(ctx, outcomes);
		reportFailedDismissals(ctx, liveTargets, outcomes);
		if (outcomes.every((outcome) => outcome.status !== "succeeded")) removeStrip(ctx, stripId);
		return outcomes;
	});
	ctx.writesRef.current.set(stripId, committed);
	ctx.unreportedRef.current.set(stripId, dismissal?.reason ?? "none");
	const strip = {
		id: stripId,
		targets: liveTargets,
		createdAt: Date.now(),
		stage: "reason",
		held: false,
		question,
		undoable: !liveTargets.some(isFinalDismissal)
	};
	ctx.stripsRef.current = [...ctx.stripsRef.current, strip];
	ctx.setStrips(() => ctx.stripsRef.current);
	scheduleClose(ctx, stripId, DISMISS_UNDO_WINDOW_MS);
};
const undo = (ctx, stripId) => {
	const strip = findStrip(ctx, stripId);
	if (!strip?.undoable) return;
	const lastWrite = ctx.writesRef.current.get(stripId);
	removeStrip(ctx, stripId);
	ctx.onEvent?.({
		kind: "undone",
		targets: strip.targets
	});
	Promise.resolve(lastWrite).then(() => actionCardCommands.restoreGroup(strip.targets.map((target) => target.cardId))).then((outcomes) => reportOutcomes(ctx, outcomes));
};
const sendReason = (ctx, stripId, dismissal) => {
	const strip = findStrip(ctx, stripId);
	const [target] = strip?.targets ?? [];
	if (!strip?.question || !target || strip.stage === "thanks") return;
	const written = Promise.resolve(ctx.writesRef.current.get(stripId)).then(() => actionCardCommands.recordDismissalReason(target.cardId, dismissal)).then((outcome) => ctx.onOutcome?.(outcome));
	ctx.writesRef.current.set(stripId, written);
	reportCommitted(ctx, stripId, dismissal.reason ?? void 0);
	patchStrip(ctx, stripId, {
		stage: "thanks",
		held: false
	});
	scheduleClose(ctx, stripId, Math.max(DISMISS_THANKS_FLASH_MS, remainingMsOf(ctx, stripId)));
};
const setStage = (ctx, stripId, stage) => {
	if (findStrip(ctx, stripId)?.question) patchStrip(ctx, stripId, { stage });
};
const buildController = (strips, ctxRef) => ({
	strips,
	isDeferred: (cardId) => strips.some((strip) => strip.targets.some((target) => target.cardId === cardId)),
	stripForCard: (cardId) => strips.find((strip) => strip.targets.some((target) => target.cardId === cardId)),
	beginDismiss: (targets, dismissal) => beginDismiss(ctxRef.current, targets, dismissal),
	undo: (stripId) => undo(ctxRef.current, stripId),
	chooseReason: (stripId, reason) => sendReason(ctxRef.current, stripId, {
		reason,
		note: null
	}),
	openOther: (stripId) => setStage(ctxRef.current, stripId, "other"),
	closeOther: (stripId) => setStage(ctxRef.current, stripId, "reason"),
	submitOther: (stripId, note) => sendReason(ctxRef.current, stripId, {
		reason: "other",
		note: note.trim() || null
	}),
	pause: (stripId) => pause(ctxRef.current, stripId),
	resume: (stripId) => resume(ctxRef.current, stripId)
});
const useDeferredDismiss = (options) => {
	const { actionCards } = useStores();
	const [strips, setStrips] = useState([]);
	const stripsRef = useRef(strips);
	stripsRef.current = strips;
	const actionCardsRef = useRef(actionCards);
	actionCardsRef.current = actionCards;
	const timersRef = useRef(/* @__PURE__ */ new Map());
	const ctx = {
		stripsRef,
		actionCardsRef,
		timersRef,
		remainingMsRef: useRef(/* @__PURE__ */ new Map()),
		startedAtRef: useRef(/* @__PURE__ */ new Map()),
		writesRef: useRef(/* @__PURE__ */ new Map()),
		unreportedRef: useRef(/* @__PURE__ */ new Map()),
		setStrips,
		onEvent: options?.onEvent,
		onOutcome: options?.onOutcome
	};
	const ctxRef = useRef(ctx);
	ctxRef.current = ctx;
	useEffect(() => {
		const timers = timersRef.current;
		return () => {
			for (const timer of timers.values()) clearTimeout(timer);
			timers.clear();
			for (const strip of ctxRef.current.stripsRef.current) reportCommitted(ctxRef.current, strip.id);
		};
	}, []);
	return useMemo(() => buildController(strips, ctxRef), [strips]);
};
//#endregion
//#region src/statuses.ts
const DONE_CARD_STATUSES = /* @__PURE__ */ new Set(["completed", "completed_externally"]);
const WITHDRAWN_CARD_STATUSES = /* @__PURE__ */ new Set(["dismissed", "revoked"]);
//#endregion
//#region src/selection.ts
const HOME_ACTION_CARD_PAGE_SIZE = 5;
const CHANNEL_SOURCE_LABELS = {
	imessage: "From iMessage",
	whatsapp_business: "From WhatsApp",
	email: "From email"
};
const messagingChannelLabel = (card) => card.origin !== "routine" && card.sourceMessagingChannelType ? CHANNEL_SOURCE_LABELS[card.sourceMessagingChannelType] : void 0;
const isWaitingOnUser = (status) => status === "draft" || status === "failed";
/**
* When the card last arrived: when it was made, or when its snooze ended. Only a card still
* waiting on the user counts its snooze, so closed cards sort the way stored history sorts them.
*/
const actionCardArrivedAt = (card) => Math.max(card.createdAt.getTime(), isWaitingOnUser(card.status) ? card.snoozedUntil?.getTime() ?? 0 : 0);
const actionCardLastTouchedAt = (card) => {
	const arrivedAtMs = actionCardArrivedAt(card);
	if (!card.followUpRepliedAt) return arrivedAtMs;
	return Math.max(arrivedAtMs, new Date(card.followUpRepliedAt).getTime());
};
/**
* Whether the user snoozed this open card and it is not due back yet. A card that closed while
* snoozed shows where closed cards show.
*/
const isActionCardSnoozed = (card, nowMs) => isWaitingOnUser(card.status) && !!card.snoozedUntil && card.snoozedUntil.getTime() > nowMs;
/** The card as an open to-do: a draft to-do that is not snoozed at `now`, or null */
const openTodoOf = (card, now) => {
	if (card.status !== "draft" || isActionCardSnoozed(card, now.getTime())) return null;
	const decoded = decodeActionCard(card);
	if (!decoded || !isTodoActionCard(decoded.content)) return null;
	return {
		card,
		decoded,
		content: decoded.content,
		arrivedAt: new Date(actionCardArrivedAt(card))
	};
};
/** The time the earliest snoozed card is due back, or null when none is snoozed. */
const nextActionCardWakeAt = (cards, nowMs) => {
	const wakeTimes = cards.flatMap((card) => card.snoozedUntil && isActionCardSnoozed(card, nowMs) ? [card.snoozedUntil.getTime()] : []);
	return wakeTimes.length ? Math.min(...wakeTimes) : null;
};
const sortNewestFirst = (cards) => cards.map((card) => ({
	card,
	id: card.id,
	at: actionCardLastTouchedAt(card)
})).sort((left, right) => right.at - left.at || left.id.localeCompare(right.id)).map(({ card }) => card);
const MEETING_ASSIST_CARD_KINDS = [
	"info",
	"todo",
	"draft"
];
const meetingAssistCardKind = (card) => {
	if (!isInformationalActionType(card.actionType)) return "draft";
	return card.content.kind === "todo" ? "todo" : "info";
};
const isMeetingNotice = (card) => !!card.sourceMeetingId && meetingAssistCardKind(card) === "info";
const isVisibleActionCard = (card, isChatDeleted) => !card.deletedAt && !(card.chatId && isChatDeleted(card.chatId)) && !isMeetingNotice(card) && (card.origin === "chat" || card.origin === "routine" || card.origin === "user");
const isPendingActionCard = (card) => card.status === "draft" || card.status === "running" || card.status === "failed";
const selectPendingActionCards = (cards, isChatDeleted) => sortNewestFirst(cards.filter((card) => isVisibleActionCard(card, isChatDeleted) && isPendingActionCard(card)));
/**
* Whether a card clears the user's importance threshold. A card nobody rated always shows: most
* card sources never rate, and the threshold only filters the ones that do.
*/
const clearsImportanceThreshold = (card, minImportance) => card.importance === null || card.importance >= minImportance;
const homeListOf = (card, { isInProgress, isDismissed }) => {
	const dismissed = isDismissed(card);
	if (!dismissed && card.status !== "revoked" && isInProgress(card)) return "inProgress";
	if (isPendingActionCard(card)) return "pending";
	if (dismissed) return "dismissed";
	if (card.status === "revoked") return "withdrawn";
	return DONE_CARD_STATUSES.has(card.status) ? "done" : void 0;
};
const filteredHomeListOf = (card, options) => {
	if (!isVisibleActionCard(card, options.isChatDeleted)) return null;
	const list = homeListOf(card, options);
	return (list === "inProgress" || list === "pending") && !clearsImportanceThreshold(card, options.minImportance) ? null : list;
};
/**
* Whether the partition's filters let a card through: the user may see it, and it clears the
* importance threshold if it waits on the user or is in progress. Subtasks shown under a parent
* pass the same test, so a subtask shows wherever it would show on its own.
*/
const passesHomeCardFilters = (card, options) => filteredHomeListOf(card, options) !== null;
const partitionHomeActionCards = (cards, options) => {
	const lists = {
		inProgress: [],
		pending: [],
		dismissed: [],
		done: [],
		withdrawn: []
	};
	for (const card of cards) {
		const list = filteredHomeListOf(card, options);
		if (list) lists[list].push(card);
	}
	return {
		inProgress: sortNewestFirst(lists.inProgress),
		pending: sortNewestFirst(lists.pending),
		dismissed: sortNewestFirst(lists.dismissed),
		done: sortNewestFirst(lists.done),
		withdrawn: sortNewestFirst(lists.withdrawn)
	};
};
const OPEN_ASSIST_CARD_STATUSES = /* @__PURE__ */ new Set([
	"draft",
	"running",
	"failed"
]);
/**
* One meeting's cards, open ones first, each list newest first. The importance
* threshold does not apply, because the user opened this meeting to see all of
* its cards. Dismissed cards go with the done ones, and each shows as a
* single row with Restore. Revoked cards are left out, since the agent withdrew
* them.
*/
const partitionMeetingAssistCards = (cards) => {
	const pending = [];
	const resolved = [];
	for (const card of cards) {
		if (card.deletedAt) continue;
		if (OPEN_ASSIST_CARD_STATUSES.has(card.status)) pending.push(card);
		else if (DONE_CARD_STATUSES.has(card.status) || card.status === "dismissed") resolved.push(card);
	}
	return {
		pending: sortNewestFirst(pending),
		resolved: sortNewestFirst(resolved)
	};
};
/**
* How many of the meeting's cards still wait for the user to send, mark done,
* or dismiss them. A card leaves the count while it sends and comes back if
* the send fails. Only cards that clear the importance threshold count.
*/
const countWaitingAssistCards = (cards, minImportance) => cards.filter((card) => !card.deletedAt && (card.status === "draft" || card.status === "failed") && clearsImportanceThreshold(card, minImportance)).length;
/**
* The card that pops up next beside the collapsed meeting widget: the oldest draft the rules let through that has
* not popped up before. A card older than the age limit stays in the Assist list only, because the moment in the
* meeting it was made for has passed
*/
const nextMeetingAssistPopupCard = (cards, rules, poppedCardIds, now) => {
	const oldestCreatedAtMs = now.getTime() - rules.maxCardAgeSeconds * 1e3;
	return cards.filter((card) => !card.deletedAt && card.status === "draft" && !poppedCardIds.has(card.id) && card.createdAt.getTime() >= oldestCreatedAtMs && rules.kinds.includes(meetingAssistCardKind(card)) && clearsImportanceThreshold(card, rules.minImportance)).sort((left, right) => left.createdAt.getTime() - right.createdAt.getTime()).at(0);
};
/** The heading and text of a Meeting Assist card in its popup: an answer's question and answer, or a draft's action and summary */
const meetingAssistPopupText = (card) => {
	const decoded = decodeActionCard(card);
	if (!decoded) return null;
	if (decoded.content.type === "informational") return {
		title: decoded.content.title,
		body: decoded.content.body
	};
	return {
		title: decoded.actionLabel,
		body: decoded.summary
	};
};
const selectVisiblePendingActionCards = (entries, retainedCardIds, visibleLimit, expandedCardIds) => {
	if (!Number.isFinite(visibleLimit) || visibleLimit >= entries.length) return [...entries];
	const visibleEntries = entries.slice(0, visibleLimit);
	const visibleCardIds = new Set(visibleEntries.map(({ card }) => card.id));
	const keptEntries = entries.filter(({ card }) => !visibleCardIds.has(card.id) && (expandedCardIds.has(card.id) || retainedCardIds.has(card.id)));
	return [...visibleEntries, ...keptEntries];
};
const getPendingActionCardTurnAnchorId = (card, threadMessages) => {
	let turnEndIndex = card.assistantMessageId ? threadMessages.findIndex((message) => message.id === card.assistantMessageId) : -1;
	if (turnEndIndex === -1) {
		for (let index = threadMessages.length - 1; index >= 0; index -= 1) if (threadMessages[index].createdAt <= card.createdAt) {
			turnEndIndex = index;
			break;
		}
	}
	for (let index = turnEndIndex; index >= 0; index -= 1) {
		const message = threadMessages[index];
		if (message && opensTurn(message)) return message.id;
	}
	return card.assistantMessageId ?? void 0;
};
/**
* Bounds for the cards recovered by chat id below: the turn's leading user message
* up to the next one, either end open when the turn opens or ends the thread.
*/
const getTurnWindow = (threadMessages, ownIndex) => {
	if (ownIndex === -1) return {
		startAt: null,
		endAtMs: Number.NEGATIVE_INFINITY
	};
	let startAt = null;
	for (let index = ownIndex - 1; index >= 0; index -= 1) {
		const message = threadMessages[index];
		if (message && opensTurn(message)) {
			startAt = message.createdAt;
			break;
		}
	}
	let endAtMs = Number.POSITIVE_INFINITY;
	for (let index = ownIndex + 1; index < threadMessages.length; index += 1) {
		const message = threadMessages[index];
		if (message && opensTurn(message)) {
			endAtMs = message.createdAt.getTime();
			break;
		}
	}
	return {
		startAt,
		endAtMs
	};
};
/**
* The cards a turn drafted that its reply never placed, to draw beneath it.
*
* A background helper's cards sync with this chat's id but anchor to an assistant
* message inside the helper's own hidden chat, so the turn walk never finds them;
* they are recovered by chat id, scoped to the turn's window so each turn keeps
* its own. `threadMessages` must be chronological.
*/
const selectTurnActionCards = ({ chatId, assistantMessageId, threadMessages, cards, includeLinkedCards = false }) => {
	const ownIndex = threadMessages.findIndex((message) => message.id === assistantMessageId);
	const turnMessageIds = [];
	const turnText = [];
	for (let index = ownIndex; index >= 0; index -= 1) {
		const message = threadMessages[index];
		if (!message || opensTurn(message)) break;
		if (message.role === "assistant") {
			turnMessageIds.unshift(message.id);
			turnText.push(message.content);
		}
	}
	if (ownIndex === -1) turnMessageIds.push(assistantMessageId);
	const anchoredCards = turnMessageIds.flatMap((id) => [...cards.getByAssistantMessageId(id)]);
	const anchoredCardIds = new Set(anchoredCards.map((card) => card.id));
	const threadMessageIds = new Set(threadMessages.map((message) => message.id));
	const { startAt, endAtMs } = getTurnWindow(threadMessages, ownIndex);
	const helperCards = cards.getByChatId(chatId).filter((card) => {
		if (anchoredCardIds.has(card.id)) return false;
		if (!card.assistantMessageId) return false;
		if (threadMessageIds.has(card.assistantMessageId)) return false;
		const createdAtMs = card.createdAt.getTime();
		if (startAt !== null && createdAtMs < startAt.getTime()) return false;
		return createdAtMs < endAtMs;
	}).sort((left, right) => left.createdAt.getTime() - right.createdAt.getTime());
	const linkedLocalIds = findLinkedActionCardIds(turnText.join("\n"));
	const seenCardIds = /* @__PURE__ */ new Set();
	return [...anchoredCards, ...helperCards].filter((card) => {
		if (card.deletedAt) return false;
		if (!includeLinkedCards && linkedLocalIds.has(card.localId)) return false;
		if (narrowCardContent(card.actionType, card.content) === null) return false;
		if (seenCardIds.has(card.id)) return false;
		seenCardIds.add(card.id);
		return true;
	});
};
//#endregion
//#region src/hubspot-fixtures.ts
const contact = {
	contact_id: 101,
	contact_name: "Jane Doe",
	contact_email: "jane@acme.com",
	contact_url: "https://app.hubspot.com/contacts/1234567/record/0-1/101",
	account_label: null
};
const DEAL_URL = "https://app.hubspot.com/contacts/1234567/record/0-3/55";
const OWNER_NAMES = { hubspot_owner_id: { "77": "Pat Kim" } };
const HUBSPOT_ACTION_CARD_FIXTURES = [
	{
		label: "HubSpot note",
		note: "Log a call summary on a contact.",
		context: "Jane asked for the renewal quote on today’s call.",
		content: HubSpotContactNoteContentSchema.parse({
			...contact,
			body: "Called about the renewal. She wants the quote by Friday."
		}),
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-1/101",
		failureMessage: "HubSpot could not add the note. Please try again."
	},
	{
		label: "HubSpot contact update",
		note: "Move a contact to a new lifecycle stage and lead status.",
		context: "Jane booked a demo, so she is now sales qualified.",
		content: HubSpotContactUpdateContentSchema.parse({
			...contact,
			lifecyclestage: "salesqualifiedlead",
			hs_lead_status: "IN_PROGRESS",
			jobtitle: "VP Finance",
			previous_lifecyclestage: "lead",
			previous_hs_lead_status: null,
			previous_jobtitle: "Finance Director",
			value_labels: {
				lifecyclestage: {
					lead: "Lead",
					salesqualifiedlead: "Sales Qualified Lead"
				},
				hs_lead_status: { IN_PROGRESS: "In Progress" }
			}
		}),
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-1/101",
		failureMessage: "HubSpot could not update the contact."
	},
	{
		label: "HubSpot deal",
		note: "Open a deal for a contact.",
		context: "Jane confirmed budget for the renewal.",
		content: HubSpotDealContentSchema.parse({
			...contact,
			dealname: "Acme renewal 2027",
			dealstage: "appointmentscheduled",
			amount: "5000",
			closedate: "2026-12-31",
			pipeline: "default",
			hubspot_owner_id: "77",
			value_labels: {
				...OWNER_NAMES,
				pipeline: { default: "Sales Pipeline" },
				dealstage: { appointmentscheduled: "Discovery Booked" }
			}
		}),
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-3/55",
		failureMessage: "HubSpot could not create the deal."
	},
	{
		label: "HubSpot company",
		note: "Create a company, flagging a possible duplicate.",
		context: "Two contacts from Acme are not linked to a company yet.",
		content: {
			type: "hubspot_company",
			name: "Acme",
			domain: "acme.com",
			contact_ids: [101, 102],
			contact_labels: ["Jane Doe · jane@acme.com", "sam@acme.com"],
			possible_duplicate: "Acme Inc (acme.com)",
			account_label: null
		},
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-2/9",
		failureMessage: "HubSpot could not create the company."
	},
	{
		label: "HubSpot call",
		note: "Log a call with its outcome on a contact.",
		context: "Jane picked up and agreed to a pricing review.",
		content: HubSpotContactNoteContentSchema.parse({
			...contact,
			activity_type: "call",
			body: "Agreed to review pricing next week.",
			title: "Renewal call",
			timestamp: "2026-10-01T15:00:00Z",
			call_outcome: "f240bbac-87c9-4f6e-bf70-924b57d47db7",
			value_labels: { call_outcome: { "f240bbac-87c9-4f6e-bf70-924b57d47db7": "Connected" } }
		}),
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-48/61",
		failureMessage: "HubSpot could not log the call."
	},
	{
		label: "HubSpot contact",
		note: "Create a contact, flagging a possible duplicate.",
		context: "Sam from Acme joined the renewal thread.",
		content: HubSpotContactCreateContentSchema.parse({
			email: "sam@acme.com",
			firstname: "Sam",
			lastname: "Lee",
			company: "Acme",
			company_id: 9,
			company_name: "Acme Inc",
			possible_duplicate: "Sam Lee · sam@acme.com"
		}),
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-1/102",
		failureMessage: "HubSpot could not create the contact."
	},
	{
		label: "HubSpot deal update",
		note: "Move a deal to a new stage.",
		context: "Jane signed the renewal.",
		content: HubSpotDealUpdateContentSchema.parse({
			deal_id: 55,
			deal_name: "Acme renewal 2027",
			deal_url: DEAL_URL,
			dealstage: "closedwon",
			amount: "7000",
			previous_dealstage: "appointmentscheduled",
			previous_amount: "5000",
			value_labels: { dealstage: {
				appointmentscheduled: "Discovery Booked",
				closedwon: "Closed Won"
			} }
		}),
		externalUrl: DEAL_URL,
		failureMessage: "HubSpot could not update the deal."
	},
	{
		label: "HubSpot task",
		note: "Create a follow-up task on a deal.",
		context: "Jane asked for the quote by Friday.",
		content: HubSpotTaskContentSchema.parse({
			target_type: "deals",
			target_id: 55,
			target_name: "Acme renewal 2027",
			target_url: DEAL_URL,
			hs_task_subject: "Send the renewal quote",
			hs_task_body: "Include the volume discount.",
			hs_timestamp: "2026-10-03T17:00:00Z",
			hubspot_owner_id: "77",
			hs_task_priority: "HIGH",
			value_labels: {
				...OWNER_NAMES,
				hs_task_priority: { HIGH: "High" }
			}
		}),
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-27/66",
		failureMessage: "HubSpot could not create the task."
	},
	{
		label: "HubSpot task update",
		note: "Complete a task.",
		context: "The quote went out this morning.",
		content: HubSpotTaskUpdateContentSchema.parse({
			task_id: 66,
			task_subject: "Send the renewal quote",
			task_url: "https://app.hubspot.com/contacts/1234567/record/0-27/66",
			hs_task_status: "COMPLETED",
			previous_hs_task_status: "NOT_STARTED",
			value_labels: { hs_task_status: {
				COMPLETED: "Completed",
				NOT_STARTED: "Not Started"
			} }
		}),
		externalUrl: "https://app.hubspot.com/contacts/1234567/record/0-27/66",
		failureMessage: "HubSpot could not update the task."
	}
];
//#endregion
//#region src/issue-fixtures.ts
const ISSUE_ACTION_CARD_FIXTURES = [
	{
		label: "GitHub PR approval",
		note: "Approve the reviewed pull request with an optional comment.",
		context: "The calendar fix has been reviewed and is ready for approval.",
		content: {
			type: "github_pull_request_approve",
			owner: "acme",
			repo: "desktop",
			pull_number: 43,
			title: "Fix duplicate calendar reminders",
			head: "fix/reminders",
			expected_head_sha: "abc123",
			body: "",
			account_label: null
		},
		externalUrl: "https://github.com/acme/desktop/pull/43#pullrequestreview-1",
		failureMessage: "GitHub could not submit the approval."
	},
	...[[
		"review_comment",
		"Review comment",
		"Post a review comment."
	], [
		"request_changes",
		"Request changes",
		"Request changes before merging."
	]].map(([operation, label, note]) => ({
		label: `GitHub PR ${label}`,
		note,
		context: "The calendar fix needs a pull request review.",
		content: {
			type: `github_pull_request_${operation}`,
			owner: "acme",
			repo: "desktop",
			pull_number: 43,
			title: "Fix duplicate calendar reminders",
			head: "fix/reminders",
			expected_head_sha: "abc123",
			body: operation === "review_comment" ? "Please add coverage for recurring events." : "The recurring event case still creates duplicate reminders.",
			account_label: null
		},
		externalUrl: "https://github.com/acme/desktop/pull/43#pullrequestreview-1",
		failureMessage: "GitHub could not submit the review."
	})),
	...[
		"update",
		"close",
		"merge"
	].map((operation) => ({
		label: `GitHub PR ${operation}`,
		note: "Review the proposed change to an existing pull request.",
		context: "The calendar reminder fix has been reviewed.",
		content: operation === "update" ? {
			type: "github_pull_request_update",
			owner: "acme",
			repo: "desktop",
			pull_number: 43,
			title: "Fix duplicate calendar reminders",
			body: "## Changes\nKeep one reminder per event.",
			account_label: null
		} : operation === "close" ? {
			type: "github_pull_request_close",
			owner: "acme",
			repo: "desktop",
			pull_number: 43,
			title: "Fix duplicate calendar reminders",
			account_label: null
		} : {
			type: "github_pull_request_merge",
			owner: "acme",
			repo: "desktop",
			pull_number: 43,
			title: "Fix duplicate calendar reminders",
			account_label: null,
			head: "fix/reminders",
			base: "main",
			expected_head_sha: "abc123",
			merge_method: "squash",
			commit_title: null,
			commit_message: null
		},
		externalUrl: "https://github.com/acme/desktop/pull/43",
		failureMessage: "GitHub could not update the pull request."
	})),
	{
		label: "GitHub comment",
		note: "A reply on an existing issue or pull request.",
		context: "Sam asked whether the fix also covers recurring events.",
		content: {
			type: "github_comment",
			owner: "acme",
			repo: "desktop",
			issue_number: 42,
			body: "Yes, the fix covers recurring events. I added a regression test for the reconnect flow.",
			account_label: null
		},
		externalUrl: "https://github.com/acme/desktop/issues/42#issuecomment-1",
		failureMessage: "GitHub could not post the comment. Please try again."
	},
	{
		label: "GitHub pull request",
		note: "A draft pull request with its destination and reviewers visible.",
		context: "The calendar reconnect fix is ready for an initial review.",
		content: {
			type: "github_pull_request",
			owner: "acme",
			repo: "desktop",
			title: "Deduplicate reminders after calendar reconnect",
			body: "## Changes\nKeep one reminder per event when a calendar reconnects.\n\n## Testing\nRegression tests cover recurring and single events.",
			head: "fix/calendar-reminders",
			base: "main",
			draft: true,
			maintainer_can_modify: true,
			reviewers: ["sam"],
			account_label: null
		},
		externalUrl: "https://github.com/acme/desktop/pull/43",
		failureMessage: "GitHub could not create the pull request. Please try again."
	},
	{
		label: "Linear issue",
		note: "A new issue with readable team and assignment details.",
		context: "Turn the calendar reminder report into a task for the desktop team.",
		content: {
			type: "linear_issue",
			team: "Desktop",
			team_name: "Desktop",
			title: "Fix duplicate reminders after reconnecting Calendar",
			description: "Reminders should appear once per event, including recurring events.",
			state: "Todo",
			state_name: "Todo",
			assignee: "maya@acme.test",
			assignee_name: "Maya",
			priority: 2,
			account_label: null,
			attachments: [{
				url: "https://picsum.photos/seed/lb-des-42-reminders/960/600",
				title: "Duplicate reminders in the sidebar",
				fallback_url: "https://picsum.photos/seed/lb-des-42-reminders/1920/1200"
			}, {
				url: "https://picsum.photos/seed/lb-des-42-calendar/720/960",
				title: "Calendar settings after reconnecting"
			}]
		},
		externalUrl: "https://linear.app/acme/issue/DES-42",
		failureMessage: "Linear could not create the issue. Please try again."
	},
	{
		label: "Linear issue update",
		note: "Only the requested changes appear; omitted fields stay unchanged.",
		context: "The reminder fix passed review and is ready for the next release.",
		content: {
			type: "linear_issue_update",
			issue_id: "DES-42",
			issue_label: "DES-42 · Fix duplicate calendar reminders",
			issue_url: "https://linear.app/acme/issue/DES-42",
			title: null,
			description: null,
			state: "Done",
			state_name: "Done",
			previous_status: "In Review",
			assignee: null,
			assignee_name: null,
			assignee_set: false,
			priority: null,
			account_label: null
		},
		externalUrl: "https://linear.app/acme/issue/DES-42",
		failureMessage: "Linear could not update the issue. Please try again."
	},
	{
		label: "Linear comment",
		note: "A concise progress update on an existing issue.",
		context: "Maya asked for a release update on DES-42.",
		content: {
			type: "linear_comment",
			issue_id: "DES-42",
			issue_label: "DES-42 · Fix duplicate calendar reminders",
			issue_url: "https://linear.app/acme/issue/DES-42",
			body: "The fix is ready for the next desktop release. Regression tests are passing.",
			account_label: null,
			attachments: [{
				url: "https://picsum.photos/seed/lb-des-42-passing/960/540",
				title: "Regression run, all green"
			}]
		},
		externalUrl: "https://linear.app/acme/issue/DES-42",
		failureMessage: "Linear could not post the comment. Please try again."
	}
];
//#endregion
//#region src/fixtures.ts
const ACTION_CARD_STATES = [
	"draft",
	"editing",
	"running",
	"completed",
	"failed",
	"dismissed",
	"revoked"
];
const ACTION_CARD_FIXTURES = [
	...ISSUE_ACTION_CARD_FIXTURES,
	...HUBSPOT_ACTION_CARD_FIXTURES,
	{
		label: "Ask user",
		note: "A question that needs an answer before Littlebird can continue.",
		content: {
			type: "ask_user",
			request_id: "request-1",
			questions: [{
				id: "audience",
				prompt: "Who should receive the project update?",
				options: ["Project team", "All stakeholders"],
				allow_multiple: false,
				allow_free_text: true
			}]
		},
		externalUrl: "",
		failureMessage: "Your answer could not be sent."
	},
	{
		label: "Tool approval",
		note: "A tool call held for the user to approve or deny.",
		content: {
			type: "tool_approval",
			integration_name: "gmail",
			tool_slug: "GMAIL_CREATE_LABEL",
			arguments: { name: "Follow up" },
			app_name: "Gmail",
			tool_name: "Create label",
			icon: "gmail",
			account_label: null,
			argument_defaults: {}
		},
		externalUrl: "",
		failureMessage: "The tool call could not run."
	},
	{
		label: "Gmail email",
		note: "A concise follow-up prepared from the inbox triage routine.",
		provenance: {
			sourceLabel: "Inbox triage",
			timestampLabel: "4:00 PM"
		},
		context: "Reply to Marc (Acme) who wrote this morning: “Just checking whether invoice #218 got approved — we’d like to close the books Friday.”",
		content: {
			type: "gmail_email_draft",
			to: ["marc@acme.co"],
			cc: [],
			bcc: [],
			subject: "Re: Invoice #218",
			body: [
				"Hi Marc — invoice #218 is approved on our side. Finance releases payment Friday, and the receipt will follow from books@littlebird.ai.",
				"",
				"Thanks for the nudge."
			].join("\n"),
			is_html: false,
			account_label: "alex@littlebird.ai"
		},
		externalUrl: "https://mail.google.com/",
		failureMessage: "Gmail could not send this draft. Check the connection and retry."
	},
	{
		label: "Gmail email (HTML body)",
		note: "A formatted draft that arrived as HTML, with a bold lede and a list.",
		provenance: {
			sourceLabel: "Inbox triage",
			timestampLabel: "9:20 AM"
		},
		content: {
			type: "gmail_email_draft",
			to: ["marc@acme.co"],
			cc: [],
			bcc: [],
			subject: "Renewal next steps",
			body: "<p><strong>Thanks for the call today.</strong> Next steps:</p><ul><li>Send updated pricing</li><li>Loop in legal for redlines</li><li>Confirm the Friday deadline</li></ul><p>Let me know if anything is missing.</p>",
			is_html: true,
			account_label: "alex@littlebird.ai"
		},
		externalUrl: "https://mail.google.com/",
		failureMessage: "Gmail could not send this draft. Check the connection and retry."
	},
	{
		label: "Gmail email (attachment)",
		note: "A draft that carries a file the routine produced, shown by name.",
		provenance: {
			sourceLabel: "Weekly report",
			timestampLabel: "8:00 AM"
		},
		context: "Sends the weekly usage report Priya asked for on Monday, with the PDF attached.",
		content: {
			type: "gmail_email_draft_with_attachments",
			to: ["priya@acme.co"],
			cc: [],
			bcc: [],
			subject: "Weekly usage report",
			body: [
				"Hi Priya — this week’s usage report is attached. Active seats are up 6%; the two flagged accounts are on page 3.",
				"",
				"Shout if you want the raw export."
			].join("\n"),
			is_html: false,
			account_label: "alex@littlebird.ai",
			attachment: {
				file_id: "file_01j9x4k7v2m3n8p6q5r4s3t2u1",
				filename: "usage-report-2026-09-14.pdf",
				mimetype: "application/pdf",
				size: 248113
			}
		},
		externalUrl: "https://mail.google.com/",
		failureMessage: "Gmail could not send this draft. Check the connection and retry."
	},
	{
		label: "Gmail noise archive",
		note: "Two promotional emails selected for archiving from the inbox.",
		provenance: {
			sourceLabel: "Inbox cleanup",
			timestampLabel: "10:15 AM"
		},
		context: "These messages are recurring promotions rather than receipts or account notices.",
		content: {
			type: "gmail_noise_archive",
			account_id: "b3d516c3-ef4f-4e0c-a864-2548847b6ed0",
			account_label: "tushar@littlebird.ai",
			items: [{
				source_id: "36efdf80-9497-44b8-82ad-6ebdb71aa651",
				sender: "offers@acme.co",
				subject: "The Acme summer sale ends tonight",
				received_at: "2026-09-14T08:12:00Z",
				group: "Promotions & newsletters"
			}, {
				source_id: "16bdb712-745a-4fb3-8b3a-f59f579c41cb",
				sender: "digest@designweekly.co",
				subject: "This week in product design",
				received_at: "2026-09-14T07:30:00Z",
				group: "Promotions & newsletters"
			}],
			selected_source_ids: ["36efdf80-9497-44b8-82ad-6ebdb71aa651", "16bdb712-745a-4fb3-8b3a-f59f579c41cb"],
			choice: "once",
			result_summary: ""
		},
		externalUrl: "https://mail.google.com/",
		failureMessage: "Gmail could not archive these messages. Check the connection and retry."
	},
	{
		label: "Gmail label review",
		note: "Suggested purchase labels for two order confirmations.",
		provenance: {
			sourceLabel: "Receipt sorting",
			timestampLabel: "10:28 AM"
		},
		context: "Both messages are order confirmations that should be easy to find with the Purchases label.",
		content: {
			type: "gmail_label_review",
			account_id: "b3d516c3-ef4f-4e0c-a864-2548847b6ed0",
			account_label: "tushar@littlebird.ai",
			items: [{
				source_id: "d0fd78c1-5565-4586-9ec0-4a1cc1f3627b",
				sender: "orders@acme.co",
				subject: "Order confirmation #A-218",
				received_at: "2026-09-13T16:42:00Z",
				category: "orders"
			}, {
				source_id: "b5b5f8cc-ef13-471b-a0e6-25b9aa2d2cf8",
				sender: "receipts@trailhead.co",
				subject: "Your Trailhead order is confirmed",
				received_at: "2026-09-12T11:06:00Z",
				category: "orders"
			}],
			selected_source_ids: ["d0fd78c1-5565-4586-9ec0-4a1cc1f3627b", "b5b5f8cc-ef13-471b-a0e6-25b9aa2d2cf8"],
			choice: "once",
			result_summary: ""
		},
		externalUrl: "https://mail.google.com/",
		failureMessage: "Gmail could not apply the label. Check the connection and retry."
	},
	{
		label: "Outlook email",
		note: "A longer project update that exercises wrapping and scroll behavior.",
		provenance: {
			sourceLabel: "Weekly project review",
			timestampLabel: "11:42 AM"
		},
		context: "Priya asked in Thursday’s review for a written update before the steering call: the Q3 board readout slipped a week after the data-warehouse migration ran long, two of the four workstreams are still blocked on the finance export, and leadership wants to know whether the 14 Oct customer summit is still realistic before they commit the venue deposit.",
		content: {
			type: "outlook_email_draft",
			to: ["thea@acme.co"],
			cc: ["jules@acme.co"],
			bcc: [],
			subject: "Q3 brief timing and final metrics review",
			body: [
				"Hi Thea,",
				"",
				"Monday works. Could you pull Jules in on the metrics section so we are aligned before review?",
				"",
				"I also added the latest activation figures and linked the source workbook. The remaining open question is whether we show the regional split in the main brief or keep it in the appendix.",
				"",
				"Thanks,",
				"Alex"
			].join("\n"),
			is_html: false,
			account_label: "alex@acme.co"
		},
		externalUrl: "https://outlook.office.com/mail/",
		failureMessage: "Outlook rejected the draft because the account needs reconnecting."
	},
	{
		label: "Google Calendar event",
		note: "An event with guests, location, and a short agenda.",
		provenance: {
			sourceLabel: "Morning briefing",
			timestampLabel: "7:00 AM"
		},
		content: {
			type: "google_calendar_event",
			title: "Q3 brief review",
			start: "2026-08-20T16:00:00-07:00",
			end: "2026-08-20T17:00:00-07:00",
			timezone: "America/Los_Angeles",
			attendees: ["thea@acme.co", "jules@acme.co"],
			location: "Zoom",
			description: "Review the final metrics, owner notes, and launch timing before the brief goes to leadership.",
			account_label: "alex@littlebird.ai",
			create_meeting_room: true,
			send_updates: true
		},
		externalUrl: "https://calendar.google.com/",
		failureMessage: "The event could not be created because the calendar is unavailable.",
		previousFieldValues: { when: "Thu, Aug 20 · 4:00–4:30 PM PDT" }
	},
	{
		label: "Google Calendar event (HTML agenda)",
		note: "An event whose description is agenda-style HTML from the invite source.",
		provenance: {
			sourceLabel: "Calendar invite",
			timestampLabel: "8:15 AM"
		},
		content: {
			type: "google_calendar_event",
			title: "Renewal call with Acme",
			start: "2026-08-21T10:00:00-07:00",
			end: "2026-08-21T10:30:00-07:00",
			timezone: "America/Los_Angeles",
			attendees: ["thea@acme.co"],
			location: "",
			description: "<b>Agenda</b><br>1. Contract renewal terms<br>2. Usage review<br>3. Open questions",
			account_label: "alex@littlebird.ai",
			create_meeting_room: null,
			send_updates: null
		},
		externalUrl: "https://calendar.google.com/",
		failureMessage: "The event could not be created because the calendar is unavailable."
	},
	{
		label: "Google Calendar RSVP",
		note: "An unanswered invite, with the reply the agent suggested.",
		provenance: {
			sourceLabel: "Morning sweep",
			timestampLabel: "7:00 AM"
		},
		context: "Thea invited you to the design review on Thursday and has not had a reply.",
		content: {
			type: "google_calendar_rsvp",
			event_id: "4kq2v8rj0t1m6s3c9b5n7d2f1a",
			response: "accepted",
			title: "Design review",
			start: "2026-08-20T14:00:00-07:00",
			end: "2026-08-20T15:00:00-07:00",
			timezone: "America/Los_Angeles",
			organizer: "Thea Lane",
			account_label: "alex@littlebird.ai"
		},
		externalUrl: "https://calendar.google.com/",
		failureMessage: "Google Calendar could not send your reply. Check the connection and retry."
	},
	{
		label: "Google Calendar event update",
		note: "A meeting the agent suggests moving to clear a clash, with a new title.",
		provenance: {
			sourceLabel: "Morning sweep",
			timestampLabel: "7:00 AM"
		},
		context: "The product sync clashes with the design review and the hiring debrief.",
		content: {
			type: "google_calendar_event_update",
			event_id: "7hd3k9w2p5r8t1y4u6i0o2e3q9",
			title: "Product sync (moved)",
			previous_title: "Product sync",
			start: "2026-08-20T16:00:00-07:00",
			end: "2026-08-20T16:30:00-07:00",
			previous_start: "2026-08-20T14:00:00-07:00",
			previous_end: "2026-08-20T14:30:00-07:00",
			timezone: "America/Los_Angeles",
			location: "Room 4",
			previous_location: "Room 4",
			description: "",
			previous_description: "",
			attendees: ["thea@littlebird.ai", "sam@littlebird.ai"],
			previous_attendees: ["thea@littlebird.ai", "sam@littlebird.ai"],
			user_email: null,
			organizer: "Alex Kim",
			notify_guests: true,
			account_label: "alex@littlebird.ai",
			meet_url: "https://meet.google.com/abc-defg-hij",
			html_link: null
		},
		externalUrl: "https://calendar.google.com/",
		failureMessage: "Google Calendar could not update the event. Check the connection and retry."
	},
	{
		label: "Todoist task",
		note: "A focused follow-up task with project, due date, and priority.",
		provenance: {
			sourceLabel: "Meeting follow-ups",
			timestampLabel: "2 min ago"
		},
		content: {
			type: "todoist_task",
			content: "Draft the Q3 brief",
			project: "6F4RvWWhm5Vg8V62",
			project_name: "Work / Q3 planning",
			due: "Friday, Aug 21",
			priority: 4,
			account_label: null,
			description: null,
			deadline: null,
			duration: null,
			labels: [],
			section: null,
			parent: null,
			order: null,
			assignee: null,
			uncompletable: null
		},
		externalUrl: "https://todoist.com/app/",
		failureMessage: "Todoist did not create the task. Retry in a moment."
	},
	{
		label: "Todoist complete task",
		note: "Marks an existing task done; the title was fetched so the card is readable.",
		provenance: {
			sourceLabel: "End-of-day review",
			timestampLabel: "5 min ago"
		},
		context: "You mentioned in #design that the Q3 brief draft went out this morning.",
		content: {
			type: "todoist_task_complete",
			task_id: "6X7pQ2mRk4vB9nWc",
			task_content: "Draft the Q3 brief",
			project_name: "Work / Q3 planning",
			due: "2026-09-18",
			priority: 3,
			account_label: null
		},
		externalUrl: "https://app.todoist.com/app/task/6X7pQ2mRk4vB9nWc",
		failureMessage: "Todoist did not complete the task. Retry in a moment."
	},
	{
		label: "Todoist reopen task",
		note: "Reopens a completed task; the title was fetched so the card is readable.",
		provenance: {
			sourceLabel: "Standup notes",
			timestampLabel: "12 min ago"
		},
		context: "You said in standup that the brief still needs the finance numbers.",
		content: {
			type: "todoist_task_uncomplete",
			task_id: "6X7pQ2mRk4vB9nWc",
			task_content: "Draft the Q3 brief",
			project_name: "Work / Q3 planning",
			due: "2026-09-18",
			priority: 3,
			account_label: null
		},
		externalUrl: "https://app.todoist.com/app/task/6X7pQ2mRk4vB9nWc",
		failureMessage: "Todoist did not reopen the task. Retry in a moment."
	},
	{
		label: "Todoist delete task",
		note: "Removes a task for good; the completed card has no link because the task is gone.",
		provenance: {
			sourceLabel: "Inbox triage",
			timestampLabel: "1 min ago"
		},
		content: {
			type: "todoist_object_delete",
			object_type: "task",
			object_id: "6X7pQ2mRk4vB9nWc",
			object_name: "Draft the Q3 brief",
			project_name: "Work / Q3 planning",
			due: "2026-09-18",
			account_label: null
		},
		externalUrl: "https://app.todoist.com/app/",
		failureMessage: "Todoist did not delete the task. Retry in a moment."
	},
	{
		label: "Todoist update task",
		note: "Changes several fields at once, showing the current value beside each new one.",
		provenance: {
			sourceLabel: "Meeting follow-ups",
			timestampLabel: "2 min ago"
		},
		content: {
			type: "todoist_task_update",
			task_id: "6X7pQ2mRk4vB9nWc",
			task_content: "Draft the Q3 brief",
			project_name: "Work / Q3 planning",
			content: "Draft and circulate the Q3 brief",
			description: null,
			due: "next Friday",
			deadline: null,
			duration: null,
			labels: ["writing", "urgent"],
			project: null,
			assignee: null,
			uncompletable: null,
			priority: 4,
			previous_due: "2026-09-18",
			previous_priority: 3,
			previous_labels: ["writing"],
			previous_project_name: null,
			account_label: null
		},
		externalUrl: "https://app.todoist.com/app/task/6X7pQ2mRk4vB9nWc",
		failureMessage: "Todoist did not update the task. Retry in a moment."
	},
	{
		label: "Todoist update task, clearing the due date",
		note: "The remove sentinel renders in words even when the current due date is shown beside it.",
		content: {
			type: "todoist_task_update",
			task_id: "6X7pQ2mRk4vB9nWc",
			task_content: "Draft the Q3 brief",
			project_name: null,
			content: null,
			description: null,
			due: "remove",
			deadline: null,
			duration: null,
			labels: null,
			project: null,
			assignee: null,
			uncompletable: null,
			priority: null,
			previous_due: "2026-09-18",
			previous_priority: null,
			previous_labels: null,
			previous_project_name: null,
			account_label: null
		},
		externalUrl: "https://app.todoist.com/app/task/6X7pQ2mRk4vB9nWc",
		failureMessage: "Todoist did not update the task. Retry in a moment."
	},
	{
		label: "Google Tasks new task",
		note: "A task drafted with a due date and notes; the list name was resolved for display.",
		provenance: {
			sourceLabel: "Inbox triage",
			timestampLabel: "2:00 PM"
		},
		context: "Marc asked for the invoice follow-up to land on your list.",
		content: {
			type: "google_task",
			title: "Follow up on invoice #218",
			notes: "Confirm approval status and expected payment date.",
			due: "2026-08-21T00:00:00.000Z",
			tasklist_id: "@default",
			tasklist_title: "Default list",
			task_parent: null,
			account_label: "alex@littlebird.ai"
		},
		externalUrl: "https://tasks.google.com/",
		failureMessage: "Google Tasks did not create the task. Retry in a moment."
	},
	{
		label: "Google Tasks mark complete",
		note: "Marking a task complete and editing it share one card type — this one only sets status.",
		provenance: {
			sourceLabel: "Standup notes",
			timestampLabel: "9:05 AM"
		},
		context: "You mentioned in standup that the Q3 brief draft went out this morning.",
		content: {
			type: "google_task_update",
			task_id: "MVFfNEN3QUhUSmpZRVk0RQ",
			tasklist_id: "N1FNZHM5Y0xYN3g5TzJBVQ",
			task_title: "Draft the Q3 brief",
			tasklist_title: "Work",
			title: null,
			notes: null,
			due: null,
			status: "completed",
			previous_title: null,
			previous_notes: null,
			previous_due: null,
			previous_status: "needsAction",
			account_label: null
		},
		externalUrl: "https://tasks.google.com/",
		failureMessage: "Google Tasks did not update the task. Retry in a moment."
	},
	{
		label: "Google Tasks edit task",
		note: "Changes the title and due date at once, showing the current value beside each new one.",
		provenance: {
			sourceLabel: "Meeting follow-ups",
			timestampLabel: "2 min ago"
		},
		content: {
			type: "google_task_update",
			task_id: "MVFfNEN3QUhUSmpZRVk0RQ",
			tasklist_id: "N1FNZHM5Y0xYN3g5TzJBVQ",
			task_title: "Draft the Q3 brief",
			tasklist_title: "Work",
			title: "Draft and circulate the Q3 brief",
			notes: null,
			due: "2026-09-26T00:00:00.000Z",
			status: null,
			previous_title: "Draft the Q3 brief",
			previous_notes: null,
			previous_due: "2026-09-18T00:00:00.000Z",
			previous_status: null,
			account_label: null
		},
		externalUrl: "https://tasks.google.com/",
		failureMessage: "Google Tasks did not update the task. Retry in a moment."
	},
	{
		label: "Google Tasks delete task",
		note: "Removes a task for good; the completed card has no link because Google returns nothing to confirm against.",
		provenance: {
			sourceLabel: "Inbox triage",
			timestampLabel: "1 min ago"
		},
		content: {
			type: "google_task_delete",
			task_id: "MVFfNEN3QUhUSmpZRVk0RQ",
			tasklist_id: "N1FNZHM5Y0xYN3g5TzJBVQ",
			task_title: "Draft the Q3 brief",
			tasklist_title: "Work",
			due: "2026-09-18T00:00:00.000Z",
			account_label: null
		},
		externalUrl: "https://tasks.google.com/",
		failureMessage: "Google Tasks did not delete the task. Retry in a moment."
	},
	{
		label: "Google Tasks move task",
		note: "Moves a task to a different list, which is the one case that can silently relocate it out of view.",
		provenance: {
			sourceLabel: "Inbox triage",
			timestampLabel: "3 min ago"
		},
		context: "You mentioned this belongs with the personal errands, not the Work list.",
		content: {
			type: "google_task_move",
			task_id: "MVFfNEN3QUhUSmpZRVk0RQ",
			tasklist_id: "N1FNZHM5Y0xYN3g5TzJBVQ",
			task_title: "Draft the Q3 brief",
			tasklist_title: "Work",
			parent: null,
			previous: null,
			destination_tasklist: "N1FNZHM5Y0xYN3g5TzJBVR",
			destination_tasklist_title: "Personal",
			account_label: null
		},
		externalUrl: "https://tasks.google.com/",
		failureMessage: "Google Tasks did not move the task. Retry in a moment."
	},
	{
		label: "Coding agent run",
		note: "A finished Codex run. Its summary goes to the chat, and the card shows only that it finished.",
		provenance: {
			sourceLabel: "Bug triage",
			timestampLabel: "4 min ago"
		},
		content: {
			type: "coding_agent",
			agent: "codex",
			cwd: "/Users/thea/Codebase/littlebird",
			prompt: "The retry helper swallows the underlying error. Fix it and add a regression test.",
			access: "edit",
			session_id: "a1b2c3d4-e5f6-4a3b-8c2d-1e0f9a8b7c6d",
			run: {
				state: "finished",
				summary: "Fixed `withRetry` to rethrow the last error instead of `undefined` when every attempt fails, and added a test covering that case."
			}
		},
		externalUrl: "https://littlebird.ai",
		failureMessage: "Codex stopped before completing the task."
	},
	{
		label: "Slack message",
		note: "A product-sync recap with enough content to test the multiline well.",
		provenance: {
			sourceLabel: "Meeting follow-ups",
			timestampLabel: "9:02 AM"
		},
		context: "Shivam asked in #product after the sync: “Can someone post the recap here for the folks who missed it?”",
		content: {
			type: "slack_message",
			channel_id: "C0BDKKYJDJQ",
			channel_name: "#product",
			message: [
				"Summary from today's product sync:",
				"",
				"• Onboarding v2 ships Thursday behind the beta flag",
				"• Pricing page holds until after the offsite",
				"• Thea drafts the Q3 brief by Friday"
			].join("\n"),
			account_label: null,
			thread_ts: null,
			message_link: "https://littlebirdai.slack.com/archives/C0BDKKYJDJQ"
		},
		externalUrl: "https://littlebirdai.slack.com/archives/C0BDKKYJDJQ/p1788681700000000",
		failureMessage: "Slack rate-limited this message. Retry in a moment."
	},
	{
		label: "Slack DM",
		note: "A direct-message draft. Open in Slack goes to the DM, not a thread.",
		provenance: {
			sourceLabel: "Inbox triage",
			timestampLabel: "9:12 AM"
		},
		context: "Tushar asked whether the recap is going out today.",
		content: {
			type: "slack_message",
			channel_id: "D07TVCZ7Y6A",
			channel_name: "Tushar",
			message: "Yes — posting it in #product in a few minutes.",
			account_label: null,
			thread_ts: null,
			person: {
				id: "U0TUSHAR",
				name: "Tushar",
				avatar_url: null
			},
			message_link: "https://littlebirdai.slack.com/archives/D07TVCZ7Y6A"
		},
		externalUrl: "https://littlebirdai.slack.com/archives/D07TVCZ7Y6A/p1787032413960949",
		failureMessage: "Slack rate-limited this message. Retry in a moment."
	},
	{
		label: "Slack reaction",
		note: "A pending emoji reaction that is only added after review.",
		provenance: {
			sourceLabel: "Product discussion",
			timestampLabel: "Just now"
		},
		content: {
			type: "slack_reaction",
			person: {
				id: "U0SHIVAM",
				name: "Shivam",
				avatar_url: null
			},
			channel_id: "C0BDKKYJDJQ",
			channel_name: "#product",
			message_ts: "1788681609.779859",
			emoji: "eyes",
			account_label: null,
			message: "Can you take a look at the updated onboarding flow before today’s product sync?",
			message_link: "https://littlebirdai.slack.com/archives/C0BDKKYJDJQ/p1788681609779859"
		},
		externalUrl: "https://littlebirdai.slack.com/archives/C0BDKKYJDJQ/p1788681609779859",
		failureMessage: "Slack could not add this reaction. Retry in a moment."
	},
	{
		label: "Slack thread reply",
		note: "A reply under a quoted parent message, with the person avatar in the quote.",
		provenance: {
			sourceLabel: "Meeting follow-ups",
			timestampLabel: "9:05 AM"
		},
		content: {
			type: "slack_message",
			channel_id: "C0BDKKYJDJQ",
			channel_name: "#product",
			message: "Posting the recap now — onboarding v2 ships Thursday behind the beta flag, pricing page holds until after the offsite, and Thea drafts the Q3 brief by Friday.",
			account_label: null,
			thread_ts: "1755851000.000100",
			parent_message: {
				person: {
					id: "U0SHIVAM",
					name: "Shivam",
					avatar_url: null
				},
				channel_name: "#product",
				timestamp: "1755850000.000100",
				text: "Can someone post the recap here for the folks who missed it?",
				message_link: "https://littlebirdai.slack.com/archives/C0BDKKYJDJQ/p1755850000000100"
			}
		},
		externalUrl: "https://littlebirdai.slack.com/archives/C0BDKKYJDJQ/p1755851000000100",
		failureMessage: "Slack rate-limited this message. Retry in a moment."
	},
	{
		label: "Slack file in a thread",
		note: "A file the agent made, shared under a quoted message with the message as its comment. The file is shown by name and cannot be swapped.",
		provenance: {
			sourceLabel: "Meeting follow-ups",
			timestampLabel: "9:06 AM"
		},
		context: "Shivam asked in #product: “Can someone drop the signup numbers here?”",
		content: {
			type: "slack_message_with_attachments",
			channel_id: "C0BDKKYJDJQ",
			channel_name: "#product",
			message: "Signup numbers for the last four weeks, split by plan.",
			account_label: null,
			thread_ts: "1755850000.000100",
			parent_message: {
				person: {
					id: "U0SHIVAM",
					name: "Shivam",
					avatar_url: null
				},
				channel_name: "#product",
				timestamp: "1755850000.000100",
				text: "Can someone drop the signup numbers here?",
				message_link: "https://littlebirdai.slack.com/archives/C0BDKKYJDJQ/p1755850000000100"
			},
			attachment: {
				file_id: "file_01j9x4k7v2m3n8p6q5r4s3t2u2",
				filename: "signups-by-plan.csv",
				mimetype: "text/csv",
				size: 1204
			}
		},
		externalUrl: "https://littlebirdai.slack.com/files/U0ALEX/F0SIGNUPS/signups-by-plan.csv",
		failureMessage: "Slack could not send this file. Retry in a moment."
	},
	{
		label: "Slack thread reply (nested message)",
		note: "A reply to a message deeper in the thread. The quote is the thread root; the context names the message being answered.",
		provenance: {
			sourceLabel: "Meeting follow-ups",
			timestampLabel: "9:08 AM"
		},
		context: "Reply to Varun, who asked further down the thread in #marketing: “Does the launch post go out before or after the pricing page update?”",
		content: {
			type: "slack_message",
			channel_id: "C0BDMKTG001",
			channel_name: "#marketing",
			message: "Before — the launch post goes out Thursday with onboarding v2, and the pricing page update waits until after the offsite.",
			account_label: null,
			thread_ts: "1755852000.000100",
			parent_message: {
				person: {
					id: "U0PRIYA",
					name: "Priya",
					avatar_url: null
				},
				channel_name: "#marketing",
				timestamp: "1755852000.000100",
				text: "Launch comms thread for onboarding v2. Drop your questions here and I will collect answers by end of day.",
				message_link: "https://littlebirdai.slack.com/archives/C0BDMKTG001/p1755852000000100"
			}
		},
		externalUrl: "https://littlebirdai.slack.com/archives/C0BDMKTG001/p1755852100000100",
		failureMessage: "Slack rate-limited this message. Retry in a moment."
	},
	{
		label: "Slack thread reply (emoji shortcodes)",
		note: "Slack hands the agent reaction names and :shortcode: text. The card renders the emoji; unknown names such as :ack-1: stay as text.",
		provenance: {
			sourceLabel: "Hourly Catch",
			timestampLabel: "10:00 AM"
		},
		context: "Parked reply (Tushar reacted :hourglass_flowing_sand:, Priya reacted :ack-1:) to Daniel Chen’s DM from Sep 2: “do you have any thoughts on what to do with the rest of the backlog? :eyes:”",
		content: {
			type: "slack_message",
			channel_id: "D0BDDANIEL",
			channel_name: "Daniel Chen",
			message: "Sorry for the wait :pray: — yes, let’s park the rest until after the offsite :white_check_mark:",
			account_label: null,
			thread_ts: "1756800000.000100",
			parent_message: {
				person: {
					id: "U0DANIEL",
					name: "Daniel Chen",
					avatar_url: null
				},
				channel_name: "Daniel Chen",
				timestamp: "1756790000.000100",
				text: "do you have any thoughts on what to do with the rest of the backlog? :eyes:",
				message_link: "https://littlebirdai.slack.com/archives/D0BDDANIEL/p1756790000000100"
			}
		},
		externalUrl: "https://littlebirdai.slack.com/archives/D0BDDANIEL/p1756800000000100",
		failureMessage: "Slack rate-limited this message. Retry in a moment."
	},
	{
		label: "Informational notice",
		note: "Something a background routine found for the user to read, with nothing to send.",
		provenance: {
			sourceLabel: "Review watch",
			timestampLabel: "8:05 AM"
		},
		context: "Priya asked you in Slack thread 1755852000.000100 to take this review before the Thursday cut.",
		content: {
			type: "informational",
			kind: "info",
			title: "PR #4821 needs your review",
			body: [
				"**Priya** opened *Rework the sync retry backoff* and named you as the only required reviewer.",
				"",
				"It touches the retry path that caused last week’s stuck uploads, so it is worth reading before the Thursday release cut. Two other reviewers have already approved."
			].join("\n"),
			url: "https://github.com/littlebirdai/littlebird/pull/4821"
		},
		externalUrl: "https://github.com/littlebirdai/littlebird/pull/4821",
		failureMessage: "This notice could not be loaded."
	},
	{
		label: "To-do item",
		note: "A to-do the user ticks off; nothing sends when it completes.",
		provenance: {
			sourceLabel: "Morning brief",
			timestampLabel: "7:40 AM"
		},
		content: {
			type: "informational",
			kind: "todo",
			title: "Send Sam the offsite dates",
			body: "Sam asked twice last week, and the venue holds the rooms until Friday."
		},
		externalUrl: "",
		failureMessage: "This to-do could not be updated."
	},
	{
		label: "Agent team",
		note: "A starter team proposed during agent setup. Display-only, so the footer offers Dismiss alone.",
		provenance: {
			sourceLabel: "Agent setup",
			timestampLabel: "9:30 AM"
		},
		context: "Proposed from the projects and routines you already run, so each agent starts with real work.",
		content: {
			type: "agent_team",
			agents: [{
				id: "6f2b1c4e-8a3d-4f5b-9c7e-1d2a3b4c5d6e",
				name: "Inbox triage",
				description: "Sorts overnight email and drafts the replies worth sending.",
				why: "You answer the same three kinds of email every morning.",
				first_task: "Draft replies to the unread threads from yesterday.",
				initial_instructions: "Keep replies short and never send without review.",
				selected: true,
				routines: [{
					title: "Morning inbox sweep",
					schedule: "Every day at 8:30"
				}]
			}, {
				id: "9a8b7c6d-5e4f-4a3b-8c2d-1e0f9a8b7c6d",
				name: "Meeting prep",
				description: "Pulls the last thread with each attendee before an external call.",
				why: "Your calendar has back-to-back external calls.",
				first_task: "Prepare notes for tomorrow’s 10:00 renewal call.",
				initial_instructions: "Lead with open commitments and owners.",
				selected: false,
				routines: []
			}]
		},
		externalUrl: "https://app.littlebird.ai/agents",
		failureMessage: "The team could not be added."
	},
	{
		label: "Routine offer",
		note: "A routine the proactive agent offers instead of setting it up. The user may edit its instructions, and Add routine creates it as one of the user's own routines.",
		provenance: {
			sourceLabel: "Littlebird",
			timestampLabel: "9:00 AM"
		},
		content: {
			type: "routine_offer",
			title: "Weekly investor update",
			description: "Drafts your investor update every Friday afternoon from the week's metrics, wins and asks.",
			prompt: "Draft this week's investor update from the user's metrics dashboard, shipped work and open asks.",
			kind: "digest",
			schedule: {
				frequency: "weekly",
				time: "15:00",
				week_days: ["FR"]
			},
			triggers: null,
			runs_label: "weekly on Friday at 3:00 PM"
		},
		context: "You wrote an investor update by hand on each of the last four Fridays.",
		externalUrl: "",
		failureMessage: "You've reached your routine limit. Delete one in Routines to add this."
	},
	{
		label: "Message to a person",
		note: "A reply drafted for another person's Littlebird. The user edits it and sends it from the card.",
		provenance: {
			sourceLabel: "Sam Rivera · Monday sync",
			timestampLabel: "11:05 AM"
		},
		content: {
			type: "teammate_message",
			thread_id: "7c1d2e3f-4a5b-4c6d-8e7f-0a1b2c3d4e5f",
			chat_id: "5b0c9a52-3d1e-4f6a-8b7c-9d0e1f2a3b4c",
			teammate: {
				id: 1978,
				name: "Sam Rivera"
			},
			incoming_text: "Can you find 30 minutes with Dhaval on Monday to go over the launch plan?",
			draft: "Monday at 3pm works. I have sent a hold and will bring the launch checklist.",
			sent_text: null,
			awaiting_reply: false
		},
		externalUrl: "",
		failureMessage: "Sam is no longer connected."
	},
	{
		label: "Message to a person, sent automatically",
		note: "A reply that the user's Littlebird sent for them, because they trust the other person.",
		provenance: {
			sourceLabel: "Sam Rivera · Monday sync",
			timestampLabel: "11:05 AM"
		},
		content: {
			type: "teammate_message",
			thread_id: "7c1d2e3f-4a5b-4c6d-8e7f-0a1b2c3d4e5f",
			chat_id: "5b0c9a52-3d1e-4f6a-8b7c-9d0e1f2a3b4c",
			teammate: {
				id: 1978,
				name: "Sam Rivera"
			},
			incoming_text: "Can you find 30 minutes with Dhaval on Monday to go over the launch plan?",
			draft: "Monday at 3pm works. I have sent a hold and will bring the launch checklist.",
			sent_text: "Monday at 3pm works. I have sent a hold and will bring the launch checklist.",
			awaiting_reply: true,
			sent_automatically: true
		},
		externalUrl: "",
		failureMessage: "Sam is no longer connected."
	},
	{
		label: "Routine reach-out",
		note: "A routine run that asked the user something in its chat and waits for the reply there.",
		provenance: {
			sourceLabel: "Weekly planning",
			timestampLabel: "9:12 AM"
		},
		content: {
			type: "routine_reach_out",
			title: "Two deadlines clash: which goes first?",
			body: "The launch checklist and the board deck are both due Friday. Which one should I plan around?",
			chat_id: "3f2e1d0c-9b8a-4c7d-8e6f-5a4b3c2d1e0f"
		},
		externalUrl: "",
		failureMessage: "The run has finished."
	},
	{
		label: "Question",
		note: "A question from the proactive agent. The user picks an option or writes their own answer, and sends it.",
		provenance: { timestampLabel: "9:40 AM" },
		content: {
			type: "question",
			prompt: "Would you like fewer meeting follow-up cards?",
			options: [
				"Only for external meetings",
				"Fewer, just the important ones",
				"Keep them as they are"
			],
			allow_multiple: false,
			allow_free_text: true,
			selected: [],
			free_text: ""
		},
		externalUrl: "",
		failureMessage: "Littlebird may not have sent your answer. Try again."
	},
	{
		label: "Teams reply",
		note: "A reply in a Teams channel thread, with the message it answers.",
		context: "Ana asked in Launch / General whether the release still ships Friday.",
		content: {
			type: "microsoft_teams_message",
			target_type: "reply",
			chat_id: null,
			team_id: "team-1",
			channel_id: "channel-1",
			message_id: "message-1",
			content: "Launch moves to Monday. QA needs one more day on the export flow.",
			content_type: "text",
			account_label: null,
			author: null,
			target_label: "Launch / General",
			parent_preview: "Are we still shipping Friday?"
		},
		externalUrl: "",
		failureMessage: "Teams could not send this message. Try again."
	}
];
const LONG_CONTENT_FIXTURE = ACTION_CARD_FIXTURES.find((fixture) => fixture.content.type === "outlook_email_draft");
//#endregion
//#region src/format-action-card-age.ts
function formatActionCardAge(createdAt, now = Date.now()) {
	const minutes = Math.floor((now - new Date(createdAt).getTime()) / 6e4);
	if (minutes < 1) return "just now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}hr ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d ago`;
	if (days < 30) return `${Math.floor(days / 7)}w ago`;
	if (days < 365) return `${Math.floor(days / 30)}mo ago`;
	return `${Math.floor(days / 365)}y ago`;
}
//#endregion
//#region src/follow-up.ts
const MAX_FOLLOW_UP_ACTION_CARDS = 10;
const resolveFollowUpThreadId = (cards, threadExists) => {
	const threadIds = new Set(cards.flatMap((card) => actionCardChatId(card) ?? []));
	if (threadIds.size !== 1) return null;
	const [threadId] = [...threadIds];
	return threadId && threadExists(threadId) ? threadId : null;
};
const widenedKindOf = (actionType) => getActionCardKind(actionType);
const progressLabelOf = (card) => {
	const kind = widenedKindOf(card.actionType);
	if (!kind?.getProgressLabel) return void 0;
	const content = kind.decode(card.content);
	return content ? kind.getProgressLabel(content, card.status) : void 0;
};
const actionCardOwnChat = (card) => {
	if (isRoutineQuestionCard(card) && card.chatId) return {
		chatId: card.chatId,
		withName: "Littlebird"
	};
	const kind = widenedKindOf(card.actionType);
	if (!kind?.getOwnChat) return null;
	const content = kind.decode(card.content);
	return content ? kind.getOwnChat(content) : null;
};
const decodedWaitingOf = (card) => {
	const kind = widenedKindOf(card.actionType);
	const content = kind?.waiting ? kind.decode(card.content) : null;
	return kind?.waiting && content ? {
		waiting: kind.waiting,
		content
	} : null;
};
const isActionCardWaiting = (card) => {
	const decoded = decodedWaitingOf(card);
	return !!decoded && decoded.waiting.isWaiting(decoded.content, card.status);
};
const isActionCardDismissedByKind = (card) => {
	const decoded = decodedWaitingOf(card);
	return !!decoded && decoded.waiting.isDismissed(decoded.content);
};
const actionCardChatId = (card) => actionCardOwnChat(card)?.chatId ?? card.followUpChatId ?? null;
const getFollowUpState = (card) => {
	const inProgress = !!card.followUpRequestId;
	return {
		threadId: card.followUpChatId ?? null,
		inProgress,
		repliedAt: card.followUpRepliedAt ?? null,
		outcome: inProgress ? null : card.followUpReplyKind ?? null,
		progressLabel: progressLabelOf(card)
	};
};
/** Littlebird is still working on the card in its chat, so Home lists it as in progress */
const isFollowUpInProgress = (card) => {
	const followUp = getFollowUpState(card);
	return followUp.inProgress || !!followUp.progressLabel;
};
const followUpOutcomeLabel = (followUp, ageLabel, now) => {
	const { repliedAt } = followUp;
	if (followUp.progressLabel) return followUp.progressLabel;
	switch (followUp.outcome) {
		case "asked": return "Waiting on you";
		case "revised": return repliedAt ? `Revised ${formatActionCardAge(repliedAt, now)}` : "Revised";
		case "replied": return repliedAt ? `Littlebird replied ${formatActionCardAge(repliedAt, now)}` : "Littlebird replied";
		case "failed": return "Reply failed";
		case null: return ageLabel;
	}
};
//#endregion
//#region src/home-card-index.ts
const entries = /* @__PURE__ */ new WeakMap();
const homeCardEntryFor = (card) => {
	let entry = entries.get(card);
	if (!entry) {
		entry = computed(() => {
			const decoded = decodeActionCard(card);
			return decoded ? {
				card,
				decoded,
				followUp: getFollowUpState(card)
			} : null;
		}, { keepAlive: true });
		entries.set(card, entry);
	}
	return entry.get();
};
const isCodingAgent = (actionType) => actionType === "coding_agent";
/** Whether a card waits for the user on Home: one to confirm, a to-do or note, a question, a coding agent to start,
* or a routine run waiting on the user's reply */
const waitsOnHome = (actionType, capability) => capability === "confirmable" || isInformationalActionType(actionType) || !getActionCardKind(actionType) || actionType === "ask_user" || isCodingAgent(actionType) || actionType === "routine_reach_out";
const isForYouEntry = ({ card, decoded }) => waitsOnHome(card.actionType, decoded.capability);
const ACTION_TYPES = Object.keys(ACTION_CARD_KINDS);
const HISTORY_ACTION_TYPES = {
	keptActionTypes: ACTION_TYPES.filter((actionType) => getActionCardKind(actionType)?.capability === "confirmable" || actionType === "ask_user" || isCodingAgent(actionType) || actionType === "routine_reach_out"),
	todoActionTypes: ACTION_TYPES.filter(isInformationalActionType)
};
/** A read note has nothing left to show; a to-do was done or dropped. */
const isKeptInHistoryEntry = ({ card, decoded }) => HISTORY_ACTION_TYPES.keptActionTypes.includes(card.actionType) || isTodoActionCard(decoded.content);
const isInProgress = (card) => {
	const followUp = homeCardEntryFor(card)?.followUp;
	return !!followUp && (followUp.inProgress || !!followUp.progressLabel);
};
const isDismissed = (card) => card.status === "dismissed" || isActionCardDismissedByKind(card);
const toEntries = (cards, shows) => cards.flatMap((card) => {
	const entry = homeCardEntryFor(card);
	return entry && shows(entry) ? [entry] : [];
});
const buildBuckets = ({ actionCards, threads, userConfig }) => {
	const cards = actionCards.orderedData;
	const partition = partitionHomeActionCards(cards, {
		isChatDeleted: (chatId) => !!threads.getById(chatId)?.deletedAt,
		isInProgress,
		isDismissed,
		minImportance: userConfig.proactiveAgents.get("min_card_importance")
	});
	return {
		pending: toEntries(partition.pending, isForYouEntry),
		in_progress: toEntries(partition.inProgress, isForYouEntry),
		dismissed: toEntries(partition.dismissed, isKeptInHistoryEntry),
		done: toEntries(partition.done, isKeptInHistoryEntry),
		chatPrompts: selectChatPromptCards(cards)
	};
};
const cachedBuckets = /* @__PURE__ */ new WeakMap();
const cachedBucketsFor = (stores) => {
	const cached = cachedBuckets.get(stores.actionCards);
	if (cached?.threads === stores.threads && cached.userConfig === stores.userConfig) return cached.buckets;
	const buckets = computed(() => buildBuckets(stores));
	cachedBuckets.set(stores.actionCards, {
		threads: stores.threads,
		userConfig: stores.userConfig,
		buckets
	});
	return buckets;
};
const readHomeCardBuckets = (stores, retainedCardIds) => {
	const { pending, in_progress: inProgress, ...others } = cachedBucketsFor(stores).get();
	const nowMs = Date.now();
	const isAwake = ({ card }) => !isActionCardSnoozed(card, nowMs);
	return {
		...others,
		in_progress: inProgress.filter(isAwake),
		pending: pending.filter((entry) => isAwake(entry) && (entry.card.status === "draft" || retainedCardIds.has(entry.card.id)))
	};
};
//#endregion
//#region src/nesting.ts
/**
* The cards under each of `parentIds`, in the order given, leaving out withdrawn ones. A card whose parent is not
* listed stays out, so it shows on its own and is never lost.
*/
const childrenByParent = (cards, parentIds) => {
	const children = /* @__PURE__ */ new Map();
	for (const card of cards) {
		if (card.parentId === null || !parentIds.has(card.parentId)) continue;
		if (WITHDRAWN_CARD_STATUSES.has(card.status)) continue;
		const siblings = children.get(card.parentId);
		if (siblings) siblings.push(card);
		else children.set(card.parentId, [card]);
	}
	return children;
};
const MAX_RUNNING_SHOWN = 2;
/** A subtask is running, or its chat is still answering, as the partition's in-progress list counts it */
const isSubtaskRunning = (card, isInProgress) => card.status === "running" || isInProgress(card);
/**
* The subtasks a parent shows on itself, in the order of `open`: the ones running now, the ones that failed, and
* the earliest one that waits for the user, so the next step can be approved without opening the parent.
*/
const currentSteps = (open, isInProgress) => {
	const running = open.filter(({ card }) => isSubtaskRunning(card, isInProgress));
	const runningIds = new Set(running.map(({ card }) => card.id));
	const shownRunning = running.slice(0, MAX_RUNNING_SHOWN);
	const next = open.find(({ card }) => card.status === "draft" && !runningIds.has(card.id));
	const shownIds = new Set([
		...shownRunning,
		...open.filter(({ card }) => card.status === "failed"),
		...next ? [next] : []
	].map(({ card }) => card.id));
	return {
		steps: open.filter(({ card }) => shownIds.has(card.id)),
		moreRunning: running.length - shownRunning.length
	};
};
const childrenOf = (cards, { toEntry, isInProgress }) => {
	const byAge = [...cards].sort((left, right) => left.createdAt.getTime() - right.createdAt.getTime());
	const done = byAge.filter((card) => DONE_CARD_STATUSES.has(card.status));
	const open = byAge.flatMap((card) => {
		if (DONE_CARD_STATUSES.has(card.status)) return [];
		const entry = toEntry(card);
		return entry ? [entry] : [];
	});
	return {
		open,
		done,
		progress: {
			done: done.length,
			total: open.length + done.length
		},
		current: currentSteps(open, isInProgress)
	};
};
/**
* Puts each card under its parent when the parent is listed, so the parent's entry owns it and the lists leave it
* out. One level only: a listed parent has no parent of its own. A subtask shows under its parent where the
* partition would show it on its own. A pinned subtask stands on its own, so the Pinned group keeps it in view.
*/
const nestUnderParents = (lists, cards, options) => {
	const parentIds = new Set([...lists.pending, ...lists.inProgress].filter(({ card }) => card.parentId === null).map(({ card }) => card.id));
	const childCards = childrenByParent(cards.filter((card) => !card.pinnedAt && passesHomeCardFilters(card, options)), parentIds);
	const nestedIds = new Set([...childCards.values()].flat().map((child) => child.id));
	const nest = (entries) => entries.flatMap((entry) => {
		if (nestedIds.has(entry.card.id)) return [];
		const children = childCards.get(entry.card.id);
		return [children ? {
			...entry,
			children: childrenOf(children, options)
		} : entry];
	});
	return {
		pending: nest(lists.pending),
		inProgress: nest(lists.inProgress)
	};
};
/**
* A family continues in the parent's chat, even if a step already has another chat. Without a live parent chat,
* use the card's own, then the most recently touched step's. Null when none is live, so a new chat starts.
*/
const resolveCardChatId = (card, { getCard, getChildren, isLiveChat }) => {
	const top = (card.parentId ? getCard(card.parentId) : void 0) ?? card;
	return [
		top,
		card,
		...[...getChildren(top.id)].sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime())
	].map(actionCardChatId).find((chatId) => !!chatId && isLiveChat(chatId)) ?? null;
};
//#endregion
//#region src/action-card-editor.state.ts
var ActionCardEditorState = class {
	editing = false;
	draftContent = null;
	isRevision = false;
	version = 0;
	listeners = /* @__PURE__ */ new Set();
	subscribe = (listener) => {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	};
	getSnapshot = () => this.version;
	getEditingSnapshot = () => this.editing;
	notify() {
		this.version += 1;
		for (const listener of this.listeners) listener();
	}
	begin(content, isRevision) {
		this.draftContent = content;
		this.isRevision = isRevision;
		this.editing = true;
		this.notify();
	}
	updateContent(content) {
		this.draftContent = content;
		this.notify();
	}
	reset() {
		this.editing = false;
		this.draftContent = null;
		this.isRevision = false;
		this.notify();
	}
};
const editorStates = /* @__PURE__ */ new WeakMap();
const idleEditorState = new ActionCardEditorState();
const getActionCardEditorState = (card) => {
	if (!card) return idleEditorState;
	const existingState = editorStates.get(card);
	if (existingState) return existingState;
	const editorState = new ActionCardEditorState();
	editorStates.set(card, editorState);
	return editorState;
};
//#endregion
//#region src/useActionCard.ts
const mutationCallbacks = (state) => ({
	onSaveEdit: async () => {
		if (!state.draftContent) return;
		if (!state.dirty) {
			state.resetEditing();
			return;
		}
		const outcome = await actionCardCommands.saveDraft(state.cardId, state.draftContent);
		if (outcome.status === "succeeded") state.resetEditing();
		return outcome;
	},
	onConfirmOrRetry: async () => {
		if (!state.content) return;
		const outcome = await actionCardCommands.confirm(state.cardId, state.editing ? state.draftContent ?? void 0 : void 0);
		if (state.editing && outcome.status === "succeeded") state.resetEditing();
		return outcome;
	},
	onDismiss: () => actionCardCommands.dismiss(state.cardId),
	onDismissNotRelevant: () => actionCardCommands.dismiss(state.cardId, NOT_RELEVANT_DISMISSAL),
	onAcknowledge: () => actionCardCommands.acknowledge(state.cardId),
	onCompleteExternally: () => actionCardCommands.completeExternally(state.cardId)
});
function useActionCardEditing(card) {
	const editorState = getActionCardEditorState(card);
	return useSyncExternalStore(editorState.subscribe, editorState.getEditingSnapshot, editorState.getEditingSnapshot);
}
const subscribeActionCardEditing = (card, listener) => getActionCardEditorState(card).subscribe(listener);
const getActionCardEditorVersion = (card) => getActionCardEditorState(card).getSnapshot();
const isDirtyDraft = (draftContent, content) => !!draftContent && !!content && JSON.stringify(draftContent) !== JSON.stringify(content);
const useEditorActions = (card, content, editorState) => {
	return {
		onEdit: useCallback(() => {
			if (!content) return;
			editorState.begin(content, card.status === "completed");
		}, [
			content,
			card.status,
			editorState
		]),
		onContentChange: useCallback((next) => editorState.updateContent(next), [editorState]),
		resetEditing: useCallback(() => {
			editorState.reset();
		}, [editorState])
	};
};
function useActionCard(card) {
	"use no memo";
	const editorState = getActionCardEditorState(card);
	useSyncExternalStore(editorState.subscribe, editorState.getSnapshot, editorState.getSnapshot);
	const decodedCard = decodeActionCard(card);
	const { editing, draftContent, isRevision } = editorState;
	const content = decodedCard?.content ?? null;
	const state = toPreviewState(card.status, editing);
	const dirty = isDirtyDraft(draftContent, content);
	const { onEdit, onContentChange, resetEditing } = useEditorActions(card, content, editorState);
	const { onConfirmOrRetry, ...mutations } = mutationCallbacks({
		cardId: card.id,
		content,
		editing,
		draftContent,
		dirty,
		resetEditing
	});
	if (!decodedCard) return { renderable: false };
	const waiting = isActionCardWaiting(card);
	return {
		renderable: true,
		content: editing && draftContent ? draftContent : decodedCard.content,
		state,
		externalUrl: decodedCard.externalUrl,
		externalRef: decodedCard.externalRef,
		revision: decodedCard.revision,
		isRevision: editing && isRevision,
		dirty,
		failureMessage: decodedCard.failureMessage,
		completionResult: card.result,
		context: decodedCard.context,
		sourceLink: decodedCard.sourceLink,
		callbacks: {
			...mutations,
			onStopWaiting: waiting ? () => actionCardCommands.stopWaiting(card.id, false) : void 0,
			onDismissWaiting: waiting ? () => actionCardCommands.stopWaiting(card.id, true) : void 0,
			onConfirm: onConfirmOrRetry,
			onRetry: onConfirmOrRetry,
			onEdit,
			onCancelEdit: resetEditing
		},
		onContentChange
	};
}
//#endregion
//#region src/watch.ts
const isPending = (card) => card.status === "draft" || card.status === "failed";
/** Whether a watcher is still checking the card. The watcher's own status can say alive after the
* card left pending, because the card's last action stops the watcher without rewriting the card */
const isCardWatched = (card) => card.watch?.status === "alive" && isPending(card);
const CARD_WATCH_WORKING_MS = 864e5;
/** Whether the watcher put the card in the Working section, which it keeps for a day after it last saw the user
* working on it, while the card still waits on the user */
const isCardWatchWorking = (card, now = Date.now()) => {
	const workingAt = card.watch?.working_at;
	if (!workingAt || !isPending(card)) return false;
	return Date.parse(workingAt) + CARD_WATCH_WORKING_MS > now;
};
/** What the card's watcher suggests the user do, while the card still waits on them */
const cardWatchSuggestionOf = (card) => {
	if (!isPending(card)) return null;
	const reason = card.watch?.reason ?? "";
	const suggestion = card.watch?.suggestion;
	if (suggestion) return {
		kind: suggestion,
		reason
	};
	const inWorking = isCardWatchWorking(card) || card.facts?.chat_started === true && card.facts?.chat_auto_started !== true;
	return card.watch?.working_suggested && !inWorking ? {
		kind: "working",
		reason
	} : null;
};
/** What the watcher saw the user do so far, such as starting the task */
const cardWatchNoteOf = (card) => isPending(card) ? card.watch?.note ?? null : null;
const RESOLVED_STATUSES = {
	complete: ["completed_externally"],
	dismiss: ["dismissed"]
};
/** How the card's watcher resolved it, or null when the user or anything else did */
const cardWatcherResolutionOf = (card) => {
	const resolved = card.watch?.resolved;
	if (!resolved || !RESOLVED_STATUSES[resolved].includes(card.status)) return null;
	return {
		kind: resolved,
		reason: card.watch?.reason ?? null
	};
};
const cardWatcherResolutionLabel = (resolution) => {
	const verb = resolution.kind === "complete" ? "Marked done by Littlebird" : "Dismissed by Littlebird";
	return resolution.reason ? `${verb} · ${resolution.reason}` : verb;
};
/** Whether Littlebird closed the card for the user: an agent dismissed it, or its watcher resolved it */
const isClearedByLittlebird = (card) => {
	const cleared = card.computed?.facts?.cleared_by_littlebird;
	if (typeof cleared !== "boolean") return cardWatcherResolutionOf(card) !== null;
	return cleared;
};
/** Who closed the card when it was not the user: an agent, its watcher among them, or its expiry */
const cardCloserOf = (card) => {
	const closer = card.computed?.facts?.closed_by;
	return closer === "agent" || closer === "expiry" ? closer : null;
};
/** What a dismissed card says about who dismissed it: its expiry, Littlebird (an agent or its watcher), or the user */
const cardDismissalLabel = (card) => {
	if (cardCloserOf(card) === "expiry") return "Expired";
	return isClearedByLittlebird(card) ? "Dismissed by Littlebird" : "Dismissed";
};
/** Whether Littlebird closed the card for the user after `since` */
const isClearedByLittlebirdSince = (card, since) => {
	if (!isClearedByLittlebird(card)) return false;
	const at = card.resolvedAt ?? card.dismissedAt;
	return at !== null && at.getTime() > since.getTime();
};
/** How many cards Littlebird closed for the user since `since`, for the Feed's banner */
const cardsClearedByLittlebirdSince = (cards, since) => cards.filter((card) => isClearedByLittlebirdSince(card, since)).length;
//#endregion
export { ACTION_CARD_COMPOSER_ASK, ACTION_CARD_FIXTURES, ACTION_CARD_KINDS, ACTION_CARD_MENTION_KIND, ACTION_CARD_STATES, ActionCardPersonSchema, AgentTeamContentSchema, AgentTeamResultSchema, AskUserContentSchema, CARD_WATCH_WORKING_MS, CHANNEL_SOURCE_LABELS, CHAT_PROMPT_ACTION_TYPE, CODING_AGENT_LABELS, ChatPromptAgentExecutionSchema, ChatPromptContentSchema, ChatPromptExecutionSchema, ChatPromptModeExecutionSchema, CodingAgentContentSchema, CodingAgentRunSchema, DISMISS_THANKS_FLASH_MS, DISMISS_UNDO_WINDOW_MS, GithubCommentContentSchema, GithubPullRequestApproveContentSchema, GithubPullRequestCloseContentSchema, GithubPullRequestContentSchema, GithubPullRequestMergeContentSchema, GithubPullRequestRequestChangesContentSchema, GithubPullRequestReviewCommentContentSchema, GithubPullRequestUpdateContentSchema, GmailEmailDraftContentSchema, GmailEmailDraftWithAttachmentsContentSchema, GmailLabelReviewContentSchema, GmailNoiseArchiveContentSchema, GoogleCalendarEventContentSchema, GoogleCalendarEventUpdateContentSchema, GoogleCalendarRsvpContentSchema, GoogleCalendarRsvpResponseSchema, GoogleTaskContentSchema, GoogleTaskDeleteContentSchema, GoogleTaskMoveContentSchema, GoogleTaskUpdateContentSchema, HISTORY_ACTION_TYPES, HOME_ACTION_CARD_PAGE_SIZE, HubSpotCompanyContentSchema, HubSpotContactCreateContentSchema, HubSpotContactNoteContentSchema, HubSpotContactUpdateContentSchema, HubSpotDealContentSchema, HubSpotDealUpdateContentSchema, HubSpotTaskContentSchema, HubSpotTaskUpdateContentSchema, InformationalContentSchema, LONG_CONTENT_FIXTURE, LinearCommentContentSchema, LinearIssueContentSchema, LinearIssueUpdateContentSchema, MAX_FOLLOW_UP_ACTION_CARDS, MEETING_ASSIST_CARD_KINDS, MENTION_GAP_MARK, MicrosoftTeamsMessageContentSchema, NOT_RELEVANT_DISMISSAL, OutlookEmailDraftPreviewContentSchema, QuestionContentSchema, ROUTINE_REACH_OUT_ACTION_TYPE, RoutineOfferContentSchema, RoutineReachOutContentSchema, SlackMessageContentSchema, SlackMessageWithAttachmentsContentSchema, SlackParentMessageSchema, SlackReactionContentSchema, TODO_CTA_MESSAGE, TeammateMessageContentSchema, TodoistObjectDeleteContentSchema, TodoistTaskCompleteContentSchema, TodoistTaskContentSchema, TodoistTaskUncompleteContentSchema, TodoistTaskUpdateContentSchema, ToolApprovalContentSchema, actionCardArrivedAt, actionCardChatId, actionCardChipTitle, actionCardCommands, actionCardComposerText, actionCardFailureMessage, actionCardLastTouchedAt, actionCardMention, actionCardMutationsStore, actionCardOwnChat, actionCardShownProps, actionCardStatusLabel, actionCardViewedProps, anyWebSourceLink, appendActionCardChip, attachedCardMessage, buildActionCardProps, buildWhenRangeValue, canOpenCodingAgent, canStartCodingAgent, cardCloserOf, cardDismissalLabel, cardWatchNoteOf, cardWatchSuggestionOf, cardWatcherResolutionLabel, cardWatcherResolutionOf, cardsClearedByLittlebirdSince, childrenByParent, classifiedSlackMessagePermalink, classifiedSourceLink, codingAgentAccessChoices, codingAgentAccessLabel, codingAgentActionCardKind, codingAgentSessionToContinue, composerBody, composerChips, composerSeed, contextWithoutSourceHref, countActionCardChips, countWaitingAssistCards, decodeActionCard, documentText, editableField, emojiFromShortcodes, failureMessageOf, finalDismissalOf, firstNameOf, followUpOutcomeLabel, formatActionCardAge, formatSlackMentionsForDisplay, getActionCardEditorVersion, getActionCardKind, getActionCardPeople, getFollowUpState, getInformationalCardKind, getInformationalSourceApp, getPendingActionCardTurnAnchorId, gmailActionCardKind, gmailLabelReviewActionCardKind, gmailNoiseArchiveActionCardKind, gmailWithAttachmentsActionCardKind, googleCalendarActionCardKind, homeCardEntryFor, informationalActionCardKind, instantToZonedIso, isActionCardDismissedByKind, isActionCardMention, isActionCardSnoozed, isActionCardWaiting, isCardWatchWorking, isCardWatched, isChipParagraph, isClearedByLittlebird, isClearedByLittlebirdSince, isCodingAgentRunning, isEditableEventTarget, isFollowUpInProgress, isInformationalActionType, isInternalSlackConversationName, isKeptInHistoryEntry, isNoticeActionCard, isRecord, isRoutineQuestionCard, isSlackMessage, isSubtaskRunning, isTodoActionCard, keptNodes, looksLikeHtml, meetingAssistCardKind, meetingAssistPopupText, mentionChipNodes, mentionTrailingSpace, messagingChannelLabel, narrowCardContent, nestUnderParents, nextActionCardWakeAt, nextMeetingAssistPopupCard, nodeText, normalizeSlackMessageMentions, openTodoOf, optionalActionCardPerson, outlookActionCardKind, partitionHomeActionCards, partitionMeetingAssistCards, passesHomeCardFilters, questionActionCardKind, readHomeCardBuckets, readonlyField, removeComposerChip, resolveActionCard, resolveCardChatId, resolveFollowUpThreadId, selectChatPromptCards, selectPendingActionCards, selectTurnActionCards, selectVisiblePendingActionCards, sentSlackMessageLink, slackActionCardKind, slackArchivesUrl, slackMessageSourceLink, slackReactionActionCardKind, slackWithAttachmentsActionCardKind, sourceLinkForCard, sourceLinkFromText, splitAddresses, strippedNodes, subscribeActionCardEditing, teammateDisplayName, teammateMessageActionCardKind, toDocument, toPreviewState, todoistActionCardKind, todoistObjectDeleteActionCardKind, todoistTaskCompleteActionCardKind, todoistTaskUncompleteActionCardKind, todoistTaskUpdateActionCardKind, useActionCard, useActionCardEditing, useDeferredDismiss, waitsOnHome, withComposerBody, zonedWallClockToInstant };

//# sourceMappingURL=index.js.map